// German (de-DE) translations — machine-first sweep, glossary-constrained.
// Mirrors en/ns1 key-for-key — enforced by `satisfies Ns1Translations` below.
import type { Ns1Translations } from '../en/ns1'

export const ns1 = {
  accordion: {
    expandAll: 'Alle ausklappen',
  },
  machineFileBrowser: {
    up: 'Eine Ebene nach oben',
    browseError: 'Verzeichnis konnte nicht aufgelistet werden',
    uploading: 'Wird hochgeladen',
    uploaded: 'Hochgeladen',
    uploadFailed: 'Upload fehlgeschlagen',
    imported: 'Methode importiert',
    importFailed: 'Methode konnte nicht importiert werden',
    inPackage: 'Im Paket',
    inPackageHint:
      'Bereits in diesem Paket — beim erneuten Importieren wird eine neue Version erstellt',
  },
  assetLibrary: {
    packageLabel: 'Paket',
    packagesHeading: 'Pakete',
    appsHeading: 'Apps',
    standaloneAssetsHeading: 'Eigenständige Assets',
    assetsInsideHeading: 'Enthaltene Assets',
    breadcrumbRoot: 'Assets',
    versionedWithApp: 'Mit App versioniert',
    selectAsset: '{{name}} auswählen',
    cardMeta: {
      editedAssets_one: '{{time}} · {{count}} Asset',
      editedAssets_other: '{{time}} · {{count}} Assets',
      editedUsage: '{{time}} · {{usage}}',
    },
    notUsed: 'Nicht verwendet',
    usedInAutomations: 'In {{count}} Automatisierungen verwendet',
    panel: {
      editor: 'Editor',
      versions: 'Versionen',
      usage: 'Verwendung',
      saveAsNewVersion: 'Als neue Version speichern',
      currentVersion: 'Aktuelle Version',
      lastModified: 'Zuletzt geändert',
      unknownUser: '—',
      filepath: 'Dateipfad',
      windowsDevice: 'Windows-Agent',
      checksum: 'Prüfsumme (SHA-256)',
      refetch: 'Erneut abrufen',
      refetchedNewVersion:
        'Erneut abgerufen — neue Version v{{version}} erstellt',
      refetchedNoChange: 'Erneut abgerufen — bereits aktuell, keine Änderungen',
      noSource: 'Für diesen Asset-Typ ist keine Vorschau verfügbar.',
      noUsage: 'Bisher in keinen Automatisierungen verwendet.',
      expand: 'Editor erweitern',
      versionedWithAppDescription:
        'Dieses Asset hat keine eigenständigen Versionen. Es wird als Teil der zugehörigen App versioniert.',
      currentAppVersion: 'Aktuelle App-Version',
    },
    noPackages: 'Noch keine Pakete',
    noAssets: 'Noch keine Assets erstellt.',
    createAsset: 'Asset erstellen',
    createPackage: 'Paket erstellen',
    newAssetTitle: 'Neues Asset',
    newAssetDescription: 'Wählen Sie einen Typ, um zu beginnen.',
    newPackageTitle: 'Neues Paket',
    fields: {
      code: 'Code',
    },
    method: {
      upload: 'Datei hochladen',
      pullFromMachine: 'Von einer Maschine abrufen',
      file: 'Methodendatei',
      machine: 'Windows-Maschine',
      selectMachineHint:
        'Wählen Sie eine Windows-Maschine, um deren Dateien zu durchsuchen.',
      noMachines: 'Keine Windows-Maschinen verfügbar.',
      machinesError:
        'Maschinen konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
      importToLibrary: 'In Library importieren',
      uploadFailed:
        'Die Methode konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.',
    },
    types: {
      app: 'App',
      method: 'Methode',
      binary: 'Binärdatei',
      notebook: 'Notebook',
    },
    typeDescriptions: {
      function:
        'Python-Skripte, die während Workflow-Ausführungen ausgeführt werden',
      method: 'Instrument-Methodendateien zur Gerätesteuerung',
      data: 'CSV-Datendateien, die als Ausführungsparameter referenziert werden',
      binary: 'Jeder andere Dateityp. Nur Eigenschaften, kein Editor.',
      app: 'Ein versioniertes Bündel von Assets, das als Einheit in Canvas referenziert wird',
      notebook: 'Ein Jupyter-Notebook zur Analyse von Ausführungsdaten',
    },
  },
  auth: {
    signOut: 'Abmelden',
    loadingProfile: 'Benutzerprofil wird geladen',
    openProfileMenu: 'Profilmenü für {{name}} öffnen',
    openUserMenu: 'Benutzermenü öffnen',
    updatePin: 'Ihre PIN aktualisieren',
  },
  preferences: {
    title: 'Einstellungen',
    saveError:
      'Ihre Einstellungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    language: {
      sectionTitle: 'Sprache',
      label: 'Spracheinstellung',
      description: 'Wählen Sie die Sprache aus, die Sie verwenden möchten',
      names: {
        en: 'English',
        de: 'Deutsch',
      },
    },
  },
  automation: {
    select: 'Automatisierung auswählen',
    selectCurrent: 'Automatisierung auswählen, aktuell: {{name}}',
    new: 'Neue Automatisierung',
    card: {
      deleteDialog: {
        description: 'Möchten Sie diese Automatisierung wirklich löschen?',
        title: 'Automatisierung löschen',
      },
      latestVersion: 'Aktuelle Version wird verwendet',
      updateAvailable: 'Update verfügbar',
      hasWarnings: 'Warnungen vorhanden',
      aria: {
        selectAutomation: 'Automatisierung {{name}} auswählen',
        openMenu: 'Menü für {{name}} öffnen',
        statusLoading: 'Automatisierungsstatus wird geprüft',
      },
    },
    createDialog: {
      noGroupsFound: 'Keine Gruppen gefunden',
      instrumentGroupsLoadError:
        'Instrumentengruppen konnten nicht geladen werden',
      title: 'Automatisierung erstellen',
      description:
        'Benennen Sie Ihre Automatisierung und wählen Sie eine Instrumentengruppe aus, um Ihren Workflow auszuführen.',
      nameLabel: 'Automatisierungsname',
      namePlaceholder: 'Automatisierungsnamen eingeben',
      instrumentsGroupLabel: 'Instrumentengruppe',
      selected: 'Ausgewählt:',
      noGroupSelected: 'Keine Gruppe ausgewählt',
      selectGroup: 'Instrumentengruppe {{name}} auswählen',
      instrumentGroupFetchError:
        'Daten der Instrumentengruppe konnten nicht geladen werden',
    },
    automap: {
      title: 'Automatisierung erfolgreich erstellt',
      autoMappedTitle_one: '{{count}} Task automatisch zugeordnet',
      autoMappedTitle_other: '{{count}} Tasks automatisch zugeordnet',
      autoMappedDescription:
        'Pro Task war nur ein kompatibles Instrument verfügbar.',
      manualRequiredTitle_one: '{{count}} Task erfordert manuelle Zuordnung',
      manualRequiredTitle_other: '{{count}} Tasks erfordern manuelle Zuordnung',
      manualRequiredDescription:
        'Für diese Tasks sind mehrere kompatible Instrumente verfügbar. Sie müssen auswählen, welches Instrument verwendet werden soll.',
      unmappableTitle_one: '{{count}} Task kann nicht zugeordnet werden',
      unmappableTitle_other: '{{count}} Tasks können nicht zugeordnet werden',
      unmappableDescription:
        'Für diese Tasks sind in {{instrumentGroupName}} keine kompatiblen Instrumente verfügbar.',
      viewDetails: 'Details anzeigen',
      done: 'Fertig',
      illDoItLater: 'Später erledigen',
      guideMe: 'Durch den Vorgang führen',
    },
  },
  breadcrumb: {
    more: 'Mehr',
    navigationLabel: 'Brotkrümelnavigation',
  },
  buildAgent: {
    errorFallback: {
      title: 'EVA ist nicht verfügbar',
      description:
        'Der Assistent ist auf einen unerwarteten Fehler gestoßen und wurde isoliert, damit der Rest von LINQ weiter funktioniert. Bitte erneut versuchen.',
      reload: 'EVA neu laden',
    },
    workflowBubble: {
      openInCanvas: 'In Canvas öffnen',
    },
    toolDetail: {
      noDetail: 'Keine Details für diesen Tool-Aufruf verfügbar.',
      outputNotCaptured:
        'Ausgabe nicht erfasst (Tool wurde abgeschlossen, bevor das Ergebnis empfangen wurde)',
      output: 'Ausgabe',
      input: 'Eingabe',
      title: 'Details zum Tool-Aufruf',
    },
    toggles: {
      web: 'Web',
      ask: 'Fragen',
    },
    skillSelect: {
      ariaLabel: 'Skill auswählen',
      downloadSkillTitle: 'Skill-Definition herunterladen',
      uploadSkillTitle: 'Skill-Definition hochladen',
      uploadFileInputAriaLabel: 'Skill-Markdown-Datei hochladen',
    },
    toasts: {
      skillNotFound: 'Skill „{{skill}}“ nicht gefunden',
      couldNotReadFile:
        'Die Datei konnte nicht gelesen werden. Bitte erneut versuchen.',
      cannotOverwriteBuiltin:
        'Integrierter Skill „{{name}}“ kann nicht überschrieben werden. Benennen Sie Ihre Datei um (z. B. eva-{{name}}-custom.md).',
      uploadFailed: 'Upload für „{{name}}“ fehlgeschlagen.',
      replacedSkill:
        'Vorhandener benutzerdefinierter Skill „{{name}}“ wurde ersetzt.',
      uploadedSkill: 'Skill „{{name}}“ hochgeladen.',
      oauthRequired: 'OAuth-Token erforderlich',
      workflowSaved: 'Workflow gespeichert',
    },
    settings: {
      previousSessions: 'Vorherige Sitzungen',
      tokenNotSet: 'Nicht festgelegt',
      tokenSet: 'Festgelegt',
      tokenLabel: 'Token',
      buttonLabel: 'Settings und Sitzungsverlauf',
      buttonLabelTokenError: 'Settings — Token-Fehler, zum Beheben klicken',
      tokenPlaceholder: 'OAuth-Token einfügen...',
    },
    sessions: 'Sitzungen',
    sessionHistory: {
      fallbackChatName: 'Chat',
      emptyText: 'Keine vorherigen Sitzungen',
      messageCount: '{{n}} Nachr.',
    },
    tabBar: {
      newTabAriaLabel: 'Neuen Chat-Tab öffnen',
      closeTabAriaLabel: 'Tab {{label}} schließen',
    },
    thinkingGroup: {
      expandAriaLabel: '{{count}} Tool-Schritte einblenden',
      collapseAriaLabel: 'Tool-Schritte ausblenden',
      stepCount: '({{count}}x)',
    },
    status: {
      inference: 'Wird generiert…',
      error: 'Fehler aufgetreten',
      ask: 'Wartet auf Ihre Antwort',
      unread: 'Ungelesene Nachrichten',
      idle: 'Inaktiv, keine Aktivität',
    },
    panel: {
      messagePlaceholder: 'Fragen Sie mich zu LINQ...',
      messageAriaLabel: 'Nachricht an Agent',
      stopAriaLabel: 'Agent-Antwort stoppen',
      sendAriaLabel: 'Nachricht senden',
      resizeAriaLabel: 'Größe des Eingabefelds ändern',
      emptyStateText: 'Fragen Sie mich zu LINQ',
      thinkingText: 'Denkt nach...',
      jumpToLatest: 'Zum Neuesten springen',
    },
    suggestedPrompts: {
      buildWorkflow: 'Erstelle mir einen Workflow',
      whatActionsAvailable: 'Welche Aktionen sind verfügbar?',
      explainWorkflow: 'Erkläre meinen Workflow',
    },
    dialog: {
      openAriaLabel: 'Eva Build Agent öffnen',
      closeAriaLabel: 'Build Agent schließen',
      ariaLabel: 'Eva Build Agent',
      resizeAriaLabel: 'Größe des Build-Agent-Panels ändern',
    },
  },
  calendar: {
    scheduleCard: {
      ariaLabel: 'Zeitplankarte',
      titledAriaLabel: 'Zeitplan: {{title}}',
    },
    previousDay: 'Vorheriger Tag',
    nextDay: 'Nächster Tag',
  },
  canvas: {
    debugDialog: {
      triggerTooltip: 'Workflow-Debugdaten',
      title: 'Workflow-Debugdaten',
    },
    loadError: {
      title: 'Workflow konnte nicht geladen werden',
    },
    workflowResourceName: 'Workflow',
    conditionalNode: {
      notFound: 'Fehler: Conditional nicht gefunden',
    },
    panelHeader: {
      simulation: 'Simulation',
      build: 'Erstellen',
    },
    floatingMenu: {
      showRelations: 'Beziehungen anzeigen',
      hideRelations: 'Beziehungen ausblenden',
      addFrame: 'Rahmen',
      changeFrameColor: 'Rahmenfarbe ändern',
      renameFrame: 'Rahmen umbenennen',
      selectOrMoveFrame: 'Rahmen auswählen oder verschieben',
      undo: 'Rückgängig',
      redo: 'Wiederherstellen',
    },
    clipboard: {
      nodesCopiedHeader_one: '{{count}} Node aus LINQ Canvas kopiert',
      nodesCopiedHeader_other: '{{count}} Nodes aus LINQ Canvas kopiert',
    },
    zoom: {
      fitToSelection: 'An Auswahl anpassen',
      fitToScreen: 'An Bildschirm anpassen',
    },
    frameColors: {
      orange: 'Orange',
      amber: 'Bernstein',
      lime: 'Limette',
      emerald: 'Smaragd',
      teal: 'Türkis',
      cyan: 'Cyan',
      sky: 'Himmelblau',
      indigo: 'Indigo',
    },
    nodeLibrary: {
      searchPlaceholder: 'Tasks suchen...',
      searchTooltip: 'Suchen',
      clearSearch: 'Suche löschen',
      showAllTasks: 'Alle Tasks anzeigen',
      unavailableTaskTooltip:
        'Kein Gerät für diesen Task in der ausgewählten Instrumentengruppe verfügbar',
      unavailableTaskIcon: 'Kein Gerät verfügbar',
    },
    setupInstructions: {
      title: 'Setup-Aktionen',
      cleanupTitle: 'Cleanup-Aktionen',
      closeButton: 'Anweisungsseitenleiste schließen',
      addInstruction: 'Anweisung hinzufügen',
      selectInstructionType: 'Anweisungstyp auswählen',
      deviceProperty: 'Geräteeigenschaft',
      device: 'Gerät',
      driverVersion: 'Driver-Version',
      selectDevice: 'Gerät auswählen',
      addProperty: 'Eigenschaft hinzufügen',
      noInstrumentsAvailable: 'Keine Instrumente verfügbar',
      timeEstimate: 'Zeitschätzung',
      noConfigurableProperties:
        'Dieser Driver hat keine konfigurierbaren Eigenschaften. Wählen Sie einen anderen Driver oder brechen Sie ab.',
      instructionName: 'Anweisungsname',
      instructionNamePlaceholder: 'Anweisungsname eingeben',
      noDevice: 'Kein Gerät (allgemeine Anweisung)',
      clickToAddDescription: 'Klicken, um Beschreibung hinzuzufügen...',
      editDescription: 'Beschreibung bearbeiten',
      action: 'Aktion',
      command: 'Befehl',
      selectCommand: 'Befehl auswählen',
      addAction: 'Aktion hinzufügen',
      noActionsAvailable: 'Keine Aktionen für diesen Driver verfügbar',
      manifestFetchError:
        'Gerätekonfigurationsschema konnte nicht abgerufen werden',
      placeLabware: 'Laborgeräte platzieren',
      removeLabware: 'Laborgeräte entfernen',
      slotDeterminedByPlan: '# und Slot werden vom Plan bestimmt',
      locationDeterminedByPlan: 'Gerät wird vom Plan bestimmt',
      labwareLoadingDeleteDisabled:
        'Anweisungen zum Laden von Laborgeräten werden automatisch hinzugefügt und können nicht gelöscht werden',
      addFunction: 'Funktion hinzufügen',
      types: {
        configureDescription: 'Gerätekonfigurationswerte festlegen',
        manualDescription: 'Der Operator führt eine manuelle Aktion aus',
        deviceCommand: 'Gerätebefehl',
        deviceCommandDescription: 'Befehle auf einem Gerät ausführen',
        function: 'Funktion',
        functionDescription: 'Inline-Python-Code ausführen',
        functionDependencies: 'Ausführen nach',
        functionDependenciesDescription:
          'Wählen Sie Anweisungen aus, die abgeschlossen sein müssen, bevor diese Funktion ausgeführt wird. Wenn nichts ausgewählt ist, wird sie nach der vorherigen Anweisung ausgeführt.',
      },
    },
    processInstructions: {
      empty:
        'Keine Anweisungen. Wählen Sie eine Workflow-Automatisierung aus, um deren Setup- und Cleanup-Aktionen anzuzeigen.',
      noStepActions: 'Keine Aktionen für diesen Schritt.',
      alreadyLoaded: {
        title: 'Laborgeräte bereits im Workflow {{workflowName}} geladen',
        viewButton: 'Erste Ladeanweisung anzeigen',
        description:
          'Laborgeräte aus einem vorgelagerten Workflow werden vom System automatisch von der Endposition des vorherigen Workflows zur Position des ersten Tasks in diesem Workflow transportiert, wenn dieser Workflow beginnt.',
      },
    },
    combineGroups: {
      combineSelected:
        '{{count}} ausgewählte Schritte oder Gruppen kombinieren',
      selectMoreHint:
        'Wählen Sie einen weiteren Schritt oder eine Gruppe aus, um sie zu kombinieren.',
      nothingToGroup: 'Es gibt nichts, womit dies gruppiert werden könnte.',
      intro:
        'Wählen Sie Workflows zum Gruppieren aus. Gruppierte Workflows teilen sich ein Setup und Cleanup: Das Setup ist abgeschlossen, bevor ein Workflow der Gruppe startet, und das Cleanup läuft erst, wenn alle abgeschlossen sind.',
      link: 'Verknüpfen',
      unlink: 'Trennen',
      linkedHint: 'Setup und Cleanup sind verknüpft.',
      ungroup: 'Gruppierung aufheben',
      removeStep: 'Schritt aus Gruppe entfernen',
      parallelTag: 'parallel',
      parallelHint:
        'Parallele Schritte teilen sich automatisch Setup und Cleanup. Ändern Sie dies über die parallele Beziehung im Canvas.',
      selectStepNamed: '{{name}} zum Kombinieren auswählen',
      selectGroupNamed: 'Gruppe ({{steps}}) zum Kombinieren auswählen',
    },
    parameters: {
      groupByTask: 'Nach Task gruppieren',
      groupByStep: 'Nach Schritt gruppieren',
      flatList: 'Flache Liste',
      close: 'Parameter-Seitenleiste schließen',
      fieldTypes: {
        field: 'Feld',
        string: 'String',
        number: 'Number',
        integer: 'Integer',
        boolean: 'Boolean',
        array: 'Array',
        object: 'Object',
        file: 'Datei',
      },
    },
    taskAutomation: {
      noCompatibleInstruments: {
        title: 'Keine kompatiblen Instrumente',
        description:
          'Die Instrumentengruppe {{instrumentGroupName}} verfügt über keine Instrumente, die mit diesem {{actionName}}-Task kompatibel sind.',
      },
    },
    accessories: {
      title: 'Zubehör',
      closeButton: 'Zubehör-Seitenleiste schließen',
      addAccessory: 'Ausgleichsplatte hinzufügen',
      noAccessories: 'Kein Zubehör definiert',
      newAccessory: 'Neue Ausgleichsplatte',
      editAccessory: 'Ausgleichsplatte bearbeiten',
      validation: {
        nameRequired: 'Namen für die Ausgleichsplatte eingeben',
        labwareTypeRequired: 'Laborgeräte-Typ auswählen',
        instrumentRequired: 'Heim-Instrument auswählen',
        slotRequired: 'Heim-Slot eingeben',
      },
      homeLocationHint:
        'Diese Ausgleichsplatte kehrt bei Nichtgebrauch immer an dieselbe Ausgangsposition zurück',
      locationSummary: '{{instrument}} · Slot {{slot}}',
      slotConflict:
        'Slot {{slot}} auf diesem Instrument wird bereits von {{name}} verwendet. Wählen Sie einen anderen Slot.',
      noHomeLocation: 'Keine Ausgangsposition festgelegt',
      usedIn: 'Verwendet in:',
      notUsed: 'Keinem Task zugewiesen',
      editAria: '{{name}} bearbeiten',
      deleteAria: '{{name}} löschen',
      deleteConfirmDescription:
        'Dieses Zubehör ist den unten aufgeführten Tasks zugewiesen. Beim Löschen wird es daraus entfernt.',
      affectedTasks: 'Betroffene Tasks:',
    },
    variables: {
      title: 'Variablen',
      closeButton: 'Variablen-Seitenleiste schließen',
      addVariable: 'Variable hinzufügen',
      selectVariableType: 'Variablentyp auswählen',
      noVariables: 'Keine Variablen definiert',
      newVariable: 'Neue Variable',
      editVariable: 'Variable bearbeiten',
      variableName: 'Variablenname',
      numberType: 'Zahlentyp',
      wholeNumber: 'Ganze Zahl',
      decimalNumber: 'Dezimalzahl',
      defaultValue: 'Standardwert',
      validRange: 'Gültiger Bereich',
      min: 'Min',
      max: 'Max',
      description: 'Beschreibung',
      descriptionPlaceholder: 'Beschreibung eingeben...',
      uploadFile: 'Datei hochladen',
      selectFile: 'Datei auswählen',
      removeFile: 'Datei entfernen',
      parameter: 'Parameter',
      parameterTooltip:
        'Als Parameter markieren, damit dieser Wert beim Erstellen eines Runs festgelegt werden kann',
      parameterUnsupportedFile: 'Dateiparameter werden noch nicht unterstützt',
      parameterUnsupportedKind:
        'Parameter werden für diesen Typ noch nicht unterstützt',
      useCount: 'Verwendet von',
      deleteConfirmTitle: '"{{name}}" löschen',
      deleteConfirmDescription:
        'Diese Variable wird von Eigenschaften in den folgenden Nodes verwendet. Beim Löschen wird der Wert aus diesen Eigenschaften entfernt.',
      affectedProperties: 'Betroffene Eigenschaften:',
      uploading: 'Wird hochgeladen...',
      uploadFailure: 'Datei-Upload fehlgeschlagen',
      uploadMissingDevicePath: 'Upload hat keinen Gerätepfad zurückgegeben',
      errors: {
        defaultBelowMin: 'Standardwert liegt unter dem Minimum ({{min}})',
        defaultAboveMax: 'Standardwert liegt über dem Maximum ({{max}})',
        minExceedsMax: 'Minimum darf Maximum nicht überschreiten',
      },
      types: {
        number: 'Zahl',
        numberDescription: 'Numerischer Wert (ganz oder dezimal)',
        text: 'Text',
        textDescription: 'String-Wert',
        boolean: 'Boolean',
        booleanDescription: 'Wahr oder falsch',
        duration: 'Dauer',
        durationDescription: 'Zeitdauer',
        file: 'Datei',
        fileDescription: 'Datei-Upload',
      },
    },
    valueChip: {
      appliedVariable: 'Angewendete Variable',
      appliedParameter: 'Angewendeter Parameter',
      appliedInputData: 'Angewendete Eingabedaten',
      appliedSecret: 'Secret-Referenz',
      appliedAsset: 'Datei aus der Asset-Bibliothek',
      assetRevision: 'Revision',
      revisionTag: 'v{{revision}}',
      defaultValue: 'Standard',
    },
    propertiesPanel: {
      slotDisabledNoLabware: 'Keine Laborgeräte verbunden',
      relation: 'Beziehung',
      executionMode: {
        label: 'Ausführungsmodus',
        placeholder: 'Ausführungsmodus auswählen',
      },
    },
    relations: {
      source: 'Quelle',
      target: 'Ziel',
      edgeAriaLabel: 'Beziehung von {{source}} zu {{target}}',
      addTimeConstraint: 'Zeitlimit hinzufügen',
      dependency: {
        title: 'Abhängigkeit',
        happens: 'erfolgt',
        before: 'Vor',
        after: 'Nach',
        parallel: 'Parallel',
        disabledReasonSingleOption:
          'Hier ist nur eine Richtung gültig. Die andere würde mit dem Laborgeräte-Fluss oder einer bestehenden Abhängigkeit in Konflikt stehen.',
        disabledReasonNoOptions:
          'Zwischen diesen Tasks ist keine Abhängigkeitsrichtung gültig (sie teilen sich Laborgeräte). Sie können dennoch ein Zeitlimit hinzufügen.',
        disabledReasonMissingLabware:
          'Fügen Sie beiden Tasks Laborgeräte-Verbindungen hinzu, bevor Sie eine Abhängigkeitsrichtung konfigurieren.',
      },
      timeConstraint: {
        deleteTooltip: 'Zeitlimit löschen',
        hardLimits: 'Feste Grenzen',
        preferredTiming: 'Bevorzugtes Timing',
        preferredDuration: 'Bevorzugte Dauer',
        priority: 'Priorität',
        weightHint:
          'Wie stark der Planer diese bevorzugte Dauer gegenüber anderen zeitlichen Zielvorgaben gewichtet. Überschreibt niemals die Gesamtdauer des Plans.',
        targetOutOfRange:
          'Die bevorzugte Dauer liegt außerhalb des Min-Max-Bereichs, sodass der Planer sie nicht erreichen kann.',
      },
    },
  },
  canvasNodeProperties: {
    header: {
      closeAriaLabel: 'Eigenschaftenbereich schließen',
    },
    analyse: {
      title: 'Intelligence',
      promptLabel: 'Prompt',
      promptPlaceholder: 'Beschreiben Sie, was Eva analysieren soll…',
      agenticLabel: 'Agentenmodus',
      agenticDescription:
        'Wenn aktiviert, kann Eva frei explorieren, um ein Ergebnis zu erreichen. Wenn deaktiviert, wird das Notebook genau wie geschrieben ausgeführt.',
      credentialLabel: 'Token',
    },
    tabs: {
      taskAutomation: 'Task-Automatisierung',
      taskDetails: 'Task-Details',
      labwareAutomation: 'Laborgeräte-Automatisierung',
      labwareDetails: 'Laborgeräte-Details',
      details: 'Details',
      automation: 'Automatisierung',
    },
    labwareAutomation: {
      startingLocationLabel: 'Startposition',
    },
    colorPicker: {
      changeColourAriaLabel: 'Farbe ändern',
    },
    errors: {
      nameAlreadyExists: 'Name existiert bereits',
      nameMinLength: 'Name muss mindestens {{minimum}} Zeichen lang sein',
      nameMaxLength: 'Name darf höchstens {{maximum}} Zeichen lang sein',
    },
    connectedData: {
      title: 'Verbundene Daten',
      dataOutputs: 'Datenausgaben',
      dataInputs: 'Dateneingaben',
      noDataOutputs: 'Keine Datenausgaben',
      noDataInputs: 'Keine Datenverbindungen zu diesem Schritt',
      fromStep: 'Von {{step}}',
    },
    dataConnections: {
      pythonFileUploadInput: 'Python-Datei-Upload-Eingabe',
      pythonCode: 'Python-Code',
      uploadCode: 'Code hochladen',
      updating: 'Wird aktualisiert...',
      nodeMatchesCode: 'Node stimmt mit Code überein',
      changesDetected: 'ÄNDERUNGEN ERKANNT: Node und Code sind nicht synchron',
      introspectionError: 'Introspektionsfehler',
      fileUploadError: 'Datei-Upload-Fehler',
      failedToReadFile: 'Datei konnte nicht gelesen werden',
      fileTooLarge: 'Dateigröße muss kleiner als 500KB sein',
      invalidFileType: 'Bitte laden Sie eine Python-Datei (.py) hoch',
      assetReference: {
        referenceAsset: 'Asset referenzieren',
        writeCustomCode: 'Stattdessen benutzerdefinierten Code schreiben',
        searchPlaceholder: 'Assets suchen...',
        showAssets: 'Assets anzeigen',
        clearSelection: 'Auswahl aufheben',
        noResults: 'Keine Assets gefunden',
        version: 'Version',
        latestVersion: 'Neueste (v{{revision}})',
        specificVersion: 'Bestimmte Version',
        changeVersion: 'Version ändern',
        latest: 'neueste',
        fromPackage: 'aus {{package}}',
        readOnly: 'Read only',
        newerAvailable: 'Eine neuere Version (v{{revision}}) ist verfügbar',
        updateToRevision: 'Auf v{{revision}} aktualisieren',
        unlink: 'Verknüpfung aufheben',
        brokenReference:
          'Diese referenzierte Funktion konnte nicht geladen werden. Möglicherweise wurde sie gelöscht.',
        signatureUnavailable:
          'Die Funktionssignatur konnte nicht aus dieser Version abgeleitet werden. Node-Eingaben und -Ausgaben wurden gelöscht.',
        loading: 'Funktion wird geladen...',
        subtitle: 'Asset-Referenz',
      },
    },
    valueType: {
      customValue: 'Benutzerdefinierter Wert',
      variableValue: 'Variablenwert',
      inputDataValue: 'Eingabedatenwert',
      searchInputs: 'Eingaben suchen',
      selectInput: 'Eingabetyp auswählen',
      noCompatibleInputs: 'Keine kompatiblen Eingaben',
      noCompatibleVariables: 'Keine kompatiblen Variablen',
      addNewVariable: 'Neue Variable hinzufügen',
      executionPresetValue: 'Wert der Ausführungsvoreinstellung',
      secretValue: 'Secret-Wert',
      assetValue: 'Aus der Asset-Bibliothek',
    },
    assetPicker: {
      searchPlaceholder: 'Datei-Assets suchen...',
      noResults: 'Keine Datei-Assets gefunden',
    },
  },
  canvasValidation: {
    emptyState: {
      noIssuesFound: 'Keine Probleme gefunden',
      noMissingInputsFound: 'Keine fehlenden Eingaben gefunden',
      noErrorsFound: 'Keine Fehler gefunden',
      adjustSearchTerms: 'Versuchen Sie, Ihre Suchbegriffe anzupassen',
      noIssuesMatchSearch: 'Keine Probleme entsprechen Ihrer Suche',
    },
    filters: {
      missingInput: 'Fehlende Eingabe',
    },
    searchPlaceholder: 'Probleme durchsuchen...',
    errors: 'Fehler',
    noContent: 'Kein Inhalt',
    issues: 'Probleme',
    closePanel: 'Validierungsbereich schließen',
    issueItem: {
      location: 'Position:',
      node: 'Node:',
      relation: 'Beziehung:',
      field: 'Feld:',
      message: 'Meldung:',
    },
    footer: {
      errors: 'Fehler ({{total}})',
      missingInputs: 'Fehlende Eingaben ({{total}})',
      warnings: 'Warnungen ({{total}})',
    },
    issueTitle: {
      invalidValue: 'Ungültiger Wert',
      deviceDataMissingForSimulationAndRunning:
        'Daten fehlen in Studio - Erforderlich für Simulation und Ausführung',
      deviceDataMissingForRunning:
        'Daten fehlen in Studio - Erforderlich für Ausführung',
      missingInputForPlanningAndRunning:
        '{{message}} - Erforderlich für Planung und Ausführung',
      missingInputForRunning: '{{message}} - Erforderlich für Ausführung',
    },
    warnings: {
      manualStepNotExecutable: {
        message:
          'Manuelle Schritte können noch nicht ausgeführt werden — die Simulation oder Ausführung eines Prozesses, der einen enthält, schlägt fehl',
        shortDescription: 'Manuelle Schritte noch nicht ausführbar',
      },
    },
    requiredForPlanningAndRunning: 'Erforderlich für Planung und Ausführung',
    requiredForRunning: 'Erforderlich für Ausführung',
    staleDataReference:
      'Veraltete Datenreferenz: Die referenzierte Ausgabe existiert nicht mehr',
    fields: {
      timeEstimate: 'Zeitschätzung',
      minimumTime: 'Mindestzeit',
      maximumTime: 'Höchstzeit',
      startSlot: 'Start-Slot',
      endSlot: 'End-Slot',
      start: 'Start',
      end: 'Ende',
      claudeCredential: 'Token',
    },
    requiredMessage: '{{field}} ist erforderlich',
    minExceedsMaxMessage: 'Min muss kleiner oder gleich Max sein',
    incompleteTimeConstraintMessage:
      'Legen Sie mindestens eine Mindest-, Höchst- oder bevorzugte Dauer fest.',
    liquidHandlerSingleDeviceMessage:
      'Ein Task, der Laborgeräte von einer Liquid-Handler-Startposition erhält, muss ein einzelnes Gerät haben',
    liquidHandlerDeviceMismatchMessage:
      'Bei Liquid-Handler-Instrumenten muss die Startposition der Laborgeräte dem Gerät des ersten Tasks entsprechen',
    startingSlotRangeMessage:
      'Der Start-Slot muss ein gültiger Slot oder Slot-Bereich sein (z. B. "1-5")',
    endSlotRangeMessage:
      'Der End-Slot muss ein gültiger Slot oder Slot-Bereich sein (z. B. "1-5")',
    instrumentNoLongerInGroupMessage:
      'Das zugewiesene Instrument befindet sich nicht mehr in der Instrumentengruppe. Weisen Sie es neu zu oder entfernen Sie die Referenz, um zu simulieren.',
    startLocationInstrumentNoLongerInGroupMessage:
      'Das Instrument der Startposition befindet sich nicht mehr in der Instrumentengruppe. Weisen Sie es neu zu oder entfernen Sie die Referenz, um zu simulieren.',
    shortDescriptions: {
      required: 'Erforderlich',
      invalidNumber: 'Ungültige Zahl',
      mustBeInteger: 'Muss eine Ganzzahl sein',
      invalidFormat: 'Ungültiges Format',
      variableOnEnum: 'Variable auf Enum',
      minAboveMax: 'Min > Max',
      slotMismatchWithDownstreamTask: 'Slot-Konflikt mit nachgelagertem Task',
      invalidSlotFormat: 'Ungültiges Slot-Format',
      multipleDevicesOnLiquidHandlerTask:
        'Mehrere Geräte bei Liquid-Handler-Task',
      liquidHandlerDeviceMismatch: 'Gerätekonflikt beim Liquid Handler',
      incompleteTimeConstraint: 'Unvollständiges Zeitlimit',
      invalidDurationRange: 'Ungültiger Dauerbereich',
      staleInstrument: 'Instrument nicht in Gruppe',
      staleDataReference: 'Veraltete Datenreferenz',
    },
  },
  codeStepProperties: {
    tabs: {
      automation: 'Code-Automatisierung',
      details: 'Code-Details',
    },
  },
  common: {
    clearSearch: 'Suche löschen',
    openSearch: 'Suche öffnen',
    speedProfileInfo: {
      label: 'Informationen zum Geschwindigkeitsprofil',
      fast: 'Schnell: 1x volle Geschwindigkeit',
      standard: 'Standard: 0,75x volle Geschwindigkeit',
      slow: 'Langsam: 0,5x volle Geschwindigkeit',
      gentle: 'Sanft: 0,25x volle Geschwindigkeit',
    },
    noItemsFound: 'Keine {{dataLabel}} gefunden.',
    process: 'Prozess',
    copyToClipboard: 'In die Zwischenablage kopieren',
    copied: 'Kopiert!',
    copy: 'Kopieren',
    home: 'Startseite',
    studio: 'Studio',
    devices: 'Geräte',
    workflows: 'Workflows',
    library: 'Library',
    workflow: 'Workflow',
    create: 'Erstellen',
    creating: 'Wird erstellt...',
    'control-room': 'Control Room',
    assetLibrary: 'Asset Library',
    lens: 'Lens',
    userManagement: 'Benutzerverwaltung',
    secrets: 'Secrets',
    labSettings: 'Laboreinstellungen',
    settingsMenu: 'Settings',
    add: 'Hinzufügen',
    update: 'Aktualisieren',
    delete: 'Löschen',
    duplicate: 'Duplizieren',
    cancel: 'Abbrechen',
    abort: 'Abbrechen',
    close: 'Schließen',
    imageLightbox: {
      viewFullSize: 'In voller Größe anzeigen',
      title: 'Bildvorschau',
    },
    comingSoon: 'Demnächst verfügbar',
    createdBy: 'Erstellt von',
    status: 'Status',
    select: 'Auswählen',
    selectPlaceholder: '{{label}} auswählen',
    loading: 'Wird geladen...',
    loadingResource: '{{resource}} wird geladen...',
    error: 'Fehler',
    yes: 'Ja',
    no: 'Nein',
    pause: 'Pause',
    labware: 'Laborgeräte',
    tasks: 'Tasks',
    task: 'Task',
    steps: 'Schritte',
    logic: 'Logik',
    notApplicable: 'N/A',
    configuration: 'Konfiguration',
    softwareVersion: 'Softwareversion',
    definition: 'Definition',
    unknown: 'Unbekannt',
    minDuration: 'Min. Dauer',
    maxDuration: 'Max. Dauer',
    save: 'Speichern',
    saveWorkflow: 'Workflow speichern',
    saveProcess: 'Prozess speichern',
    properties: 'Eigenschaften',
    slot: 'Slot',
    instrument: 'Instrument',
    true: 'Wahr',
    false: 'Falsch',
    deleteResource: '{{resource}} löschen',
    noResults: 'Keine Ergebnisse',
    name: 'Name',
    type: 'Typ',
    data: 'Daten',
    inputs: 'Eingaben',
    outputs: 'Ausgaben',
    lock: 'Sperren',
    unlock: 'Entsperren',
    expand: 'Ausklappen',
    collapse: 'Einklappen',
    help: 'Hilfe',
    conditional: 'Conditional',
    if: 'If',
    elseIf: 'ElseIf',
    else: 'Else',
    apply: 'Anwenden',
    search: 'Suchen..',
    tasksRunning: 'laufende Tasks',
    progress: 'Fortschritt',
    active: 'Aktiv',
    queued: 'In Warteschlange',
    finished: 'Abgeschlossen',
    aborted: 'Abgebrochen',
    unresolved: 'Nicht behoben',
    dateTime: {
      dateAtTime: '{{date}} um {{time}}',
      nextHourAt: 'Nächste Stunde, {{time}}',
      todayAt: 'Heute, {{time}}',
      tomorrowAt: 'Morgen, {{time}}',
      yesterdayAt: 'Gestern, {{time}}',
      secondsAgo: 'vor {{count}}s',
      minutesAgo: 'vor {{count}}m',
    },
    countdown: {
      expired: 'Abgelaufen',
    },
    production: 'Produktion',
    test: 'Test',
    to: 'bis',
    durationToGo: 'noch {{duration}}',
    tasksRunning_one: '{{count}} Task läuft',
    tasksRunning_other: '{{count}} Tasks laufen',
    confirm: 'Bestätigen',
    edit: 'Bearbeiten',
    dragToReorder: 'Zum Neuordnen ziehen',
    actionsMenu: 'Aktionsmenü',
    instruments: 'Instrumente',
    selectInstruments: 'Instrumente auswählen',
    instrumentTypeMismatch:
      'Es können nur Instrumente desselben Typs und derselben Version zusammen ausgewählt werden',
    unsavedChangesWarning:
      'Sie haben ungespeicherte Änderungen. Möchten Sie die Seite wirklich verlassen?',
  },
  conditionalAutomation: {
    sourceValue: 'Quellwert',
    operator: 'Operator',
    targetValue: 'Zielwert',
    operators: {
      equals: 'gleich',
      notEquals: 'ungleich',
      lessThan: 'kleiner als',
      greaterThan: 'größer als',
      lessThanOrEqual: 'kleiner oder gleich',
      greaterThanOrEqual: 'größer oder gleich',
    },
  },
  conditionalProperties: {
    conditions: 'Bedingungen',
    conditionName: 'Bedingungsname',
    deleteCondition: 'Bedingung löschen',
    addElseIfCondition: 'ElseIf-Bedingung hinzufügen',
    tabs: {
      details: 'Conditional-Details',
      automation: 'Conditional-Automatisierung',
    },
  },
  controlRoom: {
    labwareDetails: {
      labwareId: 'Laborgeräte-ID: {{labwareId}}',
      processId: 'Prozess-ID: {{processId}}',
      title: 'Laborgeräte-Details',
    },
    openOperatorActions: 'Operator-Aktionen öffnen',
    performRequiredAction: 'Erforderliche Aktion ausführen',
    task: {
      retryPrefix: 'Erneut versuchen: {{taskName}}',
    },
    labwareTable: {
      waitingOnHint: 'Task wartet auf den Abschluss von {{taskName}}.',
    },
    eventLog: {
      title: 'Ereignisprotokoll',
      event: 'Ereignis',
      eventType: 'Ereignistyp',
      byLabware: 'Nach Laborgeräten',
      byEventType: 'Nach Ereignistyp',
      searchLabware: 'Laborgeräte suchen',
      sortBy: 'Sortieren nach {{column}}',
      refresh: 'Aktualisieren',
      types: {
        replan: 'Neuplanung',
        alert: 'Alarm',
        other: 'Sonstige',
      },
    },
    permissions: {
      exportReports:
        'Sie haben keine Berechtigung, Berichte zu exportieren. Bitten Sie Ihren Administrator, den Zugriff export:reports zu gewähren',
      operatorActions: {
        read: 'Sie haben keine Berechtigung, Operator-Aktionen anzuzeigen. Bitten Sie Ihren Administrator, den Zugriff read:operator_actions zu gewähren',
      },
    },
    components: {
      abortLabwareBatch: {
        title: 'Laborgeräte-Batch abbrechen',
        description: 'Wählen Sie die Batches aus, die Sie abbrechen möchten',
        batchLabel: 'Batch {{batch}}',
        selector: {
          label: 'Batches auswählen',
          placeholder: 'Batches auswählen...',
          searchPlaceholder: 'Batches suchen',
          openLabel: 'Batch-Auswahl öffnen',
          removeLabel: 'Batch {{batch}} entfernen',
        },
        table: {
          labwareName: 'Laborgeräte-Name',
          currentTask: 'Aktueller Task',
          currentLocation: 'Aktuelle Position',
        },
        error: {
          loadFailed:
            'Batches konnten nicht geladen werden. Bitte versuchen Sie es erneut.',
        },
      },
      abortConfirm: {
        title:
          'Prüfen, wie betroffene Laborgeräte in diesem Workflow abgebrochen werden',
        description:
          'Die folgenden Laborgeräte werden im Rahmen dieses Workflows abgebrochen. Überprüfen und bestätigen Sie die Abbruch-Einstellungen, bevor Sie fortfahren.',
        descriptionWithName:
          'Die folgenden Laborgeräte werden im Rahmen des Workflows {{workflowName}} abgebrochen. Überprüfen und bestätigen Sie die Abbruch-Einstellungen, bevor Sie fortfahren.',
        labwareMoveAlert: {
          transport: {
            title: 'Manueller Transport vor dem Fortfahren erforderlich',
            description:
              'Die folgenden Laborgeräte befinden sich im Transport. Bewegen Sie jedes einzelne an das aufgeführte Ziel, bevor Sie fortfahren.',
            item: '{{labware}} → {{destination}}',
          },
          hasManualLabware: {
            description:
              'Einige Laborgeräte erfordern einen manuellen Eingriff. Bitte befolgen Sie die Bewegungsanweisungen für jedes betroffene Element.',
          },
        },
        labwareSection: {
          trigger: 'Betroffene Laborgeräte [{{count}}]',
          leaveAllInPlace: 'Alle Laborgeräte an Ort und Stelle belassen',
          search: 'Laborgeräte suchen',
          table: {
            labwareName: 'Laborgeräte-Name',
            currentTask: 'Aktueller Task',
            moveType: 'Bewegungsart',
            currentLocation: 'Aktuelle Position',
            returnLocation: 'Rückgabeort',
          },
        },
        moveType: {
          automated: 'Automatisiert',
          manual: 'Manuell',
        },
        returnLocation: {
          outsideWorkcell: 'Außerhalb der Workcell',
          startLocation: 'Startposition',
        },
        reason: 'Geben Sie einen Grund für den Abbruch an',
        errorLoadingPreview: 'Abbruch-Vorschau kann nicht geladen werden',
        gxp: {
          rerouteWorkflow: 'Workflow-Laborgeräte umleiten',
          rerouteBatch: 'Batch-Laborgeräte umleiten',
          abortBatch: 'Batch abbrechen',
          resumeWorkflow: 'Workflow nach fehlgeschlagener Umleitung fortsetzen',
        },
        buttons: {
          confirm: 'Abbruch bestätigen',
        },
        errorRecovery: {
          resume: 'Workflow fortsetzen',
          keepInPlace:
            'Workflow abbrechen (alle Laborgeräte an Ort und Stelle belassen)',
        },
      },
      abortLabware: {
        title: 'Laborgeräte abbrechen',
        dialogDescription:
          'Die folgenden Laborgeräte werden aus diesem Run entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
        manualRemovalNotice:
          'Sie müssen die abgebrochenen Laborgeräte manuell aus der Workcell entfernen.',
        accordion: {
          reason: 'Geben Sie einen Grund für den Abbruch dieser Laborgeräte an',
        },
      },
      ignoreTimeConstraints: {
        title: 'Einschränkungen ignorieren',
        dialogDescription:
          'Überprüfen Sie die unten stehenden Zeitlimits und geben Sie einen Grund an, bevor Sie sie ignorieren.',
        accordion: {
          reason:
            'Geben Sie einen Grund für das Ignorieren dieser Einschränkungen an',
        },
      },
      abortWorkflow: {
        title: 'Workflow abbrechen',
        dialogDescription:
          'Die folgenden Laborgeräte werden beim Beenden dieses Workflows abgebrochen. Sie können den Workflow ab diesem Punkt nicht fortsetzen.',
        description:
          'Die folgenden Laborgeräte werden im Rahmen dieses Workflows abgebrochen. Sie können den Workflow ab diesem Punkt nicht fortsetzen.',
        accordion: {
          reason: 'Geben Sie einen Grund für den Abbruch dieses Workflows an',
        },
      },
      skipTask: {
        title: 'Task überspringen',
        dialogDescription:
          'Schließen Sie den fehlgeschlagenen Task manuell ab und fahren Sie mit dem nächsten Task im Workflow fort.',
        info: {
          title: 'Operator-Aktion erforderlich',
          description:
            'Schließen Sie den fehlgeschlagenen Task manuell ab. Stellen Sie sicher, dass sich die Laborgeräte an der richtigen Position befinden, bevor Sie fortfahren.',
        },
        accordion: {
          tasks: {
            trigger: 'Manuell abzuschließender Task',
            table: {
              newLocation: 'Neue Position',
            },
          },
        },
        confirmation:
          'Wenn Sie fortfahren, bestätigen Sie, dass der fehlgeschlagene Task manuell abgeschlossen wurde und alle Laborgeräte korrekt neu positioniert wurden',
        buttons: {
          continue: 'Weiter zum nächsten Task',
        },
      },
      failoverTask: {
        title: 'Failover-Gerät verwenden',
        dialogDescription:
          'Führen Sie den Task auf einem alternativen Instrument aus. Die Planungs-Engine versucht, das nächstbeste verfügbare Instrument zu finden, um den fehlgeschlagenen Task auszuführen.',
        instruments: {
          label: 'Verfügbare Failover-Instrumente',
          help: 'Wählen Sie aus, auf welche Instrumente die Laborgeräte ausweichen dürfen. Die Planungs-Engine wählt eines der ausgewählten Instrumente aus.',
          empty: 'Keine Failover-Instrumente verfügbar',
        },
        transportMode: {
          label: 'Transport-Modus',
          info: 'Wie die Laborgeräte zum Failover-Instrument bewegt werden – automatisch durch den Roboter oder manuell durch einen Operator.',
          automatic: 'Automatisch',
          manual: 'Manuell',
          manualInfo: {
            title: 'Operator-Aktion erforderlich',
            description:
              'Sie werden aufgefordert, jedes Laborgerät manuell zum Failover-Instrument zu bewegen.',
          },
        },
        residentLabware: {
          label: 'Betroffene Laborgeräte',
          empty:
            'Keine Laborgeräte, die vom fehlgeschlagenen Instrument entfernt werden müssen',
          table: {
            batch: 'Batch',
            currentLocation: 'Aktuelle Position',
          },
        },
        confirmation:
          'Wenn Sie fortfahren, bestätigen Sie, dass das fehlgeschlagene Instrument für den Rest des Workflows deaktiviert wird.',
        buttons: {
          continue: 'Mit Failover-Gerät fortfahren',
        },
        failure: {
          description:
            'Die Planungs-Engine konnte kein verfügbares Instrument finden, um den fehlgeschlagenen Task auszuführen.',
        },
      },
      retryTask: {
        title: 'Task erneut versuchen',
        dialogDescription: 'Fehlgeschlagenen Task erneut versuchen',
        confirmation:
          'Wenn Sie fortfahren, bestätigen Sie, dass die betroffenen Laborgeräte zum Startpunkt des fehlgeschlagenen Tasks zurückbewegt wurden.',
        info: {
          description:
            'Um den fehlgeschlagenen Task erneut zu versuchen, bewegen Sie die betroffenen Laborgeräte zurück zum Startpunkt des fehlgeschlagenen Tasks.',
        },
      },
      pauseTransport: {
        title: 'Transport pausieren',
        dialogDescription:
          'Durch das Pausieren wird der aktuelle Transport vor dem Stoppen abgeschlossen. Überprüfen Sie unten die aktiven Tasks und Zeitlimits.',
        description:
          'Bevorstehende Transport-Tasks werden ausgesetzt, nachdem die aktuellen Transport-Tasks abgeschlossen wurden. Andere Tasks werden wie geplant fortgesetzt.',
        accordion: {
          currentTransportTask: {
            trigger: 'Aktueller Transport-Task',
          },
          nextTransportTask: {
            trigger: 'Nächster Transport-Task',
          },
          activeTimeConstraints: {
            trigger: 'Aktive Zeitlimits',
            table: {
              constraint: 'Zeitlimit',
            },
          },
          reason: 'Geben Sie einen Grund für das Pausieren des Transports an',
        },
      },
      resumeTransport: {
        title: 'Transport pausiert',
        dialogDescription:
          'Überprüfen Sie die Pause-Details und ausstehenden Tasks, bevor Sie die Workflow-Ausführung fortsetzen.',
        description:
          'Der Transport wurde pausiert. Andere Tasks werden wie geplant fortgesetzt',
        accordion: {
          pauseDetails: {
            trigger: 'Pause-Details',
          },
        },
      },
      addToQueue: {
        button: {
          disabledTooltip:
            'Beheben Sie die Validierungsfehler, bevor Sie zur Warteschlange hinzufügen',
        },
        topbar: {
          nameLabel: 'Planname',
          stalePlacementTooltip:
            'Neu berechnen, um Ihre Änderungen zu übernehmen',
        },
        batchSize: {
          title: 'Batch-Größe',
          label: 'Anzahl der Batches',
        },
        parameters: {
          title: 'Parameter bearbeiten',
          description: 'Parameterwerte für diesen Durchlauf anpassen',
          empty: 'Keine Parameter für diesen Workflow erforderlich',
          invalidTooltip: 'Parameterfehler vor der Berechnung beheben',
          unparseableSeedHint:
            'Der gespeicherte Wert konnte für diesen Feldtyp nicht gelesen werden. Geben Sie einen Wert ein, um ihn zu überschreiben, oder lassen Sie das Feld leer, um den Standardwert zu verwenden.',
        },
        settings: {
          latestTime: 'Späteste Zeit',
          earliestTime: 'Früheste Zeit',
          title: 'Einstellungen zum Hinzufügen anpassen',
          schedule: {
            label: 'Hinzufügen zu',
            nextAvailable: 'Nächster verfügbarer Slot',
            timeConstraint: 'Zeitlimits angeben',
          },
          earliestStart: {
            label: 'Frühester Start',
          },
          latestFinish: {
            label: 'Spätestes Ende',
          },
          priority: {
            title: 'Priorität',
            low: 'Niedrig',
            high: 'Hoch',
          },
          calculate: {
            cta: 'Position in Warteschlange berechnen',
          },
        },
        preview: {
          title: 'Position in Warteschlange',
          empty:
            'Klicken Sie auf „Position in Warteschlange berechnen“, um eine Vorschau Ihres Workflows in der Warteschlange anzuzeigen',
          loading: {
            body: 'Pläne, die in die Warteschlange berechnet werden, können eingesehen und aufgerufen werden über die',
          },
          start: {
            label: 'Vsl. Startzeit',
          },
          end: {
            label: 'Vsl. Endzeit',
          },
          manualTasks: {
            title: 'Vsl. Zeiten für manuelle Aufgaben',
            empty: 'Keine manuellen Aufgaben für diesen Workflow',
          },
          setupTimes: {
            title: 'Vsl. Setup-Zeiten',
            empty: 'Keine Setup-Zeiten für diesen Workflow',
          },
          cleanupTimes: {
            title: 'Vsl. Cleanup-Zeiten',
            empty: 'Keine Cleanup-Zeiten für diesen Workflow',
          },
          fullQueueDetails: 'Vollständige Warteschlangendetails',
          movedWorkflows: 'Verschobene Workflows:',
          tooltip:
            'Alle verschobenen Tasks erfüllen weiterhin die angewendeten Einstellungen und Regeln',
        },
        gxpDescription: 'Hinzufügen zur Warteschlange bestätigen',
        errors: {
          fetchFailed:
            'Beim Abrufen des Warteschlangenstatus ist ein Fehler aufgetreten',
          deviceGroupNotReady:
            'Richten Sie diese Geräte ein, bevor Sie zur Warteschlange hinzufügen: {{deviceNames}}',
          presetArchived:
            '{{presetName}} wurde archiviert und kann nicht zur Warteschlange hinzugefügt werden',
          presetNotSimulated:
            '{{presetName}} muss simuliert werden, bevor es zur Warteschlange hinzugefügt werden kann',
          presetNotFound:
            'Diese Voreinstellung existiert nicht mehr. Aktualisieren Sie und versuchen Sie es erneut.',
          unknown:
            'Beim Hinzufügen der Voreinstellung zur Warteschlange ist ein Fehler aufgetreten',
          parameterInvalid: 'Dieser Parameterwert ist ungültig',
          tryAgain:
            'Bitte versuchen Sie erneut, zur Warteschlange hinzuzufügen.',
        },
        queueCalendar: {
          hourMarker: '{{hours}} h',
          empty: 'Keine Tasks geplant',
          slider: {
            day: 'Tag {{number}}',
            dayRange: 'Tag {{start}} - Tag {{end}}',
            dayCount_one: '{{count}} Tag',
            dayCount_other: '{{count}} Tage',
            showCurrentWorkflow: 'Aktuellen Workflow anzeigen',
            ariaLabel: 'Schieberegler für Tagesbereich',
          },
          badges: {
            development: 'Entwicklung',
            productionShort: 'Prod',
            developmentShort: 'Dev',
            moved: 'Verschoben',
            gap: 'Lücke',
            overnight: 'Über Nacht',
            manualBooking: 'Manuelle Buchung',
          },
          legend: {
            currentWorkflow: 'Aktueller Workflow',
            scheduledWorkflow: 'Geplanter Workflow',
          },
          hover: {
            estStart: 'Vsl. Start:',
            estEnd: 'Vsl. Ende:',
            priority: 'Priorität:',
            priorityAriaLabel: 'Job-Prioritätsstufe',
            earliestStart: 'Frühester Start:',
            latestFinish: 'Spätestes Ende:',
            overnightWorkflow: 'Workflow über Nacht',
          },
          aria: {
            daySchedule: 'Zeitplan für Tag {{dayNumber}}',
            job: 'Job {{name}}',
            movedEarlier: 'Auf früher verschoben',
            movedLater: 'Auf später verschoben',
            overnightJob: 'Job über Nacht',
            partOfProcess: 'Teil eines Prozesses',
          },
        },
      },
      progressHeader: {
        controls: {
          resume: 'Transport fortsetzen',
          intervene: 'Eingreifen',
        },
        startTime: 'Startzeit: {{time}}',
        abortedAt: 'Abgebrochen um {{time}}',
        failedAt: 'Fehlgeschlagen um {{time}}',
        metrics: {
          completionEst: 'Abschluss (vsl.)',
          samples: 'Proben',
          materials: 'Materialien',
        },
      },
    },
    layout: {
      leftPanel: {
        title: 'Workflow-Details',
        togglePanel: 'Workflow-Details ein-/ausblenden',
        dateStarted: 'Startdatum',
        plannedStart: 'Geplanter Start',
        expectedDuration: 'Erwartete Dauer',
        completionEst: 'Abschluss vsl.',
        completedAt: 'Abgeschlossen am',
        runId: 'Run-ID',
        executionLogsExport: {
          buttonText: 'Ausführungsprotokolle CSV',
          modal: {
            title: 'Ausführungsprotokolle exportieren',
            description:
              'Wählen Sie aus, welche Laborgeräte-Daten exportiert werden sollen.',
            labwareSelection: {
              allLabware: 'Alle Laborgeräte in diesem Workflow',
              selectedLabware: 'Nur ausgewählte Laborgeräte',
              itemsCount: '{{count}} Elemente',
              selectionRequired:
                'Bitte wählen Sie mindestens ein Laborgerät aus',
            },
            exportOptions: {
              label: 'In den Export einzuschließende Daten',
              includeTransport: 'Transport einschließen',
              includeTransportDescription:
                'Fügt alle Transport-Tasks zum Protokoll hinzu',
              includeExecutionParameters: 'Ausführungsparameter einschließen',
              includeExecutionParametersDescription:
                'Für die Workflow-Ausführung definierte Parameterwerte',
            },
            actions: {
              export: 'Protokolle exportieren',
            },
            exportError:
              'Ausführungsprotokolle konnten nicht exportiert werden. Bitte versuchen Sie es erneut.',
          },
        },
        workflowDescription: 'Workflow-Beschreibung',
      },
      rightPanel: {
        batchConsistency: 'Batch-Konsistenz',
        batchConsistencyPlaceholder: 'Batch-Konsistenz in Kürze verfügbar',
        toggleIntegrityRisks: 'Integritätsrisiken ein-/ausblenden',
        topic: {
          jobReplanning: 'Neuplanung',
          planUpdated: 'Plan aktualisiert',
          workflowCompleted: 'Workflow abgeschlossen',
          workflowFailed: 'Workflow fehlgeschlagen',
          taskFailed: 'Task fehlgeschlagen',
          fatalError: 'Schwerwiegender Fehler',
          warning: 'Warnung',
          taskAutoRetry: 'Task automatisch wiederholen',
          manualTaskDue: 'Manuelle Aktion fällig',
          manualTaskUpcoming: 'Manuelle Aktion bevorstehend',
          functionStepCompleted: 'Funktionsschritt erfolgreich',
          timeConstraintBroken: 'Zeitlimit überschritten',
          orchestratorAlert: 'Job-Alarm',
          deviceAlert: 'Geräte-Alarm',
          safetyStateChanged: 'Sicherheitsstatus geändert',
          safetyEvent: 'Sicherheitsereignis',
          witnessSignatureRequested: 'Freigabe erforderlich',
          witnessSignaturePending: 'Warten auf Zeugen',
          witnessSignatureCompleted: 'Zeugensignatur abgeschlossen',
          witnessSignatureRejected: 'Zeugensignatur abgelehnt',
          unknown: 'Unbekannter Ereignistyp',
        },
        noEvents: 'Noch keine Ereignisse',
        card: {
          when: 'Wann',
          resolvedTime: 'Behebungszeit',
        },
      },
      metricsPanel: {
        title: 'Laborgeräte: Proben',
        setViewToTop: 'Tabellenansicht nach oben setzen',
        setViewToBottom: 'Tabellenansicht nach unten setzen',
      },
    },
    parallelWorkflows: {
      title: 'Parallele Workflows',
      errorLoading: 'Parallele Workflows können nicht geladen werden',
      summary: {
        instrumentUtilization: 'Geräteauslastung',
      },
    },
    workflow: {
      errorLoadingPlan: 'Workflow-Plan kann nicht geladen werden',
      replanning: {
        abortNotice: {
          title: 'Der Workflow wird nach der Neuplanung abgebrochen',
          description:
            'Eine Neuplanung läuft derzeit. Die Abbruch-Aktion wird nach der Neuplanung angewendet.',
        },
        pauseNotice: {
          title: 'Der Workflow wird nach der Neuplanung pausiert',
          description:
            'Eine Neuplanung läuft derzeit. Die Pause-Aktion wird nach der Neuplanung angewendet.',
        },
      },
      beacon: {
        error: 'Fehler: {{taskName}} fehlgeschlagen — {{labwareName}}',
        errorNoLabware: 'Fehler: {{taskName}} fehlgeschlagen',
        calculatingNoReason: 'Neuplanung läuft',
        paused: 'System pausiert — Warten auf Operator-Aktion',
        currentTask: 'Aktueller Task: {{description}}',
        currentTasks: 'Aktuelle Tasks: {{descriptions}}, + {{count}} weitere',
        standby: 'Standby: nächste Aktivität in {{duration}}',
        taskFormat: '{{name}} — {{device}} — {{duration}}',
        taskFormatNoTime: '{{name}} — {{device}}',
        taskFormatNoDevice: '{{name}} — {{duration}}',
        currentTasksNoOverflow: 'Aktuelle Tasks: {{descriptions}}',
        calculatingDetail: {
          overrun: 'Task {{taskName}} überschreitet um {{duration}}',
          underrun: 'Task {{taskName}} unterschreitet um {{duration}}',
          resume: 'Neuplanung, nachdem das System pausiert wurde',
          conditional:
            'Task {{taskName}} hat ein unerwartetes bedingtes Ergebnis erzeugt',
        },
      },
      error: {
        description:
          'Beim Anzeigen des Workflows ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        retry: 'Erneut versuchen',
      },
      devicesView: {
        toggleDevices: 'Geräte-Panel ein-/ausblenden',
        noDeviceGroup: 'Diesem Workflow ist keine Gerätegruppe zugewiesen',
        configuring: 'Wird konfiguriert',
        manualTasks: 'Manuelle Aufgaben',
        setupTasks: 'Setup-Tasks',
        cleanupTasks: 'Cleanup-Tasks',
        noPendingTasks: 'Keine ausstehenden Tasks',
        dueAt: 'Fällig: {{time}}',
        viewNotification: 'Benachrichtigung anzeigen',
        configuringStatus: 'Wird konfiguriert...',
        actionType: {
          labwareLoading: 'Laborgeräte laden',
          functionDeviceCommand: 'Gerätebefehl (Funktion)',
        },
        labwareTable: {
          location: 'Position',
        },
        combinedAction: {
          viewButton: 'Kombinierte Aktion anzeigen',
        },
      },
    },
    schedule: {
      tabs: {
        queue: 'Warteschlange',
        operations: 'Operationen',
      },
      queue: {
        quickFilter: {
          triggerAriaLabel: 'Schnellzeitfilter',
          datePicker: 'Datumsauswahl',
          startTimeLabel: 'Startzeit',
          endTimeLabel: 'Endzeit',
          endBeforeStart: 'Das Ende muss nach dem Start liegen',
        },
        card: {
          startEst: 'Start [vsl.]',
          endEst: 'Ende [vsl.]',
          workflowsCompleted: 'Abgeschlossene Workflows',
          workflowsInProgress: 'Workflows in Bearbeitung',
          batchesCompleted: 'Abgeschlossene Batches',
          batchesInProgress: 'Batches in Bearbeitung',
          reasonMissing: 'Grund nicht verfügbar',
          workflowAborted: 'Workflow abgebrochen',
          processAborted: 'Prozess abgebrochen',
          workflowRemoved: 'Workflow entfernt',
          processRemoved: 'Prozess entfernt',
          warnings: 'Warnungen',
          workflowsCalculating_one:
            '<strong>{{count}}</strong> Workflow wird berechnet...',
          workflowsCalculating_other:
            '<strong>{{count}}</strong> Workflows werden berechnet...',
          batchesCalculating_one:
            '<strong>{{count}}</strong> Batch wird berechnet...',
          batchesCalculating_other:
            '<strong>{{count}}</strong> Batches werden berechnet...',
        },
        popover: {
          executionPresetInstanceName:
            'Name der Ausführungsvoreinstellungs-Instanz',
          presetInfo: 'Voreinstellungs-Info',
          viewAlert: 'Alarm anzeigen',
          batchNumber: 'Batch #{{number}}',
          workflowNumber: 'Workflow #{{number}}',
          workflowsCount: '{{count}} Workflows',
        },
        filters: {
          byItemType: 'Nach Prozess-Workflow oder Workflow',
          itemType: {
            processWorkflow: 'Prozess-Workflow',
            workflow: 'Eigenständiger Workflow',
          },
          byWorkflowAndProcess: 'Nach Workflows und Prozessen',
          groups: {
            standaloneWorkflows: 'Eigenständige Workflows',
          },
        },
        menuLabel: 'Warteschlangen-Aktionen',
        removeFromQueue: 'Aus Warteschlange entfernen',
        confirmRemoveTitle: 'Aus Warteschlange entfernen?',
        confirmRemoveDescription:
          'Dadurch wird „{{jobName}}“ aus der Warteschlange entfernt.',
        reasonPlaceholder: 'Grund für die Entfernung',
        removeProcess: 'Prozess „{{name}}“ aus Warteschlange entfernen',
        removeStep: 'Schritt „{{name}}“ aus Warteschlange entfernen',
        removeWorkflow: 'Workflow „{{name}}“ aus Warteschlange entfernen',
        removeSuccess: 'Aus Warteschlange entfernt',
        removeError: 'Entfernen aus Warteschlange fehlgeschlagen',
        removeNotReady:
          'Entfernen aus Warteschlange nicht möglich. Bitte versuchen Sie es in Kürze erneut.',
      },
      operations: {
        workflowsInProgress: 'Workflows: in Bearbeitung',
        operatorActions: 'Operator-Aktionen',
        noActions: 'Keine ausstehenden Aktionen',
        groups: {
          next30: 'Nächste 30 Min.',
          later: 'Später',
        },
        countdownMinutes: '{{count}} Min.',
        noWorkflows: 'Keine Workflows in Bearbeitung',
        noDevices: 'Keine Geräte anzuzeigen',
        risks: 'Risiken',
        risksTitle: 'Integritätsrisiken',
        risksInfo:
          'Hebt Warnungen, Fehler, unerwartete Ereignisse und Batch-Konsistenz-Abweichungen hervor, die die Integrität oder Genauigkeit dieses Workflows beeinträchtigen können.',
        environmentalParameters: 'Umgebungsparameter',
        environmentalParametersMin: 'Min.',
        environmentalParametersMax: 'Max.',
        environmentalParametersNoData: 'Keine Parameter verfügbar',
        currentlyUsedBy: 'Derzeit verwendet von',
        maxCapacity: 'Max. Kapazität',
        expectedUtilization: 'Erwartete Auslastung [%]',
        chips: {
          pendingActions: 'Ausstehende Aktionen',
          activeWorkflows: 'Aktive Workflows',
          queuedWorkflows: 'Workflows in Warteschlange',
          completedWorkflows: 'Abgeschlossene Workflows',
          abortedWorkflows: 'Abgebrochene Workflows',
          activeDeviceGroups: 'Aktive Gerätegruppen',
        },
      },
      process: {
        errorLoading: 'Prozess kann nicht geladen werden',
        abortProcess: {
          title: 'Prozess abbrechen',
          description:
            'Das Abbrechen des Prozesses stoppt alle aktiven Workflows. Diese Aktion kann nicht rückgängig gemacht werden.',
          reasonLabel: 'Grund für den Abbruch',
          reasonPlaceholder:
            'Geben Sie einen Grund für den Abbruch dieses Prozesses an',
          confirm: 'Prozess abbrechen',
          continue: 'Fortfahren',
          back: 'Zurück',
          abortSuccess: 'Prozessabbruch angefordert',
          abortError: 'Prozess konnte nicht abgebrochen werden',
          selectTitle:
            'Legen Sie die abzubrechenden Teile dieses Prozesses fest',
          selectDescription:
            'Wählen Sie aus, welche Workflows abgebrochen werden sollen. Nicht ausgewählte Workflows laufen weiter.',
          selectWorkflowsLabel: 'Workflows auswählen',
          selectWorkflowsPlaceholder: 'Workflows suchen und auswählen',
          noWorkflowsAvailable: 'Keine Workflows verfügbar',
          handlingLabel: 'Batches und den nächsten Schritt auswählen',
          noWorkflowsSelected: 'Keine Workflows zur Bearbeitung ausgewählt',
          summaryLabel: 'Zusammenfassung',
          suggested: 'EMPFOHLEN',
          allBatches: 'Alle Batches ({{count}})',
          abortCompletely: 'Vollständig abbrechen',
          cannotBeUndone: 'Diese Aktion kann nicht rückgängig gemacht werden.',
          labwareTitle:
            'Legen Sie fest, wie Laborgeräte in abgebrochenen Batches gehandhabt werden',
          labwareNoticeTitle: 'Handhabung der Laborgeräte',
          labwareNotice:
            'Sie werden durch eine Operator-Aktion benachrichtigt, wenn die Laborgeräte sicher gehandhabt werden können.',
          labwareLoadError:
            'Laborgeräte für diesen Workflow konnten nicht geladen werden.',
          columns: {
            batches: 'Abzubrechende Batches',
            nextStep: 'Nächster Schritt',
            aborting: 'Wird abgebrochen?',
          },
          status: {
            aborting: 'Wird abgebrochen',
            cancelled: 'Storniert',
          },
        },
        workflows: {
          title: 'Workflows [{{count}}]',
          toComplete: 'Abzuschließen [{{count}}]',
        },
        labwareTracking: {
          title: 'Laborgeräte-Nachverfolgung',
          devicesActive: 'Geräte ({{active}}/{{total}} aktiv)',
          allDevices: 'Alle Geräte',
          filterHint:
            'Klicken Sie auf Geräte oder Gruppen, um die Laborgeräte-Tabelle unten zu filtern.',
          columns: {
            barcode: 'Barcode',
            contents: 'Inhalt',
            timeRemaining: 'Verbleibende Zeit',
          },
        },
      },
    },
    setupPhase: {
      noInstructions: 'Keine Setup-Anweisungen',
      instructionCard: {
        completed: 'Abgeschlossen {{completedAt}}',
        dueAt: 'Fällig um {{time}}',
        labware: {
          constraints: 'Einschränkungen',
        },
      },
      constraintBadge: {
        min: 'Min {{duration}}',
        max: 'Max {{duration}}',
      },
    },
  },
  createPinDialog: {
    title: 'E-Signatur-PIN erstellen',
    description:
      'Eine sichere PIN ist erforderlich, um Aktionen in einer GxP-Umgebung elektronisch zu signieren.',
    descriptionDetail:
      'Sie werden danach gefragt, wann immer Sie eine Aktion durchführen, freigeben oder bezeugen, die einen Datensatz erstellt, ändert oder löscht.',
    originalPin: 'Aktuelle PIN eingeben',
    enterPin: 'Sichere PIN mit 6 bis 8 Ziffern eingeben',
    newPin: 'Neue PIN mit 6 bis 8 Ziffern eingeben',
    confirmPin: 'PIN bestätigen',
    savePin: 'PIN speichern',
    error: {
      mismatch:
        'Die PINs stimmen nicht überein. Bitte versuchen Sie es erneut.',
      tooShort: 'Die PIN muss mindestens 6 Ziffern lang sein.',
      confirmTooShort: 'Bitte bestätigen Sie Ihre PIN.',
      originalTooShort: 'Bitte geben Sie Ihre aktuelle PIN ein.',
      originalPinIncorrect:
        'Die eingegebene aktuelle PIN ist falsch. Bitte versuchen Sie es erneut.',
      failed:
        'PIN konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    },
    update: {
      title: 'E-Signatur-PIN aktualisieren',
      submit: 'PIN aktualisieren',
    },
    success: {
      title: 'PIN erfolgreich erstellt',
      description:
        'Sie haben Ihre E-Signatur-PIN erfolgreich erstellt. Sie können Ihre PIN später in Ihren Kontoeinstellungen ändern.',
    },
    updateSuccess: {
      title: 'PIN erfolgreich aktualisiert',
      description:
        'Sie haben Ihre E-Signatur-PIN erfolgreich aktualisiert. Sie können Ihre PIN später in Ihren Kontoeinstellungen ändern.',
    },
  },
  errorFallback: {
    title: 'Etwas ist schiefgelaufen',
  },
  executionLogs: {
    navTitle: 'Protokolle',
    title: 'Ausführungsprotokolle',
    analysis: 'Analyse',
    search: 'Ausführungen suchen',
    infoBanner:
      'Audit-Daten werden in LINQ 30 Tage lang aufbewahrt. Um Langzeitspeicherung und Compliance zu gewährleisten, werden die Daten in Ihr externes System exportiert.',
    viewSettings: 'Ansichtseinstellungen',
    noRecords: 'Keine Ausführungsdatensätze gefunden',
    loadFailure: 'Ausführungsdatensätze konnten nicht geladen werden',
    recordCount_one: '{{count}} Ausführungsdatensatz',
    recordCount_other: '{{count}} Ausführungsdatensätze',
    table: {
      executionName: 'Ausführungsname',
      executionId: 'Ausführungs-ID',
      actualStartTime: 'Startzeit',
      actualCompletionTime: 'Abschlusszeit',
      initiator: 'Initiator',
    },
    status: {
      inProgress: 'In Bearbeitung',
    },
    filter: {
      label: 'Filter',
      timeWindow: {
        thisWeek: 'Diese Woche',
        thisMonth: 'Dieser Monat',
      },
      apply: 'Filter anwenden',
    },
    pagination: {
      previousPage: 'Vorherige Seite',
      nextPage: 'Nächste Seite',
    },
    detail: {
      loadFailure: 'Ausführungsdetails konnten nicht geladen werden',
      notFound: 'Ausführung nicht gefunden',
      viewExecution: 'Ausführung anzeigen',
      export: {
        label: 'Exportieren',
        unexpectedResponseError:
          'Unerwartetes Antwortformat erhalten. Bitte versuchen Sie es erneut.',
      },
      exportMenu: {
        bundle: 'Ausführungsprotokoll-Paket exportieren (alle Dateien)',
        summary: 'Ausführungszusammenfassung',
        history: 'Ausführungsverlauf',
        signatureRecords: 'Signaturdatensätze',
        artefacts: 'Artefakte',
      },
      executionDetails: 'Ausführungsdetails',
      recordId: 'Datensatz-ID: {{id}}',
      automation: 'Automatisierung',
      timeline: 'Zeitleiste',
      started: 'Gestartet',
      duration: 'Dauer:',
      planned: 'Geplant:',
      actual: 'Tatsächlich:',
      replans: 'Neuplanungen',
      replansImpact: 'Auswirkung auf die Workflow-Abschlusszeit',
      noReplansRecorded: 'Keine Neuplanungen aufgezeichnet',
      executionParameters: 'Ausführungsparameter',
      noExecutionParameters: 'Keine Parameter aufgezeichnet',
      integrityRisks: 'Integritätsrisiken ({{count}})',
      noIntegrityRisks: 'Keine Integritätsrisiken erkannt',
      integrityRiskOccurredAt: 'Aufgetreten am',
      integrityRiskSlack: 'Pufferzeit',
      devices: 'Geräte ({{count}})',
      noDevices: 'Keine Geräte aufgezeichnet',
      firmwareVersion: 'Firmware-Version',
      serialNumber: 'Seriennummer',
      globalConfig: 'Globale Konfiguration',
      notAvailable: 'Nicht verfügbar',
      userActions: 'Benutzeraktionen ({{count}})',
      noUserActions: 'Keine Benutzeraktionen aufgezeichnet',
      userActionUser: 'Benutzer',
      userActionTimestamp: 'Zeitstempel',
      userActionType: {
        EXECUTION_QUEUED: 'Initiieren',
        USER_RESUME: 'Fortsetzen',
        USER_REROUTE: 'Umleiten',
        USER_FAILOVER_TASK: 'Failover-Task',
        USER_CONTINUE_TASK: 'Task fortsetzen',
        USER_ABORT_LABWARE: 'Laborgeräte abbrechen',
        USER_RESOLVE_MANUAL_ACTION: 'Manuelle Aktion beheben',
        USER_CANCEL_STEP: 'Schritt abbrechen',
        USER_CANCEL_PROCESS: 'Prozess abbrechen',
      },
    },
    artefactDownloadDialog: {
      title: 'Artefakte herunterladen',
      description:
        'Wählen Sie die Tasks aus, deren Artefakte Sie in den ZIP-Download aufnehmen möchten.',
      noTasks: 'Dieser Run enthält keine Audit-Trail-Tasks zum Herunterladen.',
      batchCount_one: '({{count}} Batch)',
      batchCount_other: '({{count}} Batches)',
      download: 'Download',
      loadFailure: 'Tasks für diesen Run konnten nicht geladen werden.',
      downloadError:
        'Artefakte konnten nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
      unexpectedResponseError: 'Unerwartete Serverantwort',
    },
  },
  userLogs: {
    title: 'Benutzerprotokolle',
    missingValue: '—',
    retry: 'Erneut versuchen',
    export: {
      label: 'Benutzerprotokolle exportieren',
      unavailable:
        'Der Export von Benutzerprotokollen ist noch nicht verfügbar.',
    },
    actionType: {
      modify: 'Ändern',
      initiate: 'Initiieren',
      intervene: 'Eingreifen',
      alertResponse: 'Auf Warnung reagieren',
      upload: 'Hochladen',
      export: 'Exportieren',
      access: 'Zugreifen',
      approve: 'Genehmigen',
      review: 'Prüfen',
      reject: 'Ablehnen',
    },
    table: {
      timestamp: 'Zeitstempel',
      actionType: 'Aktionstyp',
      recordId: 'Datensatz-ID',
      actionPerformed: 'Ausgeführte Aktion',
      user: 'Benutzer',
    },
    list: {
      loading: 'Benutzerprotokolle werden geladen',
      empty: 'Keine Benutzerprotokolle verfügbar.',
      filteredEmpty: 'Keine Benutzerprotokolle entsprechen diesen Filtern.',
      failure: 'Benutzerprotokolle konnten nicht geladen werden.',
    },
    pagination: {
      loading: 'Weitere Benutzerprotokolle werden geladen',
      failure: 'Weitere Benutzerprotokolle konnten nicht geladen werden.',
    },
    filters: {
      allActionTypes: 'Alle Aktionstypen',
      noActionTypes: 'Keine Aktionstypen gefunden.',
      dateRange: 'Datumsbereich',
      clearDate: 'Datum löschen',
      search: 'Benutzerprotokolle durchsuchen',
    },
    detail: {
      title: 'Benutzerprotokolldetail',
      regionLabel: 'Ausgewähltes Benutzerprotokoll',
      close: 'Benutzerprotokolldetail schließen',
      loading: 'Benutzerprotokolldetail wird geladen',
      notFound: 'Benutzerprotokoll nicht gefunden oder nicht mehr verfügbar.',
      failure: 'Benutzerprotokolldetail konnte nicht geladen werden.',
      integrityFailure:
        'Gespeicherte Nachweise haben die Integritätsprüfung nicht bestanden und können nicht angezeigt werden.',
      none: 'Keine',
      copyEvidence: 'Nachweis kopieren',
      sections: {
        actionInformation: 'Aktionsinformationen',
        changeEvidence: 'Änderungsnachweis',
        affectedRecord: 'Betroffener Datensatz',
        userInformation: 'Benutzerinformationen',
        reason: 'Grund',
        impactedResources: 'Betroffene Ressourcen',
        executionLog: 'Ausführungsprotokoll',
        signatureInformation: 'Signaturinformationen',
      },
      fields: {
        timestamp: 'Datum und Uhrzeit',
        timezone: 'Zeitzone',
        outcome: 'Ergebnis',
        evidenceKind: 'Art',
        evidenceVersion: 'Version',
        evidencePayload: 'Nachweisdaten',
        recordLabel: 'Bezeichnung',
        recordType: 'Datensatztyp',
        affectedRecordId: 'Betroffene Datensatz-ID',
        userId: 'Benutzer-ID',
        email: 'E-Mail',
        authenticated: 'Authentifiziert',
        roles: 'Rollen',
        resourceType: 'Ressourcentyp',
        resourceId: 'Ressourcen-ID',
        resourceLabel: 'Ressourcenbezeichnung',
        signatureRequirement: 'Anforderung',
      },
      outcome: {
        succeeded: 'Erfolgreich',
      },
      recordType: {
        executionPreset: 'Ausführungsvoreinstellung',
      },
      evidenceKind: {
        inlineDiff: 'Inline-Differenz',
      },
    },
  },
  executionPreset: {
    statusCard: {
      infoLabel: 'Statusinfo',
    },
    settings: {
      toggleBatchingAriaLabel: 'Batching umschalten',
      title: 'Settings der Ausführungsvoreinstellung',
      parameters: 'Parameters',
      feasibilityStrategy: {
        label: 'Machbarkeitsstrategie',
        description:
          'Wählen Sie aus, wie der Planer vor der Optimierung nach einem machbaren Plan sucht. Die meisten Workflows funktionieren am besten ohne Strategie.',
        strategy: {
          consistentBatchRepetition: 'Konsistente Batch-Wiederholung',
          batchGrouping: 'Batch-Gruppierung',
        },
        strategyDescription: {
          standard:
            'Alle Batches gemeinsam in einem einzigen Durchgang planen.',
          consistentBatchRepetition:
            'Eine Baseline von Batches optimieren und diesen Zeitplan dann für die übrigen Batches wiederholen, um die Zeiten konsistent zu halten.',
          batchGrouping:
            'Batches in Gruppen fester Größe planen, um die Rechenzeit bei einer großen Anzahl von Batches zu reduzieren.',
        },
        baselineBatchCount: {
          label: 'Baseline-Batch-Anzahl',
          description:
            'Anzahl der Batches, die optimiert werden, bevor der Zeitplan wiederholt wird.',
        },
        baselineOptimisationTimeSeconds: {
          label: 'Baseline-Optimierungszeit',
          description:
            'Wie lange die Optimierung der Baseline-Batches dauern soll.',
        },
        groupBatchCount: {
          label: 'Gruppen-Batch-Anzahl',
          description:
            'Anzahl der Batches, die in jeder Gruppe gemeinsam geplant werden.',
        },
        maxLabwareWaitTimeSeconds: {
          label: 'Maximale Wartezeit der Laborgeräte',
          description:
            'Die maximal zulässige Wartezeit zwischen Tasks für ein Laborgerät.',
        },
      },
      optimisationStrategy: {
        label: 'Optimierungsstrategie',
        description:
          'Wählen Sie aus, worauf der Planer bei der Simulation dieser Ausführungsvoreinstellung optimieren soll. Ziele weiter oben in der Liste haben Vorrang und größeren Einfluss auf den resultierenden Plan.',
        addPriority: 'Priorität hinzufügen',
        empty: {
          title: 'Noch keine Prioritäten',
          description:
            'Fügen Sie ein Ziel hinzu, um dem Planer mitzuteilen, worauf er optimieren soll. Ohne Prioritäten sucht der Planer nur nach einem machbaren Plan.',
        },
        objective: {
          executionDuration: 'Ausführungsdauer',
          consistency: 'Konsistenz',
          slack: 'Puffer',
          timingTargets: 'Zeitvorgaben',
        },
        objectiveTooltip: {
          executionDuration:
            'Auf die gesamte Ausführungsdauer von Anfang bis Ende abzielen.',
          slack:
            'Die Planungsflexibilität durch größere Zeitpuffer um zeitliche Einschränkungen maximieren.',
          consistency:
            'Schwankungen bei Task- und Wartezeiten über Batches hinweg reduzieren, um die Konsistenz zu verbessern.',
          labwareWaitTime:
            'Die Wartezeit von Laborgeräten zwischen Tasks minimieren.',
          timingTargets:
            'Das Task-Timing so optimieren, dass definierte Vorgaben innerhalb der zulässigen Toleranz erreicht werden.',
        },
        objectiveHelper: {
          executionDuration:
            'Auf eine gesamte Ausführungsdauer abzielen. Der Planer versucht, einen Plan zu erstellen, der dieser Dauer möglichst nahekommt.',
          slack:
            'Den verfügbaren Puffer um zeitliche Einschränkungen maximieren. Pläne mit mehr Puffer sind in der Regel widerstandsfähiger gegenüber Verzögerungen und Schwankungen in der Ausführung.',
          labwareWaitTime:
            'Die Wartezeit von Laborgeräten zwischen Tasks minimieren und dabei die Wartezeiten unter dem konfigurierten Maximum halten.',
          consistency:
            'Auf konsistente Zeiten über Batches hinweg optimieren, indem Unterschiede bei Task-Dauern und Wartezeiten reduziert werden.',
        },
        weight: {
          label: 'Gewichtung',
          sameOrBetter: 'Gleich oder besser',
          within1Percent: 'Innerhalb von 1 %',
          within5Percent: 'Innerhalb von 5 %',
          within10Percent: 'Innerhalb von 10 %',
        },
        stopAfter: {
          label: 'Optimierung stoppen nach (Sekunden)',
        },
        row: {
          remove: 'Priorität entfernen',
          dragHandle: 'Priorität neu anordnen',
        },
      },
      relations: {
        label: 'Beziehungen',
        timeConstraint: 'Zeitlimit',
        noRelations: 'Keine Beziehungen definiert',
      },
      loadBalancing: {
        label: 'Load Balancing',
        searchPlaceholder: 'Workflow, Task, ... suchen',
        noResults: 'Keine passenden Tasks oder Instrumente',
      },
      allocationRules: {
        label: 'Zuweisungspräferenzen',
        addPreference: 'Präferenz hinzufügen',
        addColumn: 'Spalte hinzufügen',
        allLabwareAdded: 'Alle Laborgeräte bereits hinzugefügt',
        empty: {
          title: 'Noch keine Präferenzen',
          description:
            'Heften Sie Laborgeräte an eine bestimmte Spalte oder einen Slot-Bereich an. Präferenzen werden so weit berücksichtigt, wie alle Laborgeräte noch platziert werden können.',
        },
        row: {
          remove: 'Präferenz entfernen',
          selectLabware: 'Laborgeräte auswählen',
          noResults: 'Keine Laborgeräte gefunden',
          alreadyAdded: 'Bereits hinzugefügt',
          selectColumn: 'Spalte auswählen',
          slotsRange: 'Slots {{start}}–{{end}}',
          slotCount: '{{count}}/{{total}}',
          slotRangeAria: 'Slot-Bereich',
        },
      },
      combine: {
        label: 'Setup- und Cleanup-Gruppierung',
      },
      operatorAvailability: {
        label: 'Operator Verfügbarkeit',
        description:
          'Wählen Sie Workflows aus, die innerhalb der Operator Arbeitszeiten geplant werden sollen. Nicht ausgewählte Workflows können außerhalb dieser Beschränkung der Operator Arbeitszeiten geplant werden.',
        viewWorkingHours: 'Operator Arbeitszeiten anzeigen',
        setupCleanup:
          'Alle Setup- und Cleanup-Operator-Aktionen während der Arbeitszeiten planen',
        operatorActionsTooltip: 'Operator-Aktionen im Workflow',
        workingHours: {
          editButton: 'Operator Arbeitszeiten bearbeiten',
        },
      },
    },
  },
  executionPresetVersioning: {
    historyButtonLabel: 'Voreinstellungsverlauf',
    title: 'Versionsverlauf',
    description:
      'Bei jeder Simulation wird automatisch eine Version gespeichert.',
    empty: 'Noch keine Versionen',
    loading: 'Versionen werden geladen…',
    loadError:
      'Versionsverlauf konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
    current: 'Aktuell',
    previousVersions: 'Vorherige Versionen',
    versionLabel: 'Version {{number}}',
    by: 'von {{author}}',
    createRevision: 'Neue Version erstellen',
    creatingRevision: 'Wird erstellt…',
    productionLocked:
      'Diese Voreinstellung ist gesperrt, weil sich eine Version in Produktion befindet. Erstellen Sie eine neue Version oder stellen Sie eine wieder her, um Änderungen vorzunehmen.',
  },
  filterBar: {
    resetFilters: 'Filter zurücksetzen',
    activeFilters: 'Aktive Filter',
    filter: 'Filter',
    allLabel: 'Alle',
    clearDateRange: 'Datumsbereich löschen',
    datePicker: {
      am: 'AM',
      pm: 'PM',
    },
    treeFilter: {
      selectAll: 'Alle auswählen',
      deselectAll: 'Alle abwählen',
    },
    filters: {
      byType: 'Nach Typ',
      byStatus: 'Nach Status',
      byTimeWindow: 'Nach Zeitfenster',
      byDeviceGroup: 'Nach Gerätegruppe',
      byWorkflow: 'Nach Workflow',
      byProcess: 'Nach Prozess',
      failedToFetchDeviceGroups: 'Gerätegruppen konnten nicht abgerufen werden',
      options: {
        nextHour: 'Nächste Stunde',
        today: 'Heute',
        tomorrow: 'Morgen',
        thisWeek: 'Diese Woche',
        all: 'Alle',
        customDate: 'Benutzerdefiniertes Datum',
      },
    },
  },
  forbidden: {
    title: 'Zugriff verweigert',
    description: 'Sie haben keinen Zugriff auf diese Seite.',
    backToHome: 'Zurück zur Startseite',
  },
  gxp: {
    role: {
      author: 'Autor',
      witness: 'Zeuge',
    },
  },
  gxpSignatureDialog: {
    title: 'Elektronische Signatur erforderlich',
    description:
      'Ihre elektronische Signatur, einschließlich Ihres Namens, Datum/Uhrzeit und der Bedeutung Ihrer Signatur, wird Teil dieses Datensatzes im Benutzerprotokoll.',
    signingAs: 'Sie signieren als:',
    attestation:
      'Ich bestätige, dass ich diese Aktion durchgeführt habe und dass die erfassten Informationen vollständig und korrekt sind.',
    reasonForSignature: 'Grund für Signatur',
    guidance: 'Hinweise',
    authenticateSignature: 'Signatur authentifizieren',
    sign: 'Signieren',
    rejectTitle: 'Diese Aktion ablehnen',
    rejectDescription:
      'Geben Sie einen Grund an und authentifizieren Sie sich, um diese Aktion abzulehnen. Die ausführende Person wird benachrichtigt, damit sie die Aktion korrigieren und erneut einreichen kann.',
    rejectReasonLabel: 'Ablehnungsgrund',
    rejectReasonPlaceholder: 'Erklären Sie, warum diese Aktion abgelehnt wird',
    reject: 'Ablehnung bestätigen',
    error: {
      failed:
        'Signatur fehlgeschlagen. Bitte überprüfen Sie, ob Ihre PIN korrekt ist, und versuchen Sie es erneut.',
    },
    secondSignaturePending: {
      title: 'Zweite Signatur ausstehend',
      body: 'Bitte warten Sie, bis die zweite Signatur abgeschlossen ist. Wir benachrichtigen Sie, sobald dies abgeschlossen ist.',
    },
  },
  job: {
    estTime: 'Geschätzte Zeit',
    sidebar: {
      toggleSidebar: 'Seitenleiste umschalten',
    },
  },
  labSettings: {
    stageChanges: 'Änderungen vormerken',
    saveError:
      'Änderungen konnten nicht gespeichert werden. Bitte versuchen Sie es erneut.',
    operatorWorkingHours: {
      title: 'Operator Arbeitszeiten',
      description:
        'Legen Sie die Arbeitszeiten für die Operator Ihres Labors fest. Dies hilft dabei zu bestimmen, wann manuelle Operator-Aktionen stattfinden können.',
      effectiveFrom: 'Änderungen wirksam ab',
      immediately: 'Sofort',
      deleteTimeRange: {
        title: 'Zeitbereich löschen',
        description:
          'Möchten Sie diesen Zeitbereich wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      },
      days: {
        monday: 'Montag',
        tuesday: 'Dienstag',
        wednesday: 'Mittwoch',
        thursday: 'Donnerstag',
        friday: 'Freitag',
        saturday: 'Samstag',
        sunday: 'Sonntag',
      },
      searchTime: 'Zeit suchen...',
      noTimeFound: 'Keine Zeit gefunden.',
      open247:
        'Ihr Labor arbeitet derzeit rund um die Uhr. Es gelten keine Arbeitszeitbeschränkungen.',
      setHours: 'Arbeitszeiten festlegen',
      scheduledChange:
        'Eine Änderung der Arbeitszeiten ist für {{date}} geplant.',
      allDay: 'Ganztägig',
      closedWarning:
        'Alle Tage sind ausgeschaltet, daher ist das Labor die ganze Woche geschlossen. Schalten Sie einen Tag ein, um Betriebszeiten festzulegen. Zur Warteschlange hinzugefügte Workflows werden weiterhin geplant, aber als außerhalb der Betriebszeiten markiert.',
    },
    labDowntime: {
      title: 'Laborstillstand',
      description: 'Legen Sie Zeitfenster für den Laborstillstand fest.',
      deleteDowntime: {
        title: 'Stillstand löschen',
        description:
          'Möchten Sie diesen Stillstandseintrag wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      },
      selectDate: 'Datum auswählen',
      repeat: {
        doesNotRepeat: 'Wird nicht wiederholt',
        daily: 'Täglich',
        weekly: 'Wöchentlich am {{day}}',
        monthly: 'Monatlich am {{dayOrdinal}}',
        annually: 'Jährlich am {{date}}',
        everyWeekday: 'An jedem Wochentag',
      },
    },
    preview: {
      changed:
        'Die Betriebszeiten des Labors haben sich geändert: Änderungen in der Warteschlange hier ansehen',
      button: 'Vorschau',
      calculating: 'Vorschau wird berechnet',
      failed:
        'Vorschau konnte nicht berechnet werden. Versuchen Sie es erneut.',
      currentQueue: 'Aktuelle Warteschlange',
      impacted_one: '{{count}} Prozess/Workflow von der Änderung betroffen.',
      impacted_other:
        '{{count}} Prozesse/Workflows von der Änderung betroffen.',
      inProgressSafe: 'Laufende Aktivitäten sind nicht betroffen.',
      scheduledCaveat:
        'Diese Änderung ist für den {{date}} geplant und wirkt sich bis dahin nicht auf die aktuelle Warteschlange aus. Der Kalender unten zeigt eine Vorschau ihrer Auswirkung, als würde sie heute angewendet — die tatsächliche Auswirkung wird beim Inkrafttreten neu berechnet.',
      noConstraints: 'Keine verletzten Zeiteinschränkungen.',
      allConstraints: 'Alle Zeiteinschränkungen',
      brokenConstraints: 'Verletzte Einschränkungen',
      totalConstraints: 'Zeitliche Einschränkungen insgesamt',
      timeConstraints: 'Zeiteinschränkungen',
      otherConstraints: 'Andere verletzte Einschränkungen',
      confirmBlockedOther:
        'Kann nicht angewendet werden, solange andere Einschränkungen verletzt sind. Beheben Sie diese zuerst.',
      allWorkflows: 'Alle Workflows',
      collapseConstraints: 'Einschränkungen einklappen',
      hourly: 'Stündlich',
      daily: 'Täglich',
      showReplanning: 'Neuplanungsänderungen anzeigen',
      back: 'Zurück (diesen Bildschirm schließen)',
      revert: 'Änderungen rückgängig machen',
      confirm: 'Bestätigen',
      applyFailed: 'Die Laborzeiten-Änderung konnte nicht angewendet werden',
      dayLabel: 'Tag {{index}}',
      step: {
        setup: 'Vorbereitung',
        main: 'Hauptlauf',
        cleanup: 'Aufräumen',
        manual: 'Manuell',
        job: 'Auftrag',
      },
      kind: {
        process: 'Prozess',
        workflow: 'Workflow',
      },
      legend: {
        title: 'Zeitplanänderungen',
        unchanged: 'Unverändert',
        sameDay: 'Verschoben (gleicher Tag)',
        crossDay: 'Auf einen anderen Tag verschoben',
      },
      hiddenJobs: 'Ebenfalls an diesem Tag',
    },
    summary: {
      closed: 'Geschlossen',
      labClosed: 'Labor die ganze Woche geschlossen',
      unrestricted:
        'Rund um die Uhr in Betrieb — keine Arbeitszeitbeschränkungen',
      noDowntime: 'Kein Stillstand geplant',
      changeWorkingHours: 'Operator Arbeitszeiten ändern',
      changeDowntime: 'Laborstillstand ändern',
      // A time range; the separator lives here so a locale can change it if a hyphen doesn't fit.
      timeRange: '{{from}}-{{to}}',
      moreSlots: '+{{count}} mehr',
      showLess: 'Weniger anzeigen',
    },
  },
  labware: {
    unknownName: 'Unbekanntes Laborgerät',
    types: {
      Plate: 'Plate',
      Reservoir: 'Reservoir',
      'Tube Rack': 'Tube Rack',
      'Tip Rack': 'Tip-Rack',
    },
    add: 'Laborgeräte hinzufügen',
    outputSlot: 'Ausgabe-Slot',
    reusableAcrossBatches: 'Batchübergreifend wiederverwendbar',
    repeatsOnEveryBatch: 'Wird bei jedem Batch wiederholt',
    simulationError: 'An einem Simulationsfehler beteiligt',
    totalUnits: 'Einheiten gesamt',
    labwareType: 'Laborgeräte-Typ',
    motionProfile: {
      title: 'Laborgeräte-Bewegungsprofil',
      cardTitle: 'Bewegungsprofil',
      add: 'Bewegungsprofil hinzufügen',
      remove: 'Bewegungsprofil entfernen',
      speed: 'Geschwindigkeit',
      speedFast: 'Schnell',
      speedStandard: 'Standard',
      speedSlow: 'Langsam',
      speedGentle: 'Sanft',
    },
    addNewLabware: 'Neue Laborgeräte hinzufügen',
    editLabware: 'Laborgeräte bearbeiten',
    viewLabware: 'Laborgeräte anzeigen',
    editDialog: {
      description: 'Diese Laborgerätedefinition bearbeiten.',
      error:
        'Aktualisierung der Laborgeräte fehlgeschlagen. Bitte erneut versuchen.',
    },
    viewDialog: {
      description: 'Diese Laborgerätedefinition anzeigen.',
    },
    errorHandling: {
      returnLocation: 'Rückgabeort',
      returnLocationOptions: {
        start_location: 'Startposition',
        outside_workflow: 'Außerhalb des Workflows (manuell)',
        waste_bin: 'Abfallbehälter',
      },
    },
    createDialog: {
      description: 'Einen neuen Laborgerätetyp definieren.',
      info: 'Die neuen Laborgeräte werden für {{labwareName}} ausgewählt und stehen zudem für die Verwendung in anderen Workflows zur Verfügung',
      category: {
        tipRack: 'Tip-Rack',
        tubeRack: 'Tube Rack',
      },
      namePlaceholder: 'Laborgeräte-Namen eingeben',
      vendor: 'Hersteller',
      vendorPlaceholder: 'Herstellernamen eingeben',
      partNumber: 'Teilenummer',
      partNumberPlaceholder: 'Teilenummer eingeben (optional)',
      count: 'Anzahl',
      wellsPlaceholder: 'Wells #',
      rows: 'Zeilen',
      rowsPlaceholder: 'Zeilen #',
      cols: 'Spalten',
      colsPlaceholder: 'Spalten #',
      wellBottom: 'Well-Boden',
      none: 'Keine',
      wellBottomType: {
        flat: 'Flach',
        round: 'Rund',
        v: 'V',
      },
      dimensions: 'Abmessungen (mm)',
      length: 'Länge',
      width: 'Breite',
      height: 'Höhe',
      transportGrip: 'Transport-Greifhöhe (mm)',
      autoCalculateGrip: 'Transport-Greifhöhe automatisch berechnen',
      zOffset: 'Z-Offset',
      zOffsetPlaceholder: 'Z-Achsen-Offset',
      cgLandscape: 'CG Querformat',
      cgPortrait: 'CG Hochformat',
      closedGripperPlaceholder: 'Geschlossener Greifer',
      error:
        'Laborgeräte konnten nicht erstellt werden. Bitte versuchen Sie es erneut.',
      validation: {
        nameRequired: 'Name ist erforderlich',
        nameMaxLength: 'Name darf höchstens 100 Zeichen lang sein',
        nameAlphanumeric:
          'Name muss mindestens ein alphanumerisches Zeichen enthalten',
        vendorRequired: 'Hersteller ist erforderlich',
        vendorMaxLength: 'Hersteller darf höchstens 25 Zeichen lang sein',
        lengthPositive: 'Länge muss positiv sein',
        widthPositive: 'Breite muss positiv sein',
        heightPositive: 'Höhe muss positiv sein',
        numberInvalid: 'Muss eine gültige Zahl sein',
        integerRequired: 'Muss eine ganze Zahl sein',
        positiveRequired: 'Muss eine positive Zahl sein',
      },
    },
  },
  manualStepProperties: {
    tabs: {
      automation: 'Manuelle Automatisierung',
      details: 'Manuelle Details',
    },
    descriptionPlaceholder: 'Task-Beschreibung eingeben...',
    connectedLabware: 'Verbundene Laborgeräte',
    notExecutableNotice:
      'Manuelle Schritte werden zur Laufzeit noch nicht ausgeführt. Die Simulation oder Ausführung eines Prozesses, der einen enthält, schlägt fehl.',
    taskDefinition: 'Aufgabendefinition',
    taskDefinitionPlaceholder:
      'Beschreiben Sie die vom Bediener auszuführende Aufgabe...',
    pauseType: 'Pausentyp',
    pauseOptions: {
      none: 'Keine Pause',
      process: 'Prozess pausieren',
      batch: 'Batch pausieren',
    },
    estimatedDuration: 'Geschätzte Dauer (Sekunden)',
    associations: 'Aufgabenzuordnungen',
    associateInstrument: 'Mit Instrument verknüpfen',
    associateLabware: 'Mit Laborgerät verknüpfen',
    selectInstrument: 'Instrument auswählen',
    selectLabware: 'Verbundene Laborgeräte auswählen',
    noConnectedLabware:
      'Verbinden Sie Laborgeräte mit diesem Schritt, um sie zu verknüpfen.',
  },
  motionTimings: {
    title: 'Zeitmessungen',
    collected: 'Erfasst',
    stale: 'Veraltet',
    uncollected: 'Nicht erfasst',
    uncollectable: 'Nicht erfassbar',
    collectTimings: 'Zeitmessungen erfassen...',
    collectAll: 'Alle Zeitmessungen erfassen',
    collectMissingAndStale: 'Nur fehlende und veraltete erfassen',
    collecting: '{{completed}}/{{total}} Bewegungen werden erfasst',
    collectionFailed: 'Erfassung fehlgeschlagen',
    collectionLostTrack:
      'Der Erfassungsvorgang konnte nicht weiter verfolgt werden — der Dienst wurde möglicherweise neu gestartet. Erneut versuchen?',
    collectionError: {
      motionFailed:
        'Die Zeitmessung wurde gestoppt — eine Roboterbewegung ist fehlgeschlagen. Der Roboter ist möglicherweise mit einem Instrument kollidiert oder wurde von diesem blockiert. Stellen Sie sicher, dass der Roboter und der umliegende Bereich frei sind, überprüfen Sie das Training dieser Position noch einmal und starten Sie die Erfassung anschließend erneut.',
      timedOut:
        'Die Zeitmessung wurde gestoppt — eine Roboterbewegung wurde nicht rechtzeitig abgeschlossen. Prüfen Sie, ob der Roboter eingeschaltet, verbunden und frei von Hindernissen ist, und starten Sie die Erfassung anschließend erneut.',
      notTrained:
        'Dieses Gerät hat keine trainierten Positionen zum Erfassen. Trainieren Sie mindestens eine Position und starten Sie die Erfassung anschließend erneut.',
      generic:
        'Die Zeitmessung ist fehlgeschlagen. Prüfen Sie den Roboter und versuchen Sie es erneut; wenn das Problem weiterhin auftritt, wenden Sie sich an den Support.',
      affected: 'Betroffen: {{instrument}}, Slot {{slot}}',
      affectedInstrument: 'Betroffen: {{instrument}}',
      affectedSlot: 'Betroffen: Slot {{slot}}',
    },
    attentionTooltip_one:
      '{{count}} Zeitmessung erfordert Aufmerksamkeit (veraltet oder nicht erfasst)',
    attentionTooltip_other:
      '{{count}} Zeitmessungen erfordern Aufmerksamkeit (veraltet oder nicht erfasst)',
    statusDefinitions: 'Statusdefinitionen',
    collectedDefinition:
      'Erfolgreich aufgezeichnet und aktuell. Keine Änderungen am Anfahrpfad seit der Erfassung.',
    staleDefinition:
      'Zuvor erfasst, aber aufgrund von Änderungen am Anfahrpfad nicht mehr aktuell. Muss erneut erfasst werden.',
    uncollectedDefinition:
      'Kann zeitlich gemessen werden, wurde aber noch nicht erfasst.',
    uncollectableDefinition:
      'Kann nicht zeitlich gemessen werden. Anfahrpfad ist nicht richtig konfiguriert.',
    collectedDefinitionShuttle:
      'Erfolgreich aufgezeichnet und aktuell. Keine Änderungen an der Position seit der Erfassung.',
    staleDefinitionShuttle:
      'Zuvor erfasst, aber aufgrund von Positionsänderungen nicht mehr aktuell. Muss erneut erfasst werden.',
    uncollectedDefinitionShuttle:
      'Kann zeitlich gemessen werden, wurde aber noch nicht erfasst.',
    uncollectableDefinitionShuttle:
      'Kann nicht zeitlich gemessen werden. Position ist nicht richtig konfiguriert.',
    openCollectMenu: 'Erfassungsmodus wählen',
    openStatusDefinitions: 'Statusdefinitionen anzeigen',
  },
  navbar: {
    aria: {
      toggleSidebar: 'Seitenleiste umschalten - Benachrichtigungssymbol',
      mainNavigationButton: 'Hauptnavigationsschaltfläche',
      mainNavigation: 'Hauptnavigation',
    },
    sideRail: 'Seitenleiste',
  },
  notifications: {
    errors: 'Alarme',
    infoAlerts: 'Info-Alarme',
    emptyNotifications: 'Keine Benachrichtigungen vorhanden',
    functionDue: {
      inputsHeading: 'Eingaben für die Funktion hinzufügen/bearbeiten',
      startStep: 'Schritt starten',
      skipAction: 'Aktion überspringen',
      operatorNotes: 'Operator Notes',
    },
    sound: {
      mute: 'Benachrichtigungstöne stummschalten',
      unmute: 'Stummschaltung der Benachrichtigungstöne aufheben',
    },
    emptyErrors: 'Keine Fehler vorhanden',
    emptyInfoAlerts: 'Keine Info-Alarme vorhanden',
    viewActionInContext: 'Aktion im Kontext anzeigen',
    accordion: {
      impactedWorkflows: 'Betroffene Workflows',
      noImpactedWorkflows: 'Keine betroffenen Workflows',
      impactedDevices: 'Betroffene Geräte',
      noImpactedDevices: 'Keine betroffenen Geräte',
      additionalLogs: 'Zusätzliche Protokolle',
      noAdditionalLogs: 'Keine zusätzlichen Protokolle',
      impactedLabware: 'Betroffene Laborgeräte',
      noImpactedLabware: 'Keine betroffenen Laborgeräte',
    },
    actionCard: {
      due: 'Fällig',
    },
    actionGroup: {
      dueNow_one: '{{count}} Operator-Aktion jetzt fällig',
      dueNow_other: '{{count}} Operator-Aktionen jetzt fällig',
    },
    filter: {
      alertStatus: {
        open: 'Offen',
      },
      operatorActionType: {
        workflowStep: 'Workflow-Schritt',
        processStep: 'Prozessschritt',
      },
      scope: {
        thisWorkflow: 'Dieser Workflow',
        thisProcess: 'Dieser Prozess',
      },
    },
    sort: {
      tooltip: 'Sortieren',
      title: 'Sortieren nach',
      resolveBy: 'Beheben bis',
      detectedAt: 'Erkannt am',
      dueTime: 'Fälligkeitszeit',
      startBy: 'Starten bis',
    },
    error: {
      description: 'Etwas ist schiefgelaufen.',
      retry: 'Erneut versuchen',
      unavailable:
        'Benachrichtigungen nicht verfügbar. Zum erneuten Versuch klicken.',
      resolveActionFailed:
        'Aktion konnte nicht durchgeführt werden. Bitte versuchen Sie es erneut.',
      acknowledgeFailed:
        'Kenntnisnahme fehlgeschlagen. Bitte versuchen Sie es erneut.',
    },
    actions: {
      acknowledge: 'Zur Kenntnis nehmen',
      markSkipped: 'Als übersprungen markieren',
      markComplete: 'Als abgeschlossen markieren',
      helpDialog: {
        info: 'Wie sich diese Optionen auf meinen Workflow auswirken',
        skip: {
          description:
            'Den fehlgeschlagenen Task manuell abschließen und mit dem nächsten Task im Workflow fortfahren',
        },
        retry: {
          description:
            'Den fehlgeschlagenen Task erneut versuchen. Betroffene Laborgeräte müssen zurück an die Startposition des fehlerhaften Tasks bewegt werden.',
        },
        abortWorkflow: {
          description:
            'Alle laufenden Tasks dauerhaft beenden und die Ausführung aller verbleibenden Tasks verhindern. Ein Fortsetzen der Aktivität ab diesem Punkt ist nicht möglich.',
        },
      },
    },
    permissions: {
      actOperatorActions:
        'Sie haben keine Berechtigung, Operator-Aktionen durchzuführen. Bitten Sie Ihren Administrator, den Zugriff act:operator_actions zu gewähren',
      witnessSignatures:
        'Sie haben keine Berechtigung, Signaturen zu bezeugen. Bitten Sie Ihren Administrator, den Zugriff witness:signatures zu gewähren',
    },
    witness: {
      reviewPrompt:
        'Sie müssen die unten beschriebene Aktion prüfen und bezeugen.',
      received: 'Empfangen',
      signatureDetails: 'Signaturdetails der ausführenden Person',
      signed: 'Signiert',
      by: 'Von',
      approve: 'Aktion freigeben',
      reject: 'Aktion ablehnen',
      witnessBy: 'Bezeugt von',
      actionType: 'Aktionstyp',
      runStarted: 'Run gestartet',
      capturedData: 'Erfasste Daten',
      resumeFailed:
        'Die bezeugte Aktion konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
      awaitingWitnessSignature: 'Warten auf Zeugensignatur',
      awaitingWitnessBlockedHint:
        'Diese Aktion wartet auf eine Zeugensignatur und wird wirksam, sobald der Zeuge signiert. Bitte reichen Sie sie nicht erneut ein.',
      pending: {
        heading: 'Aktion wartet auf Zeugen',
        explanation:
          'Ihre Signatur wurde akzeptiert. Ein Zeuge muss diese Aktion freigeben, bevor sie wirksam wird.',
      },
      completed: {
        heading: 'Aktion bezeugt',
        witnessedBy: 'Bezeugt von {{name}}',
        witnessedByUnknown: 'Von einem anderen Benutzer bezeugt',
        approved: 'Freigegeben',
      },
      rejected: {
        heading: 'Aktion abgelehnt',
        rejectedBy: 'Abgelehnt von {{name}}',
        rejectedByUnknown: 'Von einem anderen Benutzer abgelehnt',
        reason: 'Ablehnungsgrund',
        rejected: 'Abgelehnt',
      },
    },
    severity: {
      information: 'Information',
    },
    type: {
      parameterThreshold: 'Parameter-Schwellenwert',
      deviceState: 'Gerätestatus',
      system: 'System',
      safety: 'Sicherheit',
      taskExecution: 'Task-Ausführung',
      inventoryConsumable: 'Inventar (Verbrauchsmaterial)',
      operatorAction: 'Operator-Aktion',
      witnessSignature: 'Zeugensignatur',
      scheduling: 'Zeitplanung',
      notification: 'Benachrichtigung',
    },
    navigation: {
      label: 'Ereignisnavigation',
    },
    dueAction: {
      combinedAction: {
        banner:
          'Dies ist Teil einer kombinierten Aktion mit {{workflows}}. Schließen Sie sie gemeinsam ab.',
      },
      actionStart: 'Aktion starten',
      actionDue: 'Aktion fällig',
      completeBy: 'Abschließen bis',
      durationOfTask: 'Task-Dauer',
      estimatedTime: 'Geschätzte Zeit',
      deviceGroup: 'Gerätegruppe',
      fileUpload: {
        chooseFile: 'Datei auswählen',
        noFileUploaded: 'Keine Datei hochgeladen',
        remove: 'Anhang entfernen',
        invalidFile: 'Die Datei muss eine CSV-Datei sein',
      },
      dataCapture: {
        min: 'min: {{min}}',
        max: 'max: {{max}}',
        rangeMin: 'Min: {{min}}',
        rangeMax: 'Max: {{max}}',
        validation: {
          minNumber: 'Muss mindestens {{min}} sein',
          maxNumber: 'Darf höchstens {{max}} sein',
        },
      },
      summary: {
        total: 'Laborgeräte gesamt',
        typeCount: '{{type}}: {{count}}',
      },
      table: {
        labwareIcon: 'Symbol für Laborgeräte-Typ',
        endLocation: 'Endposition',
        deviceName: 'Gerätename',
        currentStatus: 'Aktueller Status',
      },
    },
    taskAutoRetry: {
      title: 'Automatische Task-Wiederholung: {{taskId}}',
      body: 'Um {{timestamp}} wurde auf {{deviceName}} während des Tasks {{taskId}} ein Fehler erkannt. Der Task wird automatisch wiederholt.',
    },
    information: {
      attemptNo: 'Versuchsnummer',
    },
    safetyState: {
      emergencyStop: 'Not-Aus',
      alertDetectedAt: 'Alarm erkannt am',
      allDevicesStopped:
        'Alle sicherheitsgesteuerten Roboter und Geräte wurden gestoppt.',
      triggerPrefix: 'Auslöser:',
      triggerSuffix: 'wurde betätigt.',
      workflowsPaused:
        'Alle laufenden Workflows sind pausiert und warten nach dem Zurücksetzen des Sicherheitssystems auf eine Entscheidung des Operators.',
      recoveryTitle: 'Wiederherstellung nach dem Not-Aus:',
      recoveryStep1Title: 'Not-Aus physisch entriegeln',
      recoveryStep1Desc: 'Not-Aus drehen',
      recoveryStep2Title: 'Sicherheitssystem zurücksetzen',
      recoveryStep2Desc: 'Drücken Sie die Reset-Taste am Sicherheitsadapter.',
      recoveryFooter:
        'Sobald das Sicherheitssystem zurückgesetzt ist, können Sie die betroffenen Workflows fortsetzen oder abbrechen.',
      eStopCaption: 'Not-Aus gedrückt',
      fstopping: {
        heading: 'Funktionsstopp {{name}} wurde betätigt.',
        description:
          'Alle vom Sicherheitssystem gesteuerten Aktivitäten werden in {{timeUntilStop}} gestoppt.',
        connectedDevicesStopIn: 'Verbundene Geräte stoppen in',
      },
      fstopped: {
        triggerLabel: 'Funktionsstopp',
        safeToApproach:
          'Sie werden informiert, sobald die Annäherung an die Geräte sicher ist.',
        recoveryTitle: 'Wiederherstellung nach dem Funktionsstopp:',
        caption: 'Funktionsstopp gedrückt',
      },
      idle: {
        heading:
          'Sicherheitssystem zurückgesetzt, wählen Sie das weitere Vorgehen.',
        eStopReset:
          'Der Not-Aus {{name}} wurde um {{time}} physisch zurückgesetzt.',
        fStopReset:
          'Der Funktionsstopp {{name}} wurde um {{time}} physisch zurückgesetzt.',
        idleDescription:
          'Mit dem Sicherheitssystem verbundene Roboter und Geräte sind derzeit inaktiv. Wählen Sie, wie mit der betroffenen Aktivität fortgefahren werden soll.',
        resumeAll: 'Alle Workflows fortsetzen',
        abortAll: 'Alle Workflows abbrechen',
        eStopCaption: 'Not-Aus\nentriegelt',
        fStopCaption: 'Sicherheitssystem\nzurückgesetzt',
        resumeSuccess: 'Alle Workflows fortgesetzt',
        abortSuccess: 'Alle Workflows abgebrochen',
        autoResumeHeading:
          'Funktionsstopp zurückgesetzt — Workflows fortgesetzt',
        autoResumeReset:
          'Der Funktionsstopp {{name}} wurde um {{time}} zurückgesetzt, bevor ein Gerät gestoppt wurde.',
        autoResumeDescription:
          'Keine Aktion erforderlich. Alle laufenden Workflows werden automatisch fortgesetzt.',
        resolveError: 'Sicherheitsvorfall konnte nicht behoben werden',
      },
    },
    orchestratorAlert: {
      selectAction: 'Wählen Sie eine Aktion, um diesen Alarm zu beheben',
      gxpDescription: 'Alarmreaktion: {{action}}',
      actions: {
        abort: 'Abbrechen',
      },
      jobErrors: {
        couldNotStart:
          'Der Run für {{workflowName}} auf {{deviceGroupName}} konnte nicht gestartet werden. Versuchen Sie, ihn erneut zur Warteschlange hinzuzufügen — wenn es weiterhin fehlschlägt, wenden Sie sich an den Automata-Support.',
        workflowFallback: 'diesen Workflow',
        deviceGroupFallback: 'seiner Gerätegruppe',
      },
      labHours: {
        applied: 'Die Operator-Arbeitszeiten wurden aktualisiert.',
        appliedWithIssues_one:
          'Die Operator-Arbeitszeiten wurden aktualisiert, aber {{count}} Planungseinschränkung konnte unter den neuen Zeiten nicht erfüllt werden.',
        appliedWithIssues_other:
          'Die Operator-Arbeitszeiten wurden aktualisiert, aber {{count}} Planungseinschränkungen konnten unter den neuen Zeiten nicht erfüllt werden.',
      },
    },
    timeConstraint: {
      intro: {
        one: 'Ein Zeitlimit wurde überschritten, was die Integrität Ihres Workflows und den ursprünglichen Workflow-Plan beeinträchtigen kann.',
        multiple:
          'Einige Zeitlimits wurden überschritten, was die Integrität Ihres Workflows und den ursprünglichen Workflow-Plan beeinträchtigen kann.',
      },
      reason: {
        execution: {
          one: 'Die mit diesem Zeitlimit verbundenen Tasks wurden nicht innerhalb der erwarteten Dauer ausgeführt.',
          multiple:
            'Die mit jedem Zeitlimit verbundenen Tasks wurden nicht innerhalb der erwarteten Dauer ausgeführt.',
        },
        pause: {
          one: 'Der Workflow wurde zu lange pausiert, wodurch dieses Zeitlimit überschritten wurde.',
          multiple:
            'Der Workflow wurde zu lange pausiert, wodurch diese Zeitlimits überschritten wurden.',
        },
      },
      table: {
        from: 'Von',
        to: 'Bis',
        startOf: 'Beginn von {{task}}',
        endOf: 'Ende von {{task}}',
        exceededBy: 'Überschritten um',
      },
    },
  },
  plan: {
    visualisation: {
      labwareCount: '({{count}} Laborgeräte)',
      batchCount: '{{count}} Batches',
    },
    controlPanel: {
      search: {
        labware: 'Laborgeräte suchen...',
        instrument: 'Instrumente suchen...',
      },
      savedViews: {
        label: 'Gespeicherte Ansichten',
        saveCurrentSettings: 'Aktuelle Ansichtseinstellungen speichern',
        deleteView: 'Ansicht löschen',
        dialogTitle: 'Ansicht speichern',
        namePlaceholder: 'Ansichtsname',
        noSavedViews: 'Keine gespeicherten Ansichten',
      },
      view: {
        labware: 'Laborgeräte-Ansicht',
        instrument: 'Instrumentenansicht',
      },
      time: {
        relative: 'Relative Zeit',
      },
      toggle: {
        settings: 'Einstellungen umschalten',
        results: 'Ergebnisse umschalten',
        viewSettings: 'Ansichtseinstellungen umschalten',
      },
      viewSettings: {
        timeScale: 'Zeitskala der X-Achse',
        minute: 'Minute',
        hour: 'Stunde',
        detailLevel: 'Workflow-Detailgrad',
        showTasks: 'Tasks anzeigen',
        viewMode: 'Ansichtsmodus',
        otherViewTypes: 'Andere Ansichtstypen',
      },
    },
    timeline: {
      durationSeconds: '{{seconds}}s',
      collapseAll: 'Alle einklappen',
      ariaLabel: 'Workflow-Zeitleisten-Diagramm',
      showMarkers: 'Marker anzeigen',
      groupByBatch: 'Nach Batch gruppieren',
      resetZoom: 'Auf 100 % zurücksetzen',
      zoomIn: 'Hineinzoomen',
      zoomOut: 'Herauszoomen',
      setupInstructions: 'Setup-Anweisungen',
      cleanupInstructions: 'Cleanup-Anweisungen',
      estimatedTime: 'Geschätzte Zeit: {{time}}s',
      marker: {
        viewMore: 'Mehr anzeigen',
      },
    },
    filterPanel: {
      title: 'Filteroptionen',
      batches: {
        title: 'Batches',
        selectAll: 'Alle Batches auswählen',
        item: 'Batch {{number}}',
      },
      labware: {
        selectAll: 'Alle Laborgeräte auswählen',
      },
      instruments: {
        selectAll: 'Alle Instrumente auswählen',
      },
      clearAll: 'Alle Filter löschen',
    },
    tooltip: {
      type: {
        transport: 'Transport',
      },
      manualInteractionRequired: 'Manuelle Interaktion erforderlich',
      sharedAcrossWorkflows: 'In {{count}} Workflows gemeinsam genutzt',
      timeGap: 'Zeitlücke',
      deviation: 'Abweichung',
    },
    legend: {
      shared: 'Gemeinsam genutzt',
    },
    hovercard: {
      retries: 'Wiederholungen',
      blockedByReplanning: 'Task aufgrund von Neuplanung nicht gestartet',
      blockedByOverrun: 'Warten auf {{taskName}}',
      previousTaskOverrunning: '{{taskName}} überschreitet die geplante Dauer',
      overrunInProgress: 'Dieser Task hat seine geplante Dauer überschritten',
    },
    insights: {
      common: {
        minutes: '{{value}}m',
        percentMinutes: '{{percent}}% ({{minutes}} m)',
        gaps: 'Lücken',
        start: 'Start',
        end: 'Ende',
        batch: 'Batch',
      },
      labwareConsistency: {
        varianceLabel: 'Varianz [%]',
        minDurationLabel: 'Min. Dauer [m]',
        maxDurationLabel: 'Max. Dauer [m]',
        workflowVariations: 'Workflow-Variationen',
        sortByTime: 'Nach Zeit sortieren',
        varianceMinutes: 'Varianz',
        relativeStartEnd: 'Relativer Start & Ende',
        labwareAffected: 'Betroffene Laborgeräte',
      },
      instrumentDeadtime: {
        deadtimeMinutes: '{{minutes}} m',
        details: 'Details zur Totzeit',
        deadtimeLabel: 'Totzeit [m]',
      },
      labwareWaitTime: {
        details: 'Details zur Laborgeräte-Wartezeit',
        periodsLabel: 'Perioden [Lücken]',
        totalTimeLabel: 'Gesamtzeit [m]',
        waitTimeLabel: 'Wartezeit [m]',
        relativeStartEnd: 'Relativer Start & Ende [m]',
        sortLongest: 'längste',
        sortShortest: 'kürzeste',
      },
      deviceUtilisation: {
        individualTitle: 'Individuelle Geräteauslastung',
        groupByType: 'nach Typ gruppieren',
        showEachDevice: 'jedes Gerät',
        on: 'Ein',
      },
      instrumentCapacity: {
        peakCapacitySummary: '{{percent}}% [{{used}}/{{total}} Slots]',
        peakStorageCapacity: 'Maximale Lagerkapazität',
        instrumentDetails: 'Instrumentendetails',
      },
      timeConstraints: {
        atRiskLabel: 'Gefährdet [Zeitlimits]',
        totalDefinedLabel: 'Gesamt [definiert]',
        allTimeConstraints: 'Alle Zeitlimits',
        sortByLeeway: 'Nach Spielraum sortieren',
        sortHigh: 'hoch',
        sortLow: 'niedrig',
        relationName: 'Beziehungsname',
        timeConstraintDescription: 'Beschreibung des Zeitlimits',
        targetDuration: 'Zieldauer',
        executionPresetsDuration: 'Dauer der Ausführungsvoreinstellungen',
        leewayFromMax: 'Spielraum bis zur Höchstdauer',
      },
      dependencies: {
        total: 'Gesamt',
        allDependencies: 'Alle Abhängigkeiten',
        sortEarliest: 'früheste',
        sortLatest: 'späteste',
      },
      toolbar: {
        labwareConsistency: 'Laborgeräte-Konsistenz',
        deviceUtilisation: 'Geräteauslastung',
        instrumentCapacity: 'Instrumentenkapazität',
        instrumentDeadtime: 'Instrumenten-Totzeit',
        labwareWaitTime: 'Laborgeräte-Wartezeit',
        timeConstraints: 'Zeitlimits',
        dependencies: 'Abhängigkeiten',
      },
    },
  },
  planRequestErrors: {
    retractsMoreThanAvailable_one:
      'Dieser Workflow versucht, {{count}} Element mehr aus {{labware_name}} zu entfernen, als verfügbar ist. Reduzieren Sie die Batch-Anzahl oder überprüfen Sie Ihre Start-Laborgeräte und versuchen Sie es dann erneut.',
    retractsMoreThanAvailable_other:
      'Dieser Workflow versucht, {{count}} Elemente mehr aus {{labware_name}} zu entfernen, als verfügbar sind. Reduzieren Sie die Batch-Anzahl oder überprüfen Sie Ihre Start-Laborgeräte und versuchen Sie es dann erneut.',
    exceedsCapacity_one:
      'Dieser Workflow versucht, {{count}} Element mehr zu {{labware_name}} hinzuzufügen, als die Kapazität von {{capacity}} zulässt. Reduzieren Sie die Batch-Anzahl und versuchen Sie es dann erneut.',
    exceedsCapacity_other:
      'Dieser Workflow versucht, {{count}} Elemente mehr zu {{labware_name}} hinzuzufügen, als die Kapazität von {{capacity}} zulässt. Reduzieren Sie die Batch-Anzahl und versuchen Sie es dann erneut.',
    capacityViolationsHeader: 'Dieser Workflow kann nicht platziert werden:',
    violationRetractLine: '{{over_by}} zu viele aus {{labware_name}} entfernen',
    violationCapacityLine:
      '{{over_by}} zu viele zu {{labware_name}} hinzufügen (Kapazität {{capacity}})',
    capacityViolationsGuidance:
      'Reduzieren Sie die Batch-Anzahl oder überprüfen Sie Ihre Start-Laborgeräte und versuchen Sie es dann erneut.',
    planningTimedOut:
      'Ihre Position in der Warteschlange konnte nicht rechtzeitig berechnet werden. Bitte versuchen Sie es erneut.',
    translationFailed:
      'Dieser Workflow konnte nicht für die Ausführung vorbereitet werden. Wenn dies weiterhin auftritt, überprüfen Sie das Workflow-Setup.',
    workflowValidationFailed:
      'Dieser Workflow konnte aufgrund eines Problems mit seinem Setup nicht zur Warteschlange hinzugefügt werden. Öffnen Sie ihn zur Überprüfung im Workflow-Editor und fügen Sie ihn anschließend erneut zur Warteschlange hinzu.',
    manualTransportHeader:
      'Dieser Workflow enthält Laborgeräte-Bewegungen, die als manuelle (Operator-)Schritte eingerichtet werden müssen, bevor er zur Warteschlange hinzugefügt werden kann.',
    manualTransportStepOpen: 'Diesen Workflow im Editor öffnen',
    manualTransportStepSetNamed:
      'Diese Laborgeräte-Bewegungen als manuelle (Operator-)Transporte festlegen: {{labware}}',
    manualTransportStepSet:
      'Die betroffenen Laborgeräte-Bewegungen als manuelle (Operator-)Transporte festlegen',
    manualTransportStepReAdd:
      'Speichern und anschließend erneut zur Warteschlange hinzufügen',
  },
  processCanvas: {
    debugDialog: {
      triggerTooltip: 'Prozessdaten debuggen',
      title: 'Prozess-Debugdaten',
    },
    loadError: 'Prozess konnte nicht geladen werden',
    save: {
      failure: 'Aktuelle Änderungen konnten nicht gespeichert werden',
    },
    versioning: {
      restoreDialog: {
        title: 'Revision {{number}} zum Bearbeiten wiederherstellen?',
        description:
          'Beim Wiederherstellen wird eine neue Version erstellt, die bearbeitet werden kann. Ihr aktueller Entwurf bleibt im Verlauf erhalten.',
        confirm: 'Wiederherstellen',
      },
      restorePresetVersionDialog: {
        title: 'Voreinstellungsversion {{version}} wiederherstellen?',
        description:
          'Beim Wiederherstellen wird eine neue Version aus der ausgewählten Version erstellt. Frühere Versionen bleiben im Verlauf erhalten; nicht simulierte Änderungen an der aktuellen Konfiguration werden ersetzt.',
      },
      history: {
        notSaved:
          'Automatisierung noch nicht gespeichert. Speichern Sie den Prozess, um die erste Revision zu erstellen.',
      },
    },
    panelHeader: {
      executionPresets: 'Ausführungsvoreinstellungen',
    },
    steps: {
      code: 'Code ausführen',
    },
    permissions: {
      createSimulationProduction:
        'Sie haben keine Berechtigung, zur Warteschlange hinzuzufügen. Bitten Sie Ihren Administrator, Ihnen den Zugriff create:simulation:production zu gewähren.',
    },
    addToQueue: {
      title: 'Zur Warteschlange hinzufügen',
      disabledTooltip: 'Zuerst eine Simulation ausführen',
      confirm: 'Weiter',
      confirming: 'Wird bestätigt...',
      errors: {
        save: 'Prozess konnte nicht gespeichert werden',
        addToPlan: 'Prozess konnte nicht zum Plan hinzugefügt werden',
        calculate: 'Position konnte nicht berechnet werden',
        fetchFailed: 'Fehler beim Abrufen des Warteschlangenstatus',
        confirm: 'Bestätigung fehlgeschlagen',
        planFailed: 'Planung fehlgeschlagen',
      },
    },
  },
  processSimulation: {
    createExecutionProfile: 'Ausführungsprofil erstellen',
    backToProcess: 'Zurück zum Prozess',
  },
  richTextEditor: {
    link: {
      enterUrl: 'URL eingeben',
    },
    file: {
      remove: 'Datei entfernen',
      untitled: 'Unbenannte Datei',
    },
    toolbar: {
      horizontalRule: 'Horizontale Linie',
      addLink: 'Link hinzufügen',
      codeBlock: 'Codeblock',
      inlineCode: 'Inline-Code',
      blockquote: 'Blockzitat',
      heading: 'Überschrift',
      taskList: 'Aufgabenliste',
      underline: 'Unterstrichen',
      strikethrough: 'Durchgestrichen',
      orderedList: 'Nummerierte Liste',
      bulletList: 'Aufzählungsliste',
      italic: 'Kursiv',
      bold: 'Fett',
    },
  },
  robotTraining: {
    select: 'Auswählen...',
    begin: 'Mit einem Roboter verbinden, um mit dem Einlernen zu beginnen',
    start: 'Einlernen starten',
    inactivityTimeout: 'Vom Roboter getrennt — seit 10 Minuten keine Aktivität',
    needToConnect:
      'Instrumente in <studioLink>Studio</studioLink> mit diesem Roboter verbinden, um mit dem Einlernen zu beginnen',
    robotDetails: {
      title: 'Details zum Roboter-Einlernen',
      label: 'Roboter auswählen',
      connect: 'Verbinden',
      disconnect: 'Trennen',
      homeRobot: 'Greifer in Grundstellung',
      openGripper: 'Greifer öffnen',
      closeGripper: 'Greifer schließen',
      jogAngle: 'Jog-Winkel [°]',
      jogIncrement: 'Jog-Schrittweite [°]',
      status: {
        lockedByRuntime:
          'Das Gerät wird in einem Workflow verwendet. Zum Einlernen warten, bis der Workflow abgeschlossen ist.',
        lockedByStudio:
          'Das Gerät ist in Studio verbunden. Zum Einlernen die Verbindung zu Studio trennen.',
      },
      connectionSettingsRequired:
        'Bitte IP-Adresse und Port konfigurieren, um eine Verbindung herzustellen',
      noConnectionSettings:
        'Für dieses Gerät sind keine Verbindungseinstellungen verfügbar.',
      noModuleError: 'Kein Modul für den Roboter gefunden',
      homePosition: 'Grundstellung [mm]',
      homePositionDescription:
        'Die aktuellen Live-Koordinaten auf die Grundstellung anwenden.',
      cartesianControls: 'Kartesisch',
      jointControls: 'Gelenke',
      decreaseJoint: 'J{{joint}}-Winkel verringern',
      decreaseJointTooltip: 'J{{joint}} -',
      increaseJoint: 'J{{joint}}-Winkel vergrößern',
      increaseJointTooltip: 'J{{joint}} +',
      axisLabels: {
        xy: 'XY',
        z: 'Z',
        rail: 'RAIL',
        rotation: 'ROT',
      },
      moveYForward: 'Y vorwärts bewegen',
      moveYBackward: 'Y rückwärts bewegen',
      moveXLeft: 'X nach links bewegen',
      moveXRight: 'X nach rechts bewegen',
      moveZUp: 'Z nach oben bewegen',
      moveZDown: 'Z nach unten bewegen',
      moveRailBackward: 'Rail rückwärts bewegen',
      moveRailForward: 'Rail vorwärts bewegen',
      rotateCounterClockwise: 'Gegen den Uhrzeigersinn drehen',
      rotateClockwise: 'Im Uhrzeigersinn drehen',
      moveYForwardTooltip: 'Y +',
      moveYBackwardTooltip: 'Y -',
      moveXLeftTooltip: 'X -',
      moveXRightTooltip: 'X +',
      moveZUpTooltip: 'Z +',
      moveZDownTooltip: 'Z -',
      moveRailBackwardTooltip: 'Rail -',
      moveRailForwardTooltip: 'Rail +',
      rotateCounterClockwiseTooltip: 'Rotate +',
      rotateClockwiseTooltip: 'Rotate -',
      freeMode: 'Manueller Modus',
      jogMode: 'Jog',
    },
    coordinates: {
      title: 'Live-Koordinaten',
      cartesian: 'Kartesische Koordinaten [mm]',
      joints: 'Gelenkkoordinaten [°]',
      joint: 'J{{index}}',
      noJointData: 'Keine Gelenkdaten verfügbar',
      x: 'x',
      y: 'y',
      z: 'z',
      rotation: 'rot',
      rail: 'rail',
      lockRail: 'Rail sperren',
      unlockRail: 'Rail entsperren',
      railLockDisabledTooltip:
        'Der Roboter muss sich im manuellen Modus befinden, um die Rail zu sperren',
    },
    pathTrainer: {
      orientations: {
        LANDSCAPE: 'Querformat',
        REVERSE_LANDSCAPE: 'Umgekehrtes Querformat',
        PORTRAIT: 'Hochformat',
        REVERSE_PORTRAIT: 'Umgekehrtes Hochformat',
      },
      selectedApproachPath: 'Ausgewählter Anfahrpfad',
      testTransport: 'Transport testen',
      testTransportDialog: {
        pickOnly: 'Nur aufnehmen',
        placeOnly: 'Nur ablegen',
        selectAction: 'Aktion auswählen',
        error: 'Transporttest fehlgeschlagen. Bitte erneut versuchen.',
        motionProfileRunOnly:
          'Gilt nur für diesen Testlauf – wird nicht im Pfad gespeichert.',
      },
      slotStatus: {
        incomplete: 'Unvollständig',
        gxpDescription: 'Slot-Status auf {{status}} ändern',
        updateStatusError: 'Aktualisieren des Slot-Status fehlgeschlagen',
        removeApproachPath: 'Anfahrpfad entfernen',
        removePathDialog: {
          title: 'Pfad entfernen?',
          description:
            'Dadurch wird der {{orientation}}-Pfad dauerhaft aus diesem Slot entfernt. Diese Aktion kann nicht rückgängig gemacht werden.',
        },
      },
      slot: '{{name}}',
      selectOrientation: 'Bitte Ausrichtung auswählen',
      goToWaypoint: 'Roboter zum Waypoint bewegen',
      goToWaypointError: 'Anfahren des Waypoints fehlgeschlagen',
      deleteWaypoint: 'Waypoint entfernen',
      reorderWaypoint: 'Waypoint neu anordnen',
      replaceWaypoint: 'Koordinaten ersetzen',
      addNewWaypoint: 'Waypoint hinzufügen',
      waypointTable: 'Pfade',
      addOrientation: 'Ausrichtung hinzufügen',
      slotsLabel: 'Slots ({{slotNumber}})',
      approachPathLabel: 'Anfahrpfad ({{pathCount}})',
      approachHeight: 'Delta',
      editWaypoint: 'Koordinaten bearbeiten',
      editWaypointError: 'Aktualisieren des Waypoints fehlgeschlagen',
      waypointActions: 'Waypoint-Aktionen',
      deleteWaypointError: 'Löschen des Waypoints fehlgeschlagen',
      replaceWaypointError: 'Ersetzen des Waypoints fehlgeschlagen',
      reorderWaypointError: 'Neuanordnung des Waypoints fehlgeschlagen',
      updateApproachHeightError: 'Aktualisieren der Anfahrhöhe fehlgeschlagen',
      updateMotionProfileError:
        'Aktualisieren des Bewegungsprofils fehlgeschlagen',
      motionProfile: {
        info: 'Legt fest, wie sich der Roboter entlang dieses Pfads bewegt.',
        default: 'Standard',
        safetyNote:
          'Die ausgeführte Bewegung ist die sicherste Option aus der Roboterkonfiguration, den Einstellungen für Laborgeräte und den Einstellungen dieses Pfads.',
        pathType: 'Pfadtyp',
        straight: 'Gerade',
        notStraight: 'Nicht gerade',
        transition: 'Übergang',
        blended: 'Überblendet',
        notBlended: 'Nicht überblendet',
      },
      emptyState: {
        gettingStartedTitle: 'Erste Schritte',
        gettingStartedBody:
          'Den Roboter mit den Laborgeräten auf dem Slot positionieren und den ersten Waypoint hinzufügen. Anschließend den Roboter vom Slot wegbewegen und weitere Waypoints hinzufügen, um den Anfahrpfad zu definieren.',
      },
    },
    instrumentList: {
      reachableDevices: 'Erreichbare Geräte',
    },
    errors: {
      jogFailed: 'Jog fehlgeschlagen. Bitte erneut versuchen.',
      waypointUnreachable:
        'Der Roboter kann diese Position nicht erreichen. Prüfen, ob die Koordinaten im zulässigen Bereich liegen.',
      testMoveFailed:
        'Der Roboter konnte die Bewegung nicht abschließen. Bitte erneut versuchen.',
    },
  },
  rolesMultiSelect: {
    placeholder: 'Rollen auswählen',
    loadError: 'Rollen konnten nicht geladen werden',
  },
  secretPicker: {
    selectSecret: 'Secret-Wert verwenden',
    searchPlaceholder: 'Secrets suchen',
    newSecret: 'Neues Secret',
    noResults: 'Keine Secrets gefunden',
    clearSecret: 'Secret-Referenz entfernen',
    secretKeyTooltip: 'Der Wert wird aus einem Secret aufgelöst',
  },
  settings: {
    pagination: {
      previous: 'Zurück',
      next: 'Weiter',
      pageOf: 'Seite {{current}} von {{total}}',
    },
    auditExports: {
      title: 'Datenexport-Einstellungen',
      description:
        'Legen Sie fest, wohin Audit-Daten für die Langzeitarchivierung und Compliance exportiert werden.',
      loadFailure: 'Exporteinstellungen konnten nicht geladen werden',
      destination: {
        title: 'Ziel',
        emptyTitle: 'Kein Exportziel',
        emptyDescription:
          'Fügen Sie ein Ziel hinzu, um Audit-Daten automatisch in Ihr externes System zu exportieren.',
        add: 'Ziel hinzufügen',
        providerSharepoint: 'SharePoint',
        driveId: 'Drive-ID',
        statusInactive: 'Inaktiv',
        unnamed: 'Unbenanntes Ziel',
      },
      schedule: {
        title: 'Exportzeitplan',
        frequency: 'Häufigkeit',
        dayOfWeek: 'Tag',
        time: 'Uhrzeit',
        timezone: 'Zeitzone',
        nextRun: 'Nächster geplanter Export',
        frequencyHourly: 'Stündlich',
        frequencyWeekly: 'Wöchentlich',
        empty:
          'Kein Zeitplan festgelegt. Es werden keine Exporte übermittelt, bis ein Zeitplan konfiguriert ist.',
        editAria: 'Zeitplan bearbeiten',
        editTitle: 'Exportzeitplan bearbeiten',
        editDescription:
          'Wählen Sie, wie oft Audit-Daten an das Ziel übermittelt werden.',
        dayPlaceholder: 'Tag auswählen',
        clear: 'Zeitplan löschen',
        saveFailure: 'Zeitplan konnte nicht gespeichert werden',
      },
      dialog: {
        createTitle: 'Exportziel hinzufügen',
        editTitle: 'Exportziel bearbeiten',
        description:
          'Audit-Daten werden an diesen SharePoint-Speicherort exportiert.',
        namePlaceholder: 'Labor-SharePoint',
        siteUrl: 'Site-URL',
        siteUrlPlaceholder: 'https://contoso.sharepoint.com/sites/lab',
        tenantId: 'Tenant-ID',
        clientId: 'Client-ID',
        clientSecret: 'Client Secret',
        destinationPath: 'Zielpfad',
        destinationPathPlaceholder: '/Audit-Exporte',
        enabled: 'Export aktiv',
        secretEditHint:
          'Geben Sie das Client Secret erneut ein, um Änderungen zu testen und zu speichern',
        test: 'Verbindung testen',
        testing: 'Verbindung wird getestet…',
        testSuccess: 'Verbindung erfolgreich',
        testSuccessWithDrive: 'Verbindung erfolgreich — Drive {{driveId}}',
        testRequired: 'Testen Sie die Verbindung vor dem Speichern',
        saveFailure: 'Ziel konnte nicht gespeichert werden',
      },
      testReason: {
        authFailed:
          'Authentifizierung fehlgeschlagen. Überprüfen Sie Tenant-ID, Client-ID und Secret.',
        siteNotFound:
          'SharePoint-Site nicht gefunden. Überprüfen Sie die Site-URL.',
        forbidden:
          'Zugriff verweigert. Der App-Registrierung fehlt die Berechtigung für diese Site.',
        destinationNotWritable:
          'Der Zielpfad ist nicht beschreibbar. Überprüfen Sie den Pfad und die Berechtigungen.',
        networkError:
          'SharePoint konnte nicht erreicht werden. Überprüfen Sie die Verbindung und versuchen Sie es erneut.',
        unknown:
          'Verbindungstest fehlgeschlagen. Überprüfen Sie die Angaben und versuchen Sie es erneut.',
      },
    },
    users: {
      table: {
        userName: 'Benutzername',
        roleTypes: 'Rollentypen',
        lastLogin: 'Letzte Anmeldung',
      },
      status: {
        active: 'Aktiv',
        deactivated: 'Deaktiviert',
        invited: 'Eingeladen',
      },
      roleType: {
        system: 'System',
        custom: 'Benutzerdefiniert',
      },
      actions: {
        deactivate: 'Deaktivieren',
        activate: 'Aktivieren',
        resetPin: 'PIN zurücksetzen',
        unlockPin: 'PIN entsperren',
        readOnly: '{{userName}} kann nicht geändert werden',
      },
      pagination: {
        showingResults:
          '{{start}}-{{end}} von {{total}} Benutzern werden angezeigt',
      },
      emptyState: {
        title: 'Noch keine Benutzer',
        description:
          'Benutzer werden hier angezeigt, sobald sie zum System eingeladen wurden.',
        noResults: 'Keine Benutzer gefunden',
        noResultsDescription:
          'Keine Benutzer entsprechen den aktuellen Filtern. Passen Sie Ihre Suche oder Filter an.',
      },
      loading: 'Benutzer werden geladen...',
      error: 'Benutzer konnten nicht geladen werden.',
      aria: {
        moreActions: 'Weitere Aktionen für {{userName}}',
      },
      filters: {
        byRole: 'Nach Rolle',
        byRoleType: 'Nach Rollentyp',
        searchPlaceholder: 'Benutzer suchen',
      },
      neverLoggedIn: 'Nie',
      editDialog: {
        title: 'Benutzer bearbeiten',
        description: 'Benutzerinformationen aktualisieren.',
        fullName: 'Vollständiger Name',
        emailAddress: 'E-Mail-Adresse',
        roles: 'Rolle',
        rolesPlaceholder: 'Rolle auswählen...',
        adminNotes: 'Admin-Notizen',
        adminNotesPlaceholder:
          'Notizen zum Zugriff dieses Benutzers oder zu relevanten Informationen hinzufügen.',
        adminNotesDescription:
          'Optionale Notizen zur administrativen Nachverfolgung und Benutzerverwaltung.',
        updateFailure:
          'Benutzer konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        roleUpdateFailure:
          'Benutzerdetails wurden gespeichert, aber die Rollenänderung ist fehlgeschlagen. Bitte versuchen Sie es erneut.',
        userNotFound:
          'Benutzer konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
      },
      createDialog: {
        title: 'Benutzer hinzufügen',
        description: 'Neuen Benutzer zum System hinzufügen und Rolle zuweisen.',
        sourcePlaceholder: 'Quelle auswählen...',
        failure:
          'Benutzer konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
      },
      deactivateDialog: {
        title: 'Benutzer deaktivieren',
        description:
          'Dadurch werden alle Zugriffsberechtigungen für den folgenden Benutzer entfernt:',
      },
      resetPinDialog: {
        description:
          'Dadurch wird die PIN für den folgenden Benutzer zurückgesetzt. Der Benutzer muss eine neue PIN festlegen, bevor er eine elektronische Signatur leisten kann.',
        reasonLabel: 'Grund',
        reasonPlaceholder: 'Warum setzen Sie die PIN dieses Benutzers zurück?',
        reasonRequired:
          'Bitte geben Sie einen Grund für das Zurücksetzen der PIN dieses Benutzers an.',
        failure:
          'PIN konnte nicht zurückgesetzt werden. Bitte versuchen Sie es erneut.',
      },
      unlockPinDialog: {
        description:
          'Dadurch wird die PIN für den folgenden Benutzer entsperrt, sodass er wieder eine elektronische Signatur leisten kann.',
        failure:
          'PIN konnte nicht entsperrt werden. Bitte versuchen Sie es erneut.',
      },
    },
    navigation: 'Einstellungsnavigation',
    tabs: {
      users: 'Benutzer',
      roles: 'Rollen',
    },
    compliance: {
      title: 'Compliance',
      table: {
        signaturePolicyCategory: 'Kategorie der Signaturrichtlinie',
        requirement: 'Anforderung',
        lastUpdated: 'Zuletzt aktualisiert',
      },
      requirement: {
        single: 'Einzelne Signatur',
        dual: 'Doppelsignatur',
      },
      loading: 'Signaturrichtlinien werden geladen...',
      error: 'Signaturrichtlinien konnten nicht geladen werden.',
      emptyState: {
        title: 'Keine Signaturrichtlinien gefunden',
        description:
          'Signaturrichtlinien werden hier angezeigt, sobald sie konfiguriert sind.',
      },
      aria: {
        moreActions: 'Weitere Aktionen für {{policyName}}',
      },
      editDialog: {
        title: 'Signaturrichtlinie bearbeiten',
        description: 'Signaturanforderung für {{policyName}} aktualisieren.',
        saving: 'Wird gespeichert...',
        updateFailure:
          'Signaturrichtlinie konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        gxpDescription:
          'Signaturrichtlinie aktualisieren: {{policyName}} von {{oldRequirement}} zu {{newRequirement}}',
        gxpDescriptionRoles:
          'Zeugenrollen für Signaturrichtlinie aktualisieren: {{policyName}}',
        witnessRolesLabel: 'Zeugenrollen',
        witnessRolesHint:
          'Leer lassen, damit jeder autorisierte Zeuge signieren kann.',
      },
    },
    roles: {
      title: 'Rollenverwaltung',
      systemRoles: 'Systemrollen',
      systemRole: 'Systemrolle',
      permissionScopes: 'Berechtigungsbereiche: {{count}}',
      loading: 'Rollen werden geladen...',
      error: 'Rollen konnten nicht geladen werden.',
      emptyState: {
        title: 'Keine Rollen gefunden',
        description: 'Systemrollen werden hier angezeigt.',
      },
    },
    secrets: {
      loading: 'Secrets werden geladen...',
      error: 'Secrets konnten nicht geladen werden.',
      emptyState: {
        title: 'Noch keine Secrets',
        description:
          'Mit Secrets können Sie private Werte wie Passwörter, Tokens und API-Schlüssel speichern und sicher über Workflows, Prozesse und Geräteeinstellungen hinweg wiederverwenden.',
        addFirst: 'Erstes Secret hinzufügen',
      },
      table: {
        secretName: 'Secret-Name',
        lastEdited: 'Zuletzt bearbeitet',
      },
      createDialog: {
        title: 'Secret hinzufügen',
        description:
          'Neues Secret hinzufügen, um einen privaten Wert sicher zu speichern.',
        secretNamePlaceholder: 'z. B. API_KEY',
        failure:
          'Secret konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
        gxpDescription: 'Secret erstellen: {{secretName}}',
      },
      editDialog: {
        title: 'Secret bearbeiten',
        description: 'Werte für {{secretName}} aktualisieren.',
        newValueHint: 'Geben Sie zum Aktualisieren einen neuen Wert ein',
        submit: 'Änderungen speichern',
        failure:
          'Secret konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        gxpDescription: 'Secret aktualisieren: {{secretName}}',
      },
      deleteDialog: {
        title: 'Secret löschen',
        description:
          'Möchten Sie das Secret „{{secretName}}“ wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
        failure:
          'Secret konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
        gxpDescription: 'Secret löschen: {{secretName}}',
      },
      access: {
        title: 'Secret-Zugriff',
        tooltipLabel: 'Informationen zum Secret-Zugriff',
        tooltipBefore:
          'Der Zugriff auf Secrets hängt von den Benutzerrollen ab. Rollen werden verwaltet unter',
        tooltipBold: 'Settings → Benutzerverwaltung',
        tooltipAfter: 'und gelten für alle Secrets.',
        viewOnly: 'Nur Ansicht:',
        edit: 'Bearbeiten:',
      },
      aria: {
        moreActions: 'Weitere Aktionen für {{secretName}}',
        toggleVisibility: 'Sichtbarkeit des Werts umschalten',
      },
    },
  },
  sidebar: {
    toggle: 'Seitenleiste umschalten',
    mobileDescription: 'Zeigt die mobile Seitenleiste an.',
    title: 'Seitenleiste',
  },
  simulation: {
    completed: 'Simuliert',
    inProgress: 'Wird simuliert',
    createExecutionPresetUnsavedTooltip:
      'Änderungen speichern, um eine Voreinstellung zu erstellen',
    multiPlan: {
      picker: 'Multi-Plan-Strategieauswahl',
      title: 'OR-Tools Multi-Plan-Strategiematrix',
      description:
        'Jede aktivierte Zelle erzeugt eine Planer-Variante. Der erste machbare Plan gewinnt; verbleibende Varianten werden abgebrochen.',
      rowHeader: 'OR-Tools ↓ \\ Hinweis →',
      variantsSelected_one: '{{count}} Variante ausgewählt',
      variantsSelected_other: '{{count}} Varianten ausgewählt',
      run: 'Multi-Plan ausführen',
      selectorPlaceholder: 'Variante auswählen…',
      variantNoHint: 'Kein Hinweis',
      variantFailed: 'fehlgeschlagen',
      variantIssueTitle:
        'Problem: keine machbare Lösung in {{seconds}} s gefunden',
      variantIssueTitleGeneric: 'Problem: Variante abgebrochen',
      variantNoDetails: 'Keine weiteren Details vom Planer gemeldet.',
    },
    card: {
      moreActions: 'Weitere Aktionen',
      favourite: 'Favorit',
      archive: 'Archivieren',
      rename: 'Umbenennen',
      renamePrompt: 'Neuer Name für Simulation',
      renameTitle: 'Simulation umbenennen',
      currentSuffix: '(aktuell)',
      historicSuffix: '(historisch)',
    },
    gxpDescription: {
      archive: 'Ausführungsvoreinstellung archivieren: {{name}}',
    },
    timelineEmptyState:
      'Klicken Sie auf eine Simulationskarte, um hier Details und Erkenntnisse anzuzeigen.',
    executionError: {
      infeasible: {
        title: 'Kein durchführbarer Zeitplan',
        body: 'Dieser Workflow kann mit seinen aktuellen Zeitlimits und Instrumenten nicht geplant werden. Lockern Sie ein maximales Zeitlimit, geben Sie ein Instrument frei oder fügen Sie eines hinzu, oder reduzieren Sie die Batch-Anzahl.',
      },
      timeout: {
        title: 'Zeitplan nicht rechtzeitig gefunden',
        body: 'Die Zeit für die Planung ist abgelaufen, bevor ein durchführbarer Zeitplan gefunden wurde. Reduzieren Sie die Batch-Anzahl oder vereinfachen Sie den Workflow.',
      },
      invalidInput: {
        title: 'Dieser Workflow kann nicht geplant werden',
        body: 'Der Workflow ist für die Planung ungültig. Überprüfen Sie die Workflow-Definition und versuchen Sie es erneut.',
      },
      validation: {
        title: 'Workflow-Validierung fehlgeschlagen',
        body: 'Beheben Sie die im Workflow markierten Validierungsprobleme, bevor Sie simulieren.',
      },
      inProgress: {
        title: 'Simulation bereits in Bearbeitung',
        body: 'Dieser Workflow wird bereits simuliert. Möglicherweise wurde die Simulation an anderer Stelle gestartet. Warten Sie, bis sie abgeschlossen ist, und versuchen Sie es dann erneut.',
      },
      generic: {
        title: 'Zeitplan konnte nicht erstellt werden',
        body: 'Bei der Planung ist ein Fehler aufgetreten. Versuchen Sie erneut zu simulieren.',
      },
      serviceUnavailable: {
        title: 'Workflow konnte nicht vorbereitet werden',
        body: 'Ein erforderlicher Dienst hat nicht rechtzeitig geantwortet. Dies ist in der Regel vorübergehend – versuchen Sie erneut zu simulieren.',
      },
      labwareDoesNotFit: {
        title: 'Laborgeräte passen nicht',
        description:
          'Die Höhe der Laborgeräte überschreitet die Höhe der Slots, die sie verwenden würden. Wählen Sie Laborgeräte mit geringerer Höhe. Oder eine Position mit einem größeren Slot. Simulieren Sie anschließend erneut.',
        line: 'Benötigt {{needed}} mm; höchster verfügbarer Slot auf {{instrument}} ist {{slotMax}} mm',
        lineNoPosition:
          'Benötigt {{needed}} mm; keine verfügbare Position hat einen ausreichend hohen Slot',
        locate: '{{labware}} im Canvas finden',
      },
      tooManyBatches: {
        title: 'Zu viele Batches',
        body: 'Sie haben {{requested}} Batches angefordert, aber dieser Workflow unterstützt maximal {{maximum}}. Reduzieren Sie die Batch-Anzahl und simulieren Sie erneut.',
      },
      overlappingSlots: {
        title: 'Überlappende Slots',
        description:
          'Die Slot-Konfiguration eines Geräts definiert überlappende Slot-Bereiche, sodass das System nicht ermitteln kann, wo Laborgeräte platziert werden sollen. Korrigieren Sie die Slot-Konfiguration des Geräts in Studio und simulieren Sie anschließend erneut.',
        lineColumns:
          'Spalte {{columnA}} (Slots {{rangeA}}) und Spalte {{columnB}} (Slots {{rangeB}}) überlappen sich',
        line: 'Slots {{rangeA}} und Slots {{rangeB}} überlappen sich',
      },
      binSlotIsOutputOnly: {
        title: 'Ein Task versucht, Laborgeräte aus einem Bin zu entnehmen',
        description:
          'Bin-Slots sind reine Ausgabe-Slots – sobald Laborgeräte in einem Bin landen, können sie nicht erneut verwendet werden. Die unten aufgeführten Tasks würden Laborgeräte wiederverwenden, die in einem Bin landen. Verschieben Sie diese Laborgeräte in einen anderen Slot, oder konfigurieren Sie den Slot in Studio neu, und simulieren Sie anschließend erneut.',
        line: '{{task}} entnimmt {{labware}} aus einem Bin auf {{instrument}} (Slot {{slot}})',
        lineTransitive:
          '{{consumerTask}} verbraucht {{labware}}, nachdem {{producerTask}} sie in einem Bin auf {{instrument}} ablegt',
        anInstrument: 'einem Instrument',
        anUnnamedTask: 'ein unbenannter Task',
      },
      slotConflict: {
        title: 'Kollidierende Slot-Zuweisungen',
        description:
          'Mehrere Laborgeräte konkurrieren gleichzeitig um denselben Slot, sodass der Run nicht geplant werden kann. Überprüfen Sie die Slot-Zuweisungen und simulieren Sie erneut.',
        line: '{{labwareA}} und {{labwareB}} benötigen beide Slot {{slot}} auf {{instrument}} (während {{task}})',
      },
      timeConstraint: {
        title: 'Zeitlimit kann nicht eingehalten werden',
        description:
          'Ein Zeitlimit zwischen zwei Tasks kann in keinem gültigen Zeitplan eingehalten werden. Wählen Sie die Verbindung zwischen den beiden Tasks aus, um ihre Grenzwerte anzupassen, und simulieren Sie anschließend erneut.',
        tasks: '{{startTask}} → {{endTask}}',
        lineMax:
          'Abstand kann nicht innerhalb des Maximums von {{seconds}} s gehalten werden',
        lineMin: 'Mindestabstand von {{seconds}} s kann nicht erreicht werden',
        line: 'Das Zeitlimit zwischen diesen Tasks kann nicht eingehalten werden',
      },
      maxDeadTime: {
        title: 'Laborgeräte würden zu lange warten',
        description:
          'Einige Laborgeräte würden zwischen zwei Schritten länger als die maximal zulässige Wartezeit ungenutzt bleiben, sodass der Run nicht geplant werden kann. Erhöhen Sie die maximale Wartezeit für Laborgeräte in den Simulationseinstellungen, oder überarbeiten Sie die Schritte, und simulieren Sie anschließend erneut.',
        issueHeader: 'Problem',
        line: 'Würde länger als {{seconds}} s zwischen {{prevTask}} und {{nextTask}} warten',
      },
      resourceLock: {
        title: 'Ein Instrument ist doppelt belegt',
        description:
          'Ein Instrument kann jeweils nur einen Task bearbeiten. Einige Schritte halten ein Instrument über ein Zeitfenster hinweg belegt, in dem auch andere Schritte es benötigen, sodass der Run nicht geplant werden kann. Verschieben Sie einen der kollidierenden Schritte vor oder hinter dieses Zeitfenster, und simulieren Sie anschließend erneut.',
        line: 'Benötigt {{instrument}}, das von {{startTask}} bis {{endTask}} in Benutzung ist',
      },
      finalDestination: {
        title: 'Laborgeräte blockieren ein Instrument, auf dem sie enden',
        description:
          'Einige Laborgeräte beenden den Run auf einem Instrument, wodurch dieses Instrument belegt bleibt und andere Schritte, die es benötigen, nicht geplant werden können. Weisen Sie den Laborgeräten eine andere Endposition zu, oder geben Sie das Instrument frei, und simulieren Sie anschließend erneut.',
        line: 'Endet auf {{instrument}} (Slot {{slot}}) nach {{finalTask}} und hält es für andere Schritte belegt',
      },
      slotAboveCapacity: {
        title: 'Nicht genügend Slots auf dem Gerät',
        description:
          'Ein Gerät hat nicht genügend Slots verfügbar. Erhöhen Sie die Kapazität des Geräts in Studio, oder verschieben Sie einen Teil der Arbeit auf ein anderes Gerät, und simulieren Sie anschließend erneut.',
        line: 'Verwendet Slot {{slot}}; {{device}} hat nur eine Kapazität von {{capacity}}',
        aDevice: 'das Gerät',
      },
      insufficientCapacity: {
        title: 'Nicht genügend Slots für diesen Workflow',
        description:
          'Dieser Workflow benötigt auf einem Instrument mehr Slots, als gleichzeitig verfügbar sind. Erhöhen Sie die Kapazität des Instruments in Studio, oder reduzieren Sie, wie viele Laborgeräte gleichzeitig laufen, und simulieren Sie anschließend erneut.',
        shortfallHeader: 'Fehlmenge',
        line: 'Benötigt {{needs}}, hat {{has}}',
      },
      labwareSlotMismatch: {
        title: 'Laborgeräte-Slots passen zwischen den Schritten nicht zusammen',
        description:
          'Der Run kann Laborgeräte nicht in den Slots halten, in denen jeder Schritt sie benötigt. Weisen Sie einen der Schritte einem anderen Gerät zu, oder erhöhen Sie die Kapazität des Geräts in Studio, und simulieren Sie anschließend erneut.',
        receives: 'Benötigt {{labware}} in Slot {{slot}}',
        sends: 'Hinterlässt {{labware}} in Slot {{slot}}',
        locate: '{{task}} im Canvas finden',
      },
      parameterValue: {
        title: 'Parameterwerte konnten nicht angewendet werden',
        description:
          'Die Werte, die dieser Run verwenden würde, wurden abgelehnt, daher wurde nichts geplant. Überprüfen Sie die Werte unter Parameter und simulieren Sie anschließend erneut.',
        unknownField: 'Kein Feld in diesem Workflow',
      },
    },
    settings: {
      automationProfile: 'Automatisierungsprofil',
      runSimulation: 'Simulieren',
      simulating: 'Wird simuliert...',
      disabledTooltip:
        'Workflow-Probleme beheben, um die Simulation zu aktivieren',
      processDisabledTooltip:
        'Probleme beheben, um die Simulation zu aktivieren',
      noPermission:
        'Sie haben keine Berechtigung, Simulationen auszuführen. Bitten Sie Ihren Administrator, Ihnen den Zugriff write:simulation zu gewähren',
      executionPresetName: 'Name der Ausführungsvoreinstellung',
      presetName: 'Name der Voreinstellung',
      simulatedBy: 'Simuliert von: {{name}}',
      automationVersion: 'Automatisierungsversion',
      deviceGroupVersion: 'Gerätegruppenversion',
      processVersion: 'Prozessversion',
    },
    parameters: {
      description:
        'Diese Parameter können beim Hinzufügen zur Warteschlange bearbeitet werden',
      runScopeDescription:
        'Werte gelten nur für diese Simulation. Sie werden nicht in der Ausführungsvoreinstellung oder im Workflow gespeichert.',
      taskParameters: 'Task-Parameter',
      usedIn: 'Verwendet in: {{tasks}}',
      errors: {
        invalidNumber: 'Der Wert muss eine gültige Zahl sein',
        mustBeInteger: 'Der Wert muss eine ganze Zahl sein',
        belowMinimum: 'Der Wert muss mindestens {{minimum}} betragen',
        aboveMaximum: 'Der Wert darf {{maximum}} nicht überschreiten',
        belowMinLength:
          'Der Wert muss mindestens {{minLength}} Zeichen lang sein',
        aboveMaxLength:
          'Der Wert darf {{maxLength}} Zeichen nicht überschreiten',
        patternMismatch: 'Der Wert entspricht nicht dem erforderlichen Format',
        invalidParameter: 'Parameterfehler vor dem Simulieren beheben',
      },
    },
    previewInstructions: {
      button: 'Setup und Cleanup in der Vorschau anzeigen',
      title: 'Anweisungen für Setup und Cleanup',
      noInstructions:
        'Es wurden keine Anweisungen für Setup oder Cleanup hinzugefügt.',
      noInstructionsInPhase: 'Keine Anweisungen in dieser Phase.',
    },
    errors: {
      unexpected:
        'Während der Simulation ist ein unerwarteter Fehler aufgetreten.',
      versionPreviewFailed:
        'Diese Version konnte nicht geladen werden. Zurück zur aktuellen Ansicht.',
      noActiveAutomation:
        'Keine aktive Automatisierung gefunden. Bitte legen Sie eine aktive Automatisierung fest, um eine Simulation auszuführen.',
    },
  },
  status: {
    aborting: 'Wird abgebrochen...',
    canceled: 'Abgebrochen',
    cleanup: 'Cleanup',
    committed: 'Übernommen',
    completed: 'Abgeschlossen',
    constraintBroken: 'Einschränkung verletzt',
    created: 'Erstellt',
    failed: 'Fehlgeschlagen',
    failedToCommit: 'Übernahme fehlgeschlagen',
    failedToPlan: 'Planung fehlgeschlagen',
    idle: 'Inaktiv',
    inProgress: 'In Bearbeitung',
    lowInventory: 'Niedriger Bestand',
    new: 'Neu',
    notStarted: 'Nicht gestartet',
    pending: 'Ausstehend...',
    planned: 'Geplant',
    planning: 'Planung läuft...',
    planningReady: 'Planung bereit',
    processDependent: 'Prozessabhängig',
    processParallel: 'Prozessparallel',
    ready: 'Bereit',
    readyToRun: 'Zur Ausführung bereit',
    acknowledged: 'Bestätigt',
    resolved: 'Behoben',
    resuming: 'Wird fortgesetzt...',
    scheduled: 'Eingeplant',
    setup: 'Setup',
    skipped: 'Übersprungen',
    unspecified: 'Nicht angegeben',
    actionOverdue: 'Aktion überfällig',
    actionSkipped: 'Aktion übersprungen',
    actionComplete: 'Aktion abgeschlossen',
    studioBlocked: 'Blockiert',
    studioConfigured: 'Konfiguriert',
    studioEnabled: 'Aktiviert',
    studioInitialized: 'Initialisiert',
    studioNeedsTraining: 'Training erforderlich',
    studioNotConfigured: 'Nicht konfiguriert',
    studioOffline: 'Offline',
    studioOnline: 'Online',
    studioTrained: 'Trainiert',
    studioWaiting: 'Warten...',
    dueNow: 'Jetzt fällig',
    upcoming: 'Bevorstehend',
    calculating: 'Berechnung läuft...',
    reviewPlan: 'Plan überprüfen',
    preparing: 'Wird vorbereitet',
    settingUp: 'Wird eingerichtet',
    awaitingStart: 'Warten auf Start',
    awaitingCleanup: 'Warten auf Bereinigung',
    cleaningUp: 'Wird bereinigt',
    pausing: 'Wird pausiert...',
    removed: 'Entfernt',
    actionRequired: 'Aktion erforderlich',
    paused: 'Pausiert',
    eStopped: 'Not-Aus',
    fStopped: 'Funktionsstopp',
    warning: 'Warnung',
    safetyEvent: 'Sicherheitsereignis',
  },
  studio: {
    view: {
      abstract: 'Abstrakt',
      list: 'Liste',
    },
    robotTraining: {
      title: 'Roboter einlernen',
    },
    deviceType: {
      robot: 'Roboter',
      robots: 'Roboter',
      shuttle: 'Shuttle',
      shuttles: 'Shuttles',
      safety: 'Sicherheits-Controller',
      lighting: 'Beleuchtungssystem',
      controller: 'Controller',
      controllers: 'Controller',
    },
    controllerSection: {
      connectedTo: 'Verbunden mit',
      addressing: 'Adressierung',
      errors: {
        loadingDeviceData:
          'Gerätedaten werden geladen — versuchen Sie es gleich erneut',
        alreadyAssigned: 'Dem Gerät ist bereits ein Controller zugewiesen',
        incompatibleDevice:
          'Inkompatibles Gerät: "{{model}}" unterstützt das Protokoll "{{protocol}}" nicht',
        protocolMismatch:
          'Protokollkonflikt: Controller verwendet "{{ctrlProtocol}}", Gerät jedoch "{{childProtocol}}"',
        linkFailed:
          'Gerät konnte nicht mit dem Controller verknüpft werden — bitte erneut versuchen',
        unlinkFailed:
          'Verknüpfung des Geräts mit dem Controller konnte nicht aufgehoben werden — bitte erneut versuchen',
        saveFailed:
          'Controller-Konfiguration konnte nicht gespeichert werden — bitte erneut versuchen',
      },
    },
    createGroupDialog: {
      title: 'Neue Gruppe erstellen',
      placeholder: {
        name: 'Gruppennamen eingeben...',
      },
      device: '{{count}} Gerät',
      devices: '{{count}} Geräte',
      summary: 'Zusammenfassung',
      buttons: {
        create: 'Gruppe erstellen',
      },
      errors: {
        createFailed:
          'Gruppe konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
      },
    },
    deviceGroupErrors: {
      duplicateName: 'Eine Gruppe mit diesem Namen existiert bereits.',
    },
    groupDetailsPanel: {
      title: 'Details zu {{name}}',
      placeholder: {
        groupName: 'Gruppennamen eingeben',
        description: 'Gruppenbeschreibung eingeben',
      },
      sections: {
        groupSize: 'Gruppengröße',
      },
      cards: {
        totalDevices: 'Geräte gesamt',
        aria: {
          totalDevicesCount: 'Insgesamt {{count}} Geräte',
        },
      },
      buttons: {
        saveName: 'Gruppennamen speichern',
      },
      errors: {
        emptyName: 'Gruppenname darf nicht leer sein',
        renameFailed:
          'Gruppe konnte nicht umbenannt werden. Bitte versuchen Sie es erneut.',
      },
      permissionRequired:
        'Sie haben keine Berechtigung, Gerätegruppen umzubenennen',
    },
    instrumentGroupSelector: {
      viewDeviceBreakdown: 'Geräteaufschlüsselung anzeigen',
      searchPlaceholder: 'Gruppe suchen...',
    },
    instrumentGroupCard: {
      instrumentWithMarkup: '<0>Instrument</0> <1>{{count}}</1>',
      instrumentsWithMarkup: '<0>Instrumente</0> <1>{{count}}</1>',
      warnings_one: '{{count}} Gerät hat Warnungen',
      warnings_other: '{{count}} Geräte haben Warnungen',
      menu: {
        aria: {
          actionsMenu: 'Aktionsmenü für Gruppe {{groupName}}',
        },
      },
      aria: {
        selectGroup: 'Gruppe {{groupName}} auswählen',
      },
      deleteDialog: {
        title: 'Instrumentengruppe löschen',
        description:
          'Möchten Sie "{{groupName}}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      },
      duplicateDialog: {
        title: 'Instrumentengruppe duplizieren',
        description: 'Geben Sie einen Namen für die duplizierte Gruppe ein.',
        errors: {
          nameConflict: 'Eine Gruppe mit diesem Namen existiert bereits.',
          notFound:
            'Diese Gruppe existiert nicht mehr. Aktualisieren Sie die Seite und versuchen Sie es erneut.',
          unsupportedType: 'Dieser Gruppentyp kann nicht dupliziert werden.',
          unknown:
            'Gruppe konnte nicht dupliziert werden. Bitte versuchen Sie es erneut.',
        },
      },
      duplicateSuccess: 'Gruppe dupliziert',
    },
    deviceEditOverlay: {
      addToGroup: 'Gerät zur Gruppe hinzufügen',
    },
    registerDeviceDialog: {
      title: 'Gerät registrieren',
      selectDevice: 'Gerät {{vendor}} {{model}} auswählen',
      filters: {
        systems: 'Systeme',
      },
      placeholder: {
        search: 'Suchen...',
      },
      tableHeaders: {
        quantity: 'Anzahl',
      },
      selection: {
        none: 'Keine Geräte ausgewählt',
        device: '{{count}} Gerät ausgewählt',
        devices: '{{count}} Geräte ausgewählt',
      },
      buttons: {
        register: 'Registrieren',
        registering: 'Registrierung läuft...',
      },
      errors: {
        loadFailed: 'Geräte konnten nicht geladen werden',
      },
      empty: 'Keine Geräte gefunden',
    },
    registerSafetySystemDialog: {
      success: 'Sicherheitssystem erfolgreich registriert',
      error: 'Sicherheitssystem konnte nicht registriert werden',
    },
    registerLightingSystemDialog: {
      success: 'Beleuchtungssystem erfolgreich registriert',
      error: 'Beleuchtungssystem konnte nicht registriert werden',
    },
    lightingPropertiesPanel: {
      lightingDriver: 'Beleuchtungs-Driver',
      connectedGroups: 'Verbundene Gruppen',
      connectedGroupsPlaceholder: 'Verbundene Gruppen auswählen',
      connectedGroupsEmpty: 'Keine Modulgruppen verfügbar',
      connectedGroupsNoneSelected: 'Keine Gruppen ausgewählt.',
      connectedGroupsRequired:
        'Wählen Sie vor der Bereitstellung mindestens eine verbundene Gruppe aus.',
      removeConnectedGroup: 'Verbundene Gruppe entfernen',
      editingDisabledWhileDeployed:
        'Einstellungen können nicht geändert werden, während das Beleuchtungssystem bereitgestellt ist. Deaktivieren Sie es zuerst.',
      errors: {
        deployFailed: 'Beleuchtungssystem konnte nicht bereitgestellt werden',
        undeployFailed:
          'Bereitstellung des Beleuchtungssystems konnte nicht aufgehoben werden',
        setConnectedGroupsFailed:
          'Verbundene Gruppen konnten nicht aktualisiert werden',
        loadGroupsFailed: 'Modulgruppen konnten nicht geladen werden',
        patchFailed: 'Beleuchtungssystem konnte nicht aktualisiert werden',
        deployed:
          'Einstellungen des Beleuchtungssystems können nicht geändert werden, während es bereitgestellt ist. Deaktivieren Sie es zuerst.',
        notFound: 'Beleuchtungssystem nicht gefunden',
        moduleMissing:
          'Erforderliches Beleuchtungsmodul fehlt. Registrieren Sie das Beleuchtungssystem erneut.',
        groupNotFound: 'Eine der ausgewählten Gruppen existiert nicht mehr',
        groupWrongType:
          'Eine der ausgewählten Gruppen ist keine Standardgruppe',
      },
    },
    safetyPropertiesPanel: {
      safetyAdaptor: 'Sicherheitsadapter',
      version: 'Version',
      enable: 'Aktivieren',
      disable: 'Deaktivieren',
      deploying: 'Bereitstellung läuft...',
      undeploying: 'Bereitstellung wird aufgehoben...',
      connecting: 'Verbindung wird hergestellt...',
      disconnecting: 'Verbindung wird getrennt...',
      editingDisabledWhileDeployed:
        'Einstellungen können nicht geändert werden, während das Sicherheitssystem bereitgestellt ist. Deaktivieren Sie es zuerst.',
      errors: {
        deployFailed: 'Sicherheitssystem konnte nicht aktiviert werden.',
        undeployFailed: 'Sicherheitssystem konnte nicht deaktiviert werden.',
        patchFailed: 'Sicherheitssystem konnte nicht aktualisiert werden',
      },
      status: {
        eStopped: 'Not-Aus',
        fStopped: 'Funktionsstopp',
        fStopping: 'Funktionsstopp läuft',
        disabled: 'Deaktiviert',
        running: 'Running',
        paused: 'Pausiert',
      },
    },
    propertiesPanel: {
      emptyState:
        'Wählen Sie einen Node aus, um dessen Eigenschaften anzuzeigen',
      sections: {
        details: 'Details',
        components: 'Komponenten',
        connection: 'Verbindung',
        robotSettings: 'Robotereinstellungen',
        shuttlePositions: 'Shuttle-Positionen',
        slots: 'Slots',
        capacity: 'Kapazität',
        driver: 'Erweitert',
        state: 'Status',
        controls: 'Steuerung',
        methods: 'Assets',
      },
      methods: {
        empty: 'Keine Methoden hinzugefügt',
        addMethod: 'Asset hinzufügen',
        removeMethod: 'Methode entfernen',
        added: 'Methode mit Gerät verknüpft',
        addFailed: 'Methode konnte nicht verknüpft werden',
        removed: 'Methode entfernt',
        removeFailed: 'Methode konnte nicht entfernt werden',
        addToDevice: 'Zum Gerät hinzufügen',
        selectPackageHint: 'Wählen Sie zuerst ein Paket aus',
        noLibraryMethods: 'Keine Methoden in diesem Paket',
        package: 'Paket',
        method: 'Methode',
        createPackage: 'Neues Paket erstellen',
        tabs: {
          browseMachine: 'Maschine durchsuchen',
          fromLibrary: 'Aus Library',
        },
      },
      components: {
        empty: 'Keine Komponenten definiert',
      },
      state: {
        empty: 'Keine Statusfelder verfügbar',
        readOnly: 'Read only',
        unit: 'Einheit: {{unit}}',
      },
      tasks: {
        empty: 'Keine Tasks verfügbar',
        stepAction: '{{action}}',
        stepTransportIn: 'Transport hinein',
        stepTransportOut: 'Transport heraus',
      },
      controls: {
        endSession: 'Sitzung beenden',
        disconnecting: 'Verbindung wird getrennt',
        inactivityTimeout:
          'Driver-Bereitstellung aufgehoben — seit 10 Minuten keine Aktivität',
        noActions: 'Keine Aktionen verfügbar',
        trigger: 'Auslösen',
        bookingDuration: 'Sitzungsdauer',
        bookingDurationOption: '{{minutes}} min',
        timeRemainingValue: '{{minutes}}m {{seconds}}s',
        timeRemainingLow: 'Sitzung endet in weniger als einer Minute',
        timeRemainingExpired: 'Sitzung ist beendet',
        extend: 'Verlängern +{{minutes}} min',
        extendFailed: 'Sitzung konnte nicht verlängert werden',
        move: 'Bewegen',
        configurationRequiredFields: 'Pflichtfelder',
        triggerDialog: {
          title: 'Auslösen: {{action}}',
          success: 'Aktion erfolgreich abgeschlossen',
          result: 'Ergebnis',
          error: 'Aktion fehlgeschlagen',
          unexpectedError: 'Ein unerwarteter Fehler ist aufgetreten',
        },
        moveDialog: {
          title: 'Shuttle bewegen',
          description:
            'Bewegen Sie das Shuttle zu einer Koordinate innerhalb seines konfigurierten Bereichs.',
          coordinates: 'Koordinaten',
        },
        errors: {
          cannotConnectLocked:
            'Verbindung nicht möglich, während das Gerät gesperrt ist',
          cannotDisconnectLocked:
            'Trennen nicht möglich, während das Gerät durch einen Workflow gesperrt ist',
          cannotTriggerActionLocked:
            'Aktion kann nicht ausgelöst werden, während das Gerät durch einen Workflow gesperrt ist',
          triggerActionFailed: 'Aktion konnte nicht ausgelöst werden',
          deviceUnreachable:
            'Das Gerät antwortet nicht. Prüfen Sie, ob es eingeschaltet und verbunden ist, und versuchen Sie es erneut.',
          connectionRejected:
            'Verbindung zum Gerät nicht möglich. Prüfen Sie die Verbindungseinstellungen (z. B. Adresse oder Port) und versuchen Sie es erneut.',
          couldNotStart:
            'Gerät konnte nicht gestartet werden. Prüfen Sie, ob es eingeschaltet und verbunden ist, und versuchen Sie es erneut.',
          connectionSecrets:
            'Die gespeicherten Verbindungsdaten des Geräts konnten nicht gelesen werden. Prüfen Sie die Konfiguration und versuchen Sie es erneut.',
          inUse:
            'Dieses Gerät wird derzeit verwendet. Versuchen Sie es erneut, sobald es frei ist.',
          sessionEnded:
            'Ihre Gerätesitzung ist beendet. Stellen Sie die Verbindung wieder her, um fortzufahren.',
          actionTimedOut:
            'Das Gerät hat nicht rechtzeitig geantwortet. Bitte versuchen Sie es erneut.',
          actionFailed:
            'Das Gerät konnte die Aktion nicht abschließen. Bitte versuchen Sie es erneut.',
          unavailable:
            'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut — falls das Problem weiterhin besteht, wenden Sie sich an Automata.',
        },
      },
      slotConfiguration: {
        customized: 'angepasst',
        metric: 'mm',
        accessibility: 'Zugänglichkeit',
        addSlot: 'Slot hinzufügen',
        deleteSlot: 'Slot löschen',
        overCapacity:
          'Anzahl speicherbarer Slots ({{count}}) überschreitet die Kapazität ({{capacity}})',
        columns: 'Spalten',
        column: 'Spalte {{number}}',
        row: 'Zeile {{number}}',
        removeColumn: 'Spalte entfernen',
        slotsCount_one: '{{count}} Slot',
        slotsCount_other: '{{count}} Slots',
        slotsCountCustomized_one: '{{count}} Slot ({{customized}} angepasst)',
        slotsCountCustomized_other:
          '{{count}} Slots ({{customized}} angepasst)',
        accessibilityOptions: {
          storage: 'Lager',
          robotAccessible: 'Für Roboter zugänglich',
          loadingBay: 'Ladebucht',
          bin: 'Behälter',
        },
        customizeSlots: 'Anpassen',
        loadingBays: 'Ladebuchten',
        addLoadingBay: 'Ladebucht hinzufügen',
        sharedWithInstrument: 'Gemeinsam mit einem anderen Instrument genutzt',
        selectInstrument: 'Instrument auswählen…',
        transferStationSharedWith: 'Transferstation gemeinsam genutzt mit',
        editConnection: 'Verbindung bearbeiten',
      },
      labels: {
        model: 'Modell',
        image: 'Image',
        mockMode: 'Mock-Modus',
        version: 'Version',
        versionChannel: 'Versionskanal',
        prereleaseVersion: 'Vorabversion',
        prereleaseExpired:
          'Diese Vorabversion ist nicht mehr verfügbar. Bitte wechseln Sie zu einer stabilen Version.',
      },
      versionChannels: {
        stable: 'Stabil',
        prerelease: 'Vorabversion',
      },
      selectVersion: 'Version auswählen',
      selectPrereleaseVersion: 'Vorabversion auswählen',
      searchPrereleaseVersion: 'Vorabversionen durchsuchen',
      prereleaseUnstableWarning:
        'Vorabversionen können instabil sein. Nicht empfohlen für Produktions-Workflows.',
      emptyRelations: 'Keine Verbindungen',
      emptyShuttlePositions: 'Keine Positionen konfiguriert',
      robotSettings: {
        speedProfile: 'Geschwindigkeitsprofil',
        speedProfiles: {
          FAST: 'Schnell',
          STANDARD: 'Standard',
          SLOW: 'Langsam',
          GENTLE: 'Sanft',
        },
        railLength: 'Schienenlänge',
        millimetres: 'mm',
        updateSettingsError:
          'Robotereinstellungen konnten nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        safeRange: {
          title: 'Sicherer Bewegungsbereich',
          description:
            'Der Bereich, in dem sich jede Achse oder jedes Gelenk befinden muss, damit der Roboter automatisch in seine Ausgangsposition fahren kann.',
          axes: {
            X: 'X',
            Y: 'Y',
            Z: 'Z',
            ROTATION: 'Rotation',
            RAIL: 'Schiene',
          },
          addAxis: 'Achse hinzufügen',
          joints: 'Gelenke',
          addJoint: 'Gelenk hinzufügen',
          jointLabel: 'Gelenk {{index}}',
          minLabel: '{{label}} min',
          maxLabel: '{{label}} max',
          remove: 'Sicheren Bereich für {{label}} entfernen',
          setError:
            'Sicherer Bereich konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
          removeError:
            'Sicherer Bereich konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
        },
      },
      shuttlePositions: {
        addPosition: 'Position hinzufügen',
        removePosition: 'Position entfernen',
        duplicateName: 'Eine Position mit diesem Namen existiert bereits',
        selectRobotPlaceholder: 'Roboter auswählen',
        noRobotsConnected: 'Keine Roboter mit diesem Shuttle verbunden',
        addPositionError:
          'Position konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.',
        editPosition: 'Position bearbeiten',
        editPositionError:
          'Position konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
        setAsHomePosition: 'Als Ausgangsposition festlegen',
        positionActions: 'Aktionen für {{name}}',
        coordinateRequired: 'Erforderlich',
      },
      emptyGroups: 'Keiner Gruppe zugeordnet',
      unknownDevice: 'Unbekanntes Gerät',
      tableHeaders: {
        actions: 'Aktionen',
        groupName: 'Gruppenname',
      },
      buttons: {
        close: 'Eigenschaftenbereich schließen',
        removeConnection: 'Verbindung entfernen',
        removeFromGroup: 'Aus Gruppe entfernen',
      },
      validation: {
        invalidFormat: 'Ungültiges Format',
        required: '{{name}} ist erforderlich',
        tooLittle: 'Wert muss mindestens {{value}} betragen',
        tooBig: 'Wert darf höchstens {{value}} betragen',
      },
      connectionDisabledWhileConnected:
        'Die Verbindung kann nicht konfiguriert werden, während dieses Gerät verbunden ist. Bitte trennen Sie zuerst die Verbindung.',
    },
    permissions: {
      devicesConfig: {
        write:
          'Sie haben keine Berechtigung, die Gerätekonfiguration zu ändern. Bitten Sie Ihren Administrator, Ihnen den Zugriff write:devices:config zu gewähren',
        archive:
          'Sie haben keine Berechtigung, Geräte zu löschen. Bitten Sie Ihren Administrator, Ihnen den Zugriff archive:devices:config zu gewähren',
      },
      deviceGroups: {
        write:
          'Sie haben keine Berechtigung, Gerätegruppen zu ändern. Bitten Sie Ihren Administrator, Ihnen den Zugriff write:device_groups zu gewähren',
      },
    },
    floatingMenu: {
      viewModes: {
        select: 'Auswahlmodus',
        relation: 'Beziehungsmodus',
        groupEdit: 'Gruppenbearbeitungsmodus',
      },
      grouping: {
        createGroup: 'Instrumente auswählen, um eine Gruppe zu erstellen',
      },
      display: {
        showExcluded: 'Ausgeschlossene Elemente anzeigen',
        hideExcluded: 'Ausgeschlossene Elemente ausblenden',
      },
    },
    removeDialog: {
      title: 'Gerät aus Gruppe entfernen',
      description:
        'Möchten Sie "{{deviceName}}" wirklich aus "{{groupName}}" entfernen?',
      confirm: 'Entfernen',
    },
    deleteDeviceDialog: {
      title_one: 'Gerät löschen',
      title_other: 'Geräte löschen',
      description_one:
        'Möchten Sie "{{deviceName}}" wirklich löschen? Sie können es später erneut registrieren.',
      description_other:
        'Möchten Sie {{count}} Geräte wirklich löschen? Sie können sie später erneut registrieren.',
      devicesToDelete: 'Zusammenfassung ({{count}})',
    },
    gxp: {
      deleteDeviceGroup: 'Gerätegruppe "{{groupName}}" löschen',
      removeDeviceFromGroup:
        'Gerät "{{deviceName}}" aus Gruppe "{{groupName}}" entfernen',
      deleteDevice_one: 'Gerät "{{deviceName}}" löschen',
      deleteDevice_other: '{{count}} Geräte löschen',
      removeConnection_one:
        'Verbindung zwischen "{{sourceName}}" und "{{targetName}}" entfernen',
      removeConnection_other: '{{count}} Verbindungen entfernen',
    },
    removeConnectionDialog: {
      title_one: 'Verbindung entfernen',
      title_other: 'Verbindungen entfernen',
      description_one:
        'Möchten Sie die Verbindung zwischen "{{sourceName}}" und "{{targetName}}" wirklich entfernen?',
      description_other:
        'Möchten Sie {{count}} Verbindungen wirklich entfernen?',
    },
    edge: {
      removeLink: 'Verbindung zwischen {{source}} und {{target}} entfernen',
    },
    errors: {
      saveCanvasFailed: 'Canvas-Layout konnte nicht gespeichert werden.',
    },
    listView: {
      loading: 'Geräte werden geladen...',
      emptyState: {
        title: 'Keine Geräte',
        description:
          'Registrieren Sie Geräte, um sie hier aufgelistet zu sehen.',
        noDevicesInGroup: 'Keine Geräte in dieser Gruppe',
      },
      tableHeaders: {
        groups: 'Gruppen',
      },
      actions: {
        locateOnCanvas: 'Im Canvas finden',
      },
      aria: {
        selectDevice: '{{deviceName}} auswählen',
        locateDevice: '{{deviceName}} im Canvas finden',
        moreActions: 'Weitere Aktionen für {{deviceName}}',
      },
    },
    status: {
      connected: 'Verbunden',
      connecting: 'Verbindung wird hergestellt',
      disconnected: 'Nicht verbunden',
    },
    groupUsage: {
      checking: 'Suche nach betroffenen Workflows und Prozessen...',
      currentlyDeployed:
        'Diese Gruppe ist derzeit bereitgestellt. Das Löschen stört den laufenden Betrieb.',
      instrumentInActiveGroupWarning:
        'Dieses Instrument befindet sich in einer Gerätegruppe, die derzeit aktiv ist. Das Löschen stört den laufenden Betrieb.',
      affectedWorkflows:
        'Die folgenden Workflows und Prozesse verweisen auf diese Gerätegruppe: <0>{{groupName}}</0>.',
      automation: 'Automatisierung: {{name}}',
      automationUnnamed: 'Unbenannte Automatisierung',
      processExternalId: 'Externe ID: {{externalId}}',
      hasScheduledRuns: 'Aktive Runs',
      blockedAction:
        'Entfernen Sie diese aus der Warteschlange, bevor Sie diese Gruppe {{action}}.',
      blockedActionDevice:
        'Entfernen Sie diese aus der Warteschlange, bevor Sie dieses Gerät entfernen.',
      configWarning:
        'Das Ändern der Konfiguration kann deren Ausführung beeinflussen. Sind Sie sicher?',
    },
  },
  tasks: {
    labwareUsage: {
      title: 'Laborgeräteverbrauch',
      unitsConsumed: 'Verbrauchte Einheiten',
      unitsConsumedFor: 'Verbrauchte Einheiten für {{labware}}',
    },
    errorHandling: {
      label: 'Fehlerbehandlung',
      addAutoRetryButton: 'Auto-Retry hinzufügen',
      addRecoveryInstructionsButton: 'Wiederherstellungsanweisungen hinzufügen',
      autoRetry: {
        cardTitle: 'Auto-Retry',
        removeLabel: 'Auto-Retry entfernen',
      },
      recoveryInstructions: {
        cardTitle: 'Wiederherstellungsanweisungen',
        removeLabel: 'Wiederherstellungsanweisungen entfernen',
      },
      maxRetries: 'Anzahl der Wiederholungen',
      maxRetriesError:
        'Anzahl der Wiederholungen muss zwischen 1 und 10 liegen',
      applyTo: 'Anwenden auf',
      scopeLabel: 'Fehlercode-Geltungsbereich',
      scope: {
        all: 'Alle Fehlercodes',
        selected: 'Ausgewählte Fehlercodes',
      },
      selectionPrompt: 'Fehlercodes auswählen, für die Auto-Retry gelten soll:',
      codes: {
        COMM_TIMEOUT: 'Kommunikations-Timeout',
        COMM_ERROR: 'Kommunikationsfehler',
        INIT_FAILED: 'Initialisierung fehlgeschlagen',
        NOT_INITIALIZED: 'Nicht initialisiert',
        DEVICE_ERROR: 'Gerätefehler',
        COMMAND_FAILED: 'Befehl fehlgeschlagen',
        OPERATION_TIMEOUT: 'Vorgangs-Timeout',
        INVALID_PARAMETER: 'Ungültiger Parameter',
        DEVICE_BUSY: 'Gerät belegt',
        DEVICE_NOT_READY: 'Gerät nicht bereit',
        EMERGENCY_STOP: 'Not-Aus',
        DOOR_OPEN: 'Tür offen',
        DOOR_CLOSED: 'Tür geschlossen',
        DOOR_JAM: 'Tür klemmt',
        DOOR_OPEN_TIMEOUT: 'Timeout beim Öffnen der Tür',
        DOOR_CLOSE_TIMEOUT: 'Timeout beim Schließen der Tür',
        TEMP_OUT_OF_RANGE: 'Temperatur außerhalb des Bereichs',
        TEMP_TIMEOUT: 'Temperatur-Timeout',
        TEMP_SENSOR_ERROR: 'Temperatursensor-Fehler',
        NO_PLATE_FOUND: 'Keine Platte gefunden',
        TRANSFER_FAILED: 'Transfer fehlgeschlagen',
        SLOT_OCCUPIED: 'Slot belegt',
        SLOT_EMPTY: 'Slot leer',
        SEAL_FAILED: 'Seal fehlgeschlagen',
        PEEL_FAILED: 'Peel fehlgeschlagen',
        CAP_FAILED: 'Verschließen fehlgeschlagen',
        UNCAP_FAILED: 'Entkappen fehlgeschlagen',
        MOTOR_ERROR: 'Motorfehler',
        IMBALANCE_DETECTED: 'Unwucht erkannt',
        HOME_FAILED: 'Referenzfahrt fehlgeschlagen',
        POSITION_ERROR: 'Positionsfehler',
        COLLISION_DETECTED: 'Kollision erkannt',
        ITEM_LOST: 'Objekt verloren',
        ITEM_MISSING: 'Objekt fehlt',
        METHOD_NOT_FOUND: 'Methode nicht gefunden',
        METHOD_FAILED: 'Methode fehlgeschlagen',
        SOFTWARE_CRASH: 'Softwareabsturz',
        PROTOCOL_ERROR: 'Protokollfehler',
        SPIN_TIMEOUT: 'Spin-Timeout',
        HATCH_OPEN_TIMEOUT: 'Timeout beim Öffnen der Klappe',
        HATCH_CLOSE_TIMEOUT: 'Timeout beim Schließen der Klappe',
        CONDITIONAL_EVALUATION_FAILED:
          'Auswertung der Bedingung fehlgeschlagen',
      },
      codeDescriptions: {
        COMM_TIMEOUT: 'Kommunikations-Timeout mit dem Gerät (seriell, IP, USB)',
        COMM_ERROR:
          'Lesen oder Schreiben auf dem Gerät fehlgeschlagen oder unerwartete Antwort',
        INIT_FAILED: 'Verbindung oder Initialisierung fehlgeschlagen',
        NOT_INITIALIZED:
          'Driver wurde verwendet, bevor die Verbindung hergestellt wurde',
        DEVICE_ERROR: 'Sammelmeldung für herstellerspezifische Hardwarefehler',
        COMMAND_FAILED: 'Gerät hat den Befehl nicht bestätigt oder ausgeführt',
        OPERATION_TIMEOUT:
          'Aktion wurde nicht innerhalb der erwarteten Zeit abgeschlossen',
        INVALID_PARAMETER:
          'Parameter außerhalb des Bereichs oder ungültig für den aktuellen Gerätestatus',
        DEVICE_BUSY: 'Gerät ist mit einem anderen Vorgang beschäftigt',
        DEVICE_NOT_READY:
          'Gerät kann die angeforderte Aktion in seinem aktuellen Status nicht ausführen',
        EMERGENCY_STOP: 'Not-Aus am Gerät ausgelöst',
        DOOR_OPEN: 'Tür ist offen, obwohl sie geschlossen sein sollte',
        DOOR_CLOSED: 'Tür ist geschlossen, obwohl sie offen sein sollte',
        DOOR_JAM: 'Türmechanismus ist blockiert oder verklemmt',
        DOOR_OPEN_TIMEOUT:
          'Tür hat sich nicht innerhalb der erwarteten Zeit geöffnet',
        DOOR_CLOSE_TIMEOUT:
          'Tür hat sich nicht innerhalb der erwarteten Zeit geschlossen',
        TEMP_OUT_OF_RANGE: 'Temperatur liegt außerhalb des zulässigen Bereichs',
        TEMP_TIMEOUT:
          'Zieltemperatur konnte nicht innerhalb der erwarteten Zeit erreicht werden',
        TEMP_SENSOR_ERROR:
          'Temperatursensor ist defekt oder nicht angeschlossen',
        NO_PLATE_FOUND: 'Keine Platte an der erwarteten Position erkannt',
        TRANSFER_FAILED: 'Plattentransfer fehlgeschlagen',
        SLOT_OCCUPIED: 'Ziel-Slot enthält bereits Laborgeräte',
        SLOT_EMPTY: 'Quell-Slot enthält keine Laborgeräte',
        SEAL_FAILED: 'Seal-Vorgang fehlgeschlagen',
        PEEL_FAILED: 'Peel-Vorgang fehlgeschlagen',
        CAP_FAILED:
          'Verschließvorgang fehlgeschlagen — Ausrichtung von Kappe und Röhrchen prüfen',
        UNCAP_FAILED:
          'Entkappungsvorgang fehlgeschlagen — Ausrichtung von Kappe und Röhrchen prüfen',
        MOTOR_ERROR: 'Motorblockade, Überstrom oder Treiberfehler',
        IMBALANCE_DETECTED: 'Unwucht während der Rotation erkannt',
        HOME_FAILED: 'Referenzfahrt fehlgeschlagen',
        POSITION_ERROR: 'Zielposition konnte nicht erreicht werden',
        COLLISION_DETECTED: 'Unerwarteter physischer Kontakt erkannt',
        ITEM_LOST:
          'Laborgeräte wurden während des Transports fallen gelassen oder nicht mehr erfasst',
        ITEM_MISSING: 'Erwartete Laborgeräte an der Station nicht gefunden',
        METHOD_NOT_FOUND: 'Protokoll- oder Methodendatei nicht gefunden',
        METHOD_FAILED: 'Protokollausführung fehlgeschlagen',
        SOFTWARE_CRASH: 'Software des Hersteller-Instruments abgestürzt',
        PROTOCOL_ERROR: 'Fehler bei Protokollvalidierung oder -ausführung',
        SPIN_TIMEOUT:
          'Spin-Zyklus wurde nicht innerhalb der erwarteten Zeit abgeschlossen',
        HATCH_OPEN_TIMEOUT:
          'Klappe hat sich nicht innerhalb der erwarteten Zeit geöffnet',
        HATCH_CLOSE_TIMEOUT:
          'Klappe hat sich nicht innerhalb der erwarteten Zeit geschlossen',
        CONDITIONAL_EVALUATION_FAILED:
          'Ein Bedingungsschritt konnte nicht ausgewertet werden, häufig weil die erwarteten Daten noch nicht verfügbar waren. Ein erneuter Versuch kann erfolgreich sein, sobald die erforderlichen Daten generiert wurden.',
      },
    },
    mockBehaviour: {
      label: 'Mock-Verhalten',
      addButton: 'Mock-Verhalten hinzufügen',
      success: 'Erfolg',
      useExecutionTime: 'Ausführungszeit verwenden',
      executionTime: 'Ausführungszeit (Sekunden)',
      executionTimeError:
        'Ausführungszeit muss zwischen 0 und 3600 Sekunden liegen',
      removeLabel: 'Mock-Verhalten entfernen',
    },
    types: {
      analyze: 'Analysieren',
      aspirate: 'Aspirieren',
      cap: 'Verschließen',
      dispense: 'Ausgeben',
      evaporate: 'Verdampfen',
      extract: 'Extrahieren',
      incubate: 'Inkubieren',
      manual_task: 'Manuelle Aufgabe',
      peel: 'Peel',
      read: 'Lesen',
      rinse: 'Spülen',
      run_liquid_handler: 'Run liquid handler',
      run_protocol: 'Protokoll ausführen',
      scan_barcode: 'Barcode scannen',
      seal: 'Seal',
      shake: 'Shake',
      spin: 'Spin',
      store: 'Ablegen',
      thermocycle: 'Thermocycle',
      ultrasonicate: 'Ultraschallbehandlung',
      uncap: 'Entkappen',
      wash: 'Waschen',
    },
    properties: {
      labware_and_deck: {
        labware_and_deck: 'Laborgeräte & Deck',
        startSlotAria: 'Start-Slot für {{labware}}',
        endSlotAria: 'End-Slot für {{labware}}',
      },
      accessories: {
        addAccessory: 'Zubehör hinzufügen',
        accessory: 'Zubehör',
        actionsColumn: 'Zubehör entfernen',
        removeAria: '{{name}} entfernen',
      },
      task_instructions: 'Task-Anweisungen',
      selectInstrument: 'Instrument auswählen',
      addDescription: 'Beschreibung hinzufügen',
      editorPlaceholder: "Schreiben oder '/' für Befehle eingeben...",
      updateDescription: 'Beschreibung aktualisieren',
      uploadFailed:
        'Medien konnten nicht hochgeladen werden. Bitte erneut versuchen.',
      dataCapture: {
        title: 'Datenerfassung',
        tooltip:
          'Daten, die der Operator im Rahmen dieser Aktion erfasst und bereitstellt',
        add: 'Datenerfassung hinzufügen',
        namePlaceholder: 'Name eingeben',
        descriptionOptional: '(optional)',
        minValue: 'Mindestwert',
        maxValue: 'Höchstwert',
        types: {
          text: 'Text',
          number: 'Zahl',
          boolean: 'Boolean',
          duration: 'Dauer',
          file: 'Datei',
        },
      },
      signatureConfig: {
        modalTitle: 'Signatur konfigurieren',
        requirementLabel: 'Signaturanforderung',
        requirement: {
          singleDescription: 'Autor der Aktion',
          dualDescription: 'Autor und Freigeber oder Zeuge der Aktion',
        },
        primarySignature: 'Primäre Signatur',
        primaryMeaningLabel: 'Bedeutung der Signatur',
        primaryMeaningValue:
          'Autorschaft. Aktion korrekt durchgeführt und aufgezeichnet.',
        guidanceForSigner: 'Hinweise für Unterzeichner',
        guidanceForSignerPlaceholder:
          'Hinweise, die dem Unterzeichner angezeigt werden',
        secondarySignature: 'Sekundäre Signatur',
        removeSecondary: 'Sekundäre Signatur entfernen',
        secondaryReason: 'Grund für die sekundäre Signatur',
        secondaryReasonOptions: {
          witness: 'Bezeugung durch sekundären Benutzer',
          approval: 'Freigabe durch sekundären Benutzer',
        },
        secondaryFrom: 'Sekundäre Signatur von',
        save: 'Konfiguration speichern',
      },
    },
    affixTask: {
      preAndPostTasks: 'Vor- & Nachaufgaben',
      preTasks: 'Voraufgaben',
      postTasks: 'Nachaufgaben',
      addTask: 'Aufgabe hinzufügen',
      removeAction: 'Aktion entfernen',
      noActionsAvailable: 'Keine Unteraktionen verfügbar',
      deleteConfirmTitle: 'Aufgabe löschen',
      deleteConfirmDescription:
        'Möchten Sie "{{actionName}}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      reorderAction: 'Aktion neu anordnen',
      timeSensitive: 'Zeitkritisch',
    },
  },
  timeInput: {
    secondsPlaceholder: 'ss',
    seconds: 'Sekunden',
    minutesPlaceholder: 'mm',
    minutes: 'Minuten',
    hoursPlaceholder: 'hh',
    hours: 'Stunden',
  },
  toast: {
    error: {
      failedToAddNodeToCanvas:
        'Node konnte nicht zum Canvas hinzugefügt werden. Bitte wenden Sie sich an das Automata-Team.',
      someDevicesFailedToRegister:
        'Einige Geräte konnten nicht registriert werden. {{registered}} von {{total}} Geräten wurden erfolgreich registriert.',
      failedToAddDevicesToGroup:
        'Geräte wurden registriert, konnten aber nicht zur Gruppe hinzugefügt werden.',
      failedToDeleteDevices:
        'Ein oder mehrere Geräte konnten nicht gelöscht werden. Bitte versuchen Sie es erneut.',
    },
    info: {
      automationMismatchOnPaste:
        'Automatisierungskonfiguration wurde nicht kopiert – konfigurieren Sie die Geräte für die eingefügten Nodes.',
      pasteAutomapAllMapped_one:
        '{{count}} Task automatisch einem kompatiblen Instrument zugeordnet.',
      pasteAutomapAllMapped_other:
        '{{count}} Tasks automatisch kompatiblen Instrumenten zugeordnet.',
      pasteAutomapPartial_one:
        '{{count}} Task automatisch zugeordnet. {{remaining}} benötigen manuelle Konfiguration.',
      pasteAutomapPartial_other:
        '{{count}} Tasks automatisch zugeordnet. {{remaining}} benötigen manuelle Konfiguration.',
    },
  },
  workflowLibrary: {
    resourceName: 'library',
    libraryRow: {
      rowActions: 'Zeilenaktionen',
    },
    tempSimulatedErrorSelect: {
      placeholder: 'Voreingestellten Workflow auswählen (optional)',
      searchPlaceholder: 'Workflow suchen',
      empty: 'Kein Workflow gefunden',
    },
    createNew: 'Neu erstellen',
    createNewWorkflow: 'Neuen Workflow erstellen',
    createWorkflow: 'Workflow erstellen',
    createNewProcess: 'Neuen Prozess erstellen',
    createProcess: 'Prozess erstellen',
    process: 'Prozess',
    processes: 'Prozesse',
    searchBar: {
      placeholder: 'Nach Name suchen oder nach Tag filtern...',
      openLabel: 'Suche öffnen',
      filterLabel: 'Nach Tag filtern',
    },
    selectWorkcell: 'Workcell auswählen...',
    searchWorkcell: 'Workcell suchen...',
    noWorkcellFound: 'Keine Workcell gefunden',
    workflowCreationError: 'Fehler beim Erstellen des Workflows',
    errorLoadingLibrary: 'Fehler beim Laden der Library',
    presetWorkflowIncompatible:
      'Der voreingestellte Workflow ist nicht mit dieser Workcell kompatibel. Einige erforderliche Instrumente fehlen.',
    duplicateAutomationDialog: {
      title: 'Automatisierung duplizieren',
      description: 'Namen für die duplizierte Automatisierung eingeben',
      placeholder: 'z. B. Hamilton STAR Automatisierung',
      copySuffix: '{{name}} (Kopie)',
    },
    deleteWorkflowDialog: {
      title: 'Workflow löschen',
      description:
        'Möchten Sie "{{workflowName}}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      gxpDescription: 'Workflow löschen: {{workflowName}}',
    },
    deleteWorkflowError: 'Workflow konnte nicht gelöscht werden',
    duplicateWorkflowDialog: {
      title: 'Workflow duplizieren',
      definitionOnlyDescription:
        'Nur die Workflow-Definition ohne Automatisierungen duplizieren',
      definitionAndAutomationsDescription:
        'Workflow-Definition und ausgewählte Automatisierungen duplizieren',
      executionPresets_one: '{{count}} Ausführungsvoreinstellung',
      executionPresets_other: '{{count}} Ausführungsvoreinstellungen',
    },
    duplicateWorkflowError: 'Workflow konnte nicht dupliziert werden',
    duplicateWorkflowSuccess: 'Workflow erfolgreich dupliziert',
    renameWorkflowDialog: {
      title: 'Workflow umbenennen',
      description: 'Neuen Namen für "{{name}}" eingeben',
      nameLabel: 'Workflow-Name',
    },
    renameWorkflowError: 'Workflow konnte nicht umbenannt werden',
    renameWorkflowSuccess: 'Workflow erfolgreich umbenannt',
    archiveWorkflowDialog: {
      title: 'Workflow {{workflowName}} archivieren',
      description:
        'Sie sind dabei, {{workflowName}} aus der Library zu archivieren.',
      warning:
        'Alle Automatisierungen und zugehörigen Ausführungsvoreinstellungen in diesem Workflow werden archiviert.\nArchivierte Elemente stehen für neue Ausführungen nicht mehr zur Verfügung.\n\nVorhandene Ausführungsdatensätze bleiben zur Nachvollziehbarkeit im Ausführungsprotokoll erhalten.',
      reasonLabel: 'Grund für die Archivierung',
      reasonPlaceholder:
        'Grund für diese Aktion angeben. Dieser wird im Benutzerprotokoll erfasst.',
      confirm: 'Workflow archivieren',
    },
    tags: {
      label: 'Tag',
      listLabel: 'Tags',
      editorLabel: 'Tags bearbeiten',
      addLabel: 'Tag hinzufügen',
      removeLabel: 'Tag {{name}} entfernen',
      moreLabel_one: '+{{count}} weiteres Tag',
      moreLabel_other: '+{{count}} weitere Tags',
      searchPlaceholder: 'Suchen oder erstellen',
      createPrefix: 'Erstellen',
      noResults: 'Keine Tags gefunden',
      manageLabel: 'Tag {{name}} verwalten',
      renameInputLabel: 'Tag-Name',
      deleteDisabledTooltip:
        'Dieses Tag vor dem Löschen von allen Elementen trennen',
      renameSuccess: 'Tag umbenannt',
      renameError: 'Tag konnte nicht umbenannt werden',
      deleteSuccess: 'Tag gelöscht',
      deleteError: 'Tag konnte nicht gelöscht werden',
      validation: {
        empty: 'Tag-Name darf nicht leer sein',
        maxLength: 'Tag-Name darf höchstens {{maxLength}} Zeichen lang sein',
        invalidCharacters:
          'Tag-Name darf nur Buchstaben, Ziffern, Punkte und einzelne Leerzeichen enthalten',
      },
    },
    archiveWorkflowSuccess: 'Workflow erfolgreich archiviert',
    archiveWorkflowError: 'Workflow konnte nicht archiviert werden',
    deleteProcessDialog: {
      title: 'Prozess löschen',
      description:
        'Möchten Sie "{{processName}}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
      gxpDescription: 'Prozess löschen: {{processName}}',
    },
    deleteProcessError: 'Prozess konnte nicht gelöscht werden',
    duplicateProcessDialog: {
      title: 'Prozess duplizieren',
      description: 'Kopie von "{{name}}" erstellen',
      nameLabel: 'Prozessname',
      copySuffix: '{{name}} (Kopie)',
      selectAutomationsLabel: 'Zu duplizierende Automatisierungen auswählen',
      definitionOnly: 'Nur Definition',
      definitionOnlyDescription:
        'Nur die Prozessdefinition ohne Automatisierungen duplizieren',
      definitionAndAutomations: 'Definition + Automatisierungen',
      definitionAndAutomationsDescription:
        'Prozessdefinition und ausgewählte Automatisierungen duplizieren',
      warning:
        'Ausführungsvoreinstellungen sowie zugehörige Tags der Produktions- und Teststufe werden nicht dupliziert. Die Kopie beginnt mit einem neuen Versionsverlauf.',
    },
    duplicateProcessError: 'Prozess konnte nicht dupliziert werden',
    duplicateProcessSuccess: 'Prozess erfolgreich dupliziert',
    rightPanel: {
      automations: 'Automatisierungen',
      executionPresetsTest: 'Ausführungsvoreinstellungen: Test [{{count}}]',
      executionPresetsProduction:
        'Ausführungsvoreinstellungen: Produktion [{{count}}]',
      executionPresets: 'Ausführungsvoreinstellungen [{{count}}]',
      noAutomations: 'Keine Automatisierungen',
      executionPresetsUnavailable:
        'Details zur Ausführungsvoreinstellung sind für Prozesse noch nicht verfügbar.',
      close: 'Panel schließen',
    },
    permissions: {
      writeDefinition:
        'Sie haben keine Berechtigung, Definitionen zu bearbeiten. Bitten Sie Ihren Administrator, Ihnen den Zugriff write:definition zu gewähren.',
      createSimulationTest:
        'Sie haben keine Berechtigung, Testsimulationen auszuführen. Bitten Sie Ihren Administrator, Ihnen den Zugriff create:simulation:test zu gewähren.',
      createSimulationProduction:
        'Sie haben keine Berechtigung, Produktionssimulationen auszuführen. Bitten Sie Ihren Administrator, Ihnen den Zugriff create:simulation:production zu gewähren.',
    },
  },
  workflowStepProperties: {
    tabs: {
      automation: 'Workflow-Automatisierung',
      details: 'Workflow-Details',
    },
    mapLabware: 'Verbundene Laborgeräte zuordnen',
    noMappings:
      'Laborgeräte verbinden oder aus dem Workflow hinzufügen, um Zuordnungen zu erstellen.',
    selectLabware: 'Laborgeräte auswählen',
    unconnected: 'Nicht verbunden',
    deleteMapping: 'Zuordnung löschen',
    additionalLabware: 'Zusätzliche Laborgeräte',
    allLabwareMapped: 'Alle Workflow-Laborgeräte wurden zugeordnet.',
    automation: 'Automatisierung',
    selectAutomation: 'Automatisierung auswählen',
    plan: 'Ausführungsvoreinstellung',
    selectPlan: 'Ausführungsvoreinstellung auswählen',
    noPlans:
      'Keine Ausführungsvoreinstellungen für diese Automatisierung verfügbar.',
    parameters: 'Parameters',
    noParameters: 'Keine Parameter in dieser Ausführungsvoreinstellung.',
    parametersReadOnly:
      'Die Parameterbearbeitung ist in dieser Umgebung nicht verfügbar. Dies sind die Werte aus der Ausführungsvoreinstellung.',
    selectWorkflowFirst: 'Zuerst einen Workflow auswählen',
    parametersLoadError:
      'Parameter für diese Ausführungsvoreinstellung konnten nicht geladen werden.',
    parameterErrors: {
      required: 'Dieser Parameter benötigt einen Wert',
    },
  },
  workflowVersioning: {
    permissions: {
      archiveAutomation:
        'Sie haben keine Berechtigung, Automatisierungen zu löschen. Bitten Sie Ihren Administrator, Ihnen Zugriff auf archive:automation zu gewähren.',
    },
    history: {
      title: 'Versionsverlauf: {{name}}',
      notSaved:
        'Automatisierung noch nicht gespeichert. Speichern Sie den Workflow, um die erste Revision zu erstellen.',
      noRevisions: 'Keine Revisionen gefunden',
    },
    revisionCard: {
      revision: 'Revision {{number}}',
      definitionRevision: 'Definitionsrevision {{number}}',
      basedOnRevision: 'Basiert auf Revision {{number}}',
    },
    preview: {
      title: 'Vorschau von Revision {{number}}',
      from: ' vom {{date}}',
      exit: 'Vorschau beenden',
      executionPresetBanner: {
        title: 'Vorschau von Automatisierungsversion {{automationVersion}}',
        presetVersion: 'Voreinstellungsversion {{version}} wird angezeigt',
        presetVersionNamed:
          'Version {{version}} der Ausführungsvoreinstellung „{{name}}“ wird angezeigt',
        presetVersionRevisions:
          'basiert auf Automatisierung v{{automationRevision}} und Workflow v{{definitionRevision}}',
        presetVersionProcessRevision:
          'simuliert mit Prozessrevision {{processRevision}}',
        createdAt: 'Erstellt am {{date}}',
        associatedPreset: 'Verknüpft mit Ausführungsvoreinstellung: {{name}}',
        restore: 'Diese Version zum Bearbeiten wiederherstellen',
      },
      loadFailed:
        'Diese Version konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
    },
    restoreVersionDialog: {
      title:
        'Automatisierungsversion {{automationVersion}} zum Bearbeiten wiederherstellen?',
      versionDetails:
        'Automatisierungsversion {{automationVersion}} vom {{date}}.',
      deselectNote:
        'Die aktuell ausgewählte Ausführungsvoreinstellung wird bei der Wiederherstellung abgewählt.',
      error:
        'Wiederherstellung der Version fehlgeschlagen. Bitte versuchen Sie es erneut.',
      analysisError:
        'Diese Version konnte nicht auf Gerätekompatibilität geprüft werden. Bei der Wiederherstellung könnten Automatisierungen auf Geräte verweisen, die nicht mehr existieren.',
    },
    revert: {
      title: 'Auf Revision {{number}} zurücksetzen?',
      description:
        'Dadurch wird eine neue Revision mit dem Inhalt von Revision {{number}} erstellt. Ihr aktueller Entwurf bleibt im Verlauf erhalten.',
      checkingChanges: 'Änderungen der Instrumentengruppe werden geprüft...',
      deviceChanges: 'Geräteänderungen der Instrumentengruppe',
      deviceChangesDescription:
        'Die Instrumentengruppe hat sich seit dieser Revision geändert. Überprüfen Sie die Geräteänderungen für jede betroffene Automatisierung.',
      reverting: 'Wird zurückgesetzt...',
      confirm: 'Zurücksetzen',
      devicesAddedNoBreaking_one:
        '{{count}} Gerät hinzugefügt, keine inkompatiblen Änderungen.',
      devicesAddedNoBreaking_other:
        '{{count}} Geräte hinzugefügt, keine inkompatiblen Änderungen.',
      noDeviceChanges: 'Keine Geräteänderungen erkannt.',
    },
    upgrade: {
      title: 'Instrumentengruppe aktualisieren',
      description:
        'Überprüfen Sie die Geräteänderungen vor der Aktualisierung.',
      safeWithAdditions_one:
        '{{count}} Gerät hinzugefügt, keine inkompatiblen Änderungen. Aktualisierung sicher möglich.',
      safeWithAdditions_other:
        '{{count}} Geräte hinzugefügt, keine inkompatiblen Änderungen. Aktualisierung sicher möglich.',
      safeNoChanges:
        'Keine Geräteänderungen erkannt. Aktualisierung sicher möglich.',
      updatedDevices_one: '{{count}} Gerät auf eine neue Version aktualisiert.',
      updatedDevices_other:
        '{{count}} Geräte auf eine neue Version aktualisiert.',
      removedNotAffected_one:
        '{{count}} Gerät entfernt, aber keine Workflow-Elemente sind betroffen.',
      removedNotAffected_other:
        '{{count}} Geräte entfernt, aber keine Workflow-Elemente sind betroffen.',
      applying: 'Wird angewendet...',
      apply: 'Aktualisierung anwenden',
    },
    revisionUpdate: {
      missingGroup:
        'Instrumentengruppe für diese Automatisierung nicht gefunden',
      revisionMismatch:
        'Instrumentengruppe wurde aktualisiert (Revision {{current}} → {{active}})',
      updateAutomation: 'Automatisierung aktualisieren',
    },
    deviceImpact: {
      added: 'Hinzugefügt ({{count}})',
      removed: 'Entfernt ({{count}})',
      affectedItems: 'Betroffene Elemente:',
      resolution: 'Lösung:',
      selectResolution: 'Lösung auswählen...',
      clearAssignment: 'Zuweisung aufheben',
      noAffectedItems: 'Keine Workflow-Elemente verweisen auf dieses Gerät.',
    },
    automationCard: {
      upgradeTooltip:
        'Geräte der Instrumentengruppe geändert — zum Aktualisieren klicken',
    },
    lifecycle: {
      versionName: 'Versionsname',
      versionNamePlaceholder: 'z. B. v1.0, stabil, release-2024',
      versionNameDuplicate:
        'Es existiert bereits eine {{stage}}-Version mit dem Namen „{{name}}“',
      confirmTest: {
        title: 'Revision {{number}} als Test markieren?',
        description:
          'Dadurch wird Revision {{number}} als Testversion zur Validierung gekennzeichnet.',
      },
      confirmProduction: {
        title: 'Revision {{number}} auf Produktion hochstufen?',
        description:
          'Dadurch wird Revision {{number}} als Produktion gekennzeichnet, was bedeutet, dass sie SOP-bereit ist.',
      },
      confirmRemoveTag: {
        description:
          'Dadurch wird das {{stage}}-Tag „{{name}}“ von Revision {{number}} entfernt.',
      },
      badges: {
        draft: 'Entwurf',
      },
      errors: {
        updateStageFailed: 'Planstufe konnte nicht aktualisiert werden',
      },
      dialog: {
        planDetails: 'Plandetails',
        workflow: 'Workflow:',
        automation: 'Automatisierung:',
        simulation: 'Simulation:',
        deviceGroup: 'Gerätegruppe:',
        parametersEditableAtExecution:
          'Parameter bei der Ausführung bearbeitbar',
        promotionHistory: 'Hochstufungsverlauf',
        descriptionOptional: 'Beschreibung (optional)',
        descriptionPlaceholder: 'Notizen zu dieser Version hinzufügen...',
      },
    },
  },
  notebook: {
    title: 'Notebook-Analyse',
    runPicker: {
      title: 'Ausführungen in einem Notebook analysieren',
      description:
        'Wählen Sie die Ausführungen aus, die in das Notebook übernommen werden sollen — die Ausführung, von der Sie kamen, ist bereits ausgewählt.',
      descriptionNoPreselect:
        'Wählen Sie die Ausführungen aus, die in das Notebook übernommen werden sollen.',
      legend: 'In die Analyse einzubeziehende Ausführungen',
      next: 'Weiter',
      back: 'Zurück',
      open: 'Notebook öffnen',
      selected: '{{count}} ausgewählt',
      presetFilterLabel: 'Ausführungs-Preset',
      allWorkflows: 'Alle Workflows',
      allPresets: 'Alle Presets',
      dateRangeLabel: 'Zeitraum',
      columnRun: 'Ausführung',
      columnPreset: 'Preset',
      columnStarted: 'Gestartet',
      removeSelected: '{{label}} entfernen',
      notebookStepTitle: 'Neue Analyse',
      notebookStepDescription:
        'Wähle ein Notebook, in dem die Analyse gestartet werden soll, oder fahre mit einem leeren Notebook fort.',
      notebookLabel: 'Notebook',
    },
    spawning: 'Notebook-Sitzung wird gestartet…',
    sessionUnavailable: 'Diese Notebook-Sitzung ist nicht mehr verfügbar.',
    back: 'Zurück',
    sessionError: 'Notebook-Sitzung konnte nicht gestartet werden',
    connecting: 'Verbindung zum Kernel wird hergestellt…',
    connectionError: 'Verbindung zum Notebook-Kernel fehlgeschlagen. {{error}}',
    runData: 'Ausführungsdaten',
    dataError: 'Ausführungsdaten konnten nicht geladen werden.',
    runCell: 'Zelle ausführen',
    deleteCell: 'Zelle löschen',
    editMarkdown: 'Markdown bearbeiten',
    markdownPlaceholder: 'Leere Markdown-Zelle – zum Bearbeiten doppelklicken',
    runAll: 'Alle ausführen',
    interrupt: 'Unterbrechen',
    restart: 'Neu starten',
    addCode: 'Codezelle',
    addMarkdown: 'Markdown-Zelle',
    saving: 'Wird gespeichert…',
    saved: 'Gespeichert',
    kernel: 'Kernel: {{status}}',
    imageOutput: 'Zellenausgabe',
    interactiveOutput: 'Interaktive Ausgabe',
    imageUnavailable: 'Bild nicht verfügbar',
    openLens: 'Notebook öffnen',
    openSession: 'Bearbeitbares Notebook öffnen',
    openWorkflow: 'Workflow öffnen',
    frameShape: '{{rows}} Zeilen × {{cols}} Spalten',
    indexColumn: 'Index',
    endAnalysis: 'Analyse beenden',
    openInJupyterLab: 'In JupyterLab öffnen',
    openInEva: 'In Eva öffnen',
    endAnalysisTitle: 'Analyse beenden?',
    endAnalysisBody:
      'Dadurch werden die Notebook-Daten der Ausführung entfernt. Speichern Sie zuerst alles, was Sie behalten möchten, in der Bibliothek.',
    endAnalysisError: 'Analyse konnte nicht beendet werden',
  },
  notebookLibrary: {
    saved: 'Notebook gespeichert',
    saveError: 'Notebook konnte nicht gespeichert werden',
    rename: 'Umbenennen',
    renameTitle: 'Notebook umbenennen',
    deleteTitle: 'Notebook löschen?',
    deleteBody:
      'Dadurch wird {{name}} aus der Bibliothek entfernt und kann nicht rückgängig gemacht werden.',
    saveToLibrary: 'In Bibliothek speichern',
    saveToLibraryTitle: 'Notebook in Bibliothek speichern',
    saveToLibrarySuccess: 'Notebook in der Bibliothek gespeichert',
    selectPackage: 'Paket auswählen',
  },
  lens: {
    analyse: 'Analysieren',
    searchPlaceholder: 'Analysen suchen',
    agentSessions: {
      title: 'Agent-Sitzungen [{{count}}]',
      agent: 'Agent',
      messages: 'Nachrichten',
      lastActive: 'Zuletzt aktiv',
      view: 'Ansehen',
      untitled: 'Unbenannte Sitzung',
      readOnly: 'Schreibgeschützte Ansicht der Agent-Sitzung.',
      viewError: 'Diese Sitzung konnte nicht geladen werden.',
      noMessages: 'Keine Nachrichten in dieser Sitzung.',
      toolActivity: 'Tool-Aktivität',
    },
    agentNotebooks: {
      title: 'Agent [{{count}}]',
    },
    source: 'aus {{source}}',
    agentBadge: 'LINQ AI',
    agentBadgeLabel: 'Von LINQ AI erstellt',
    cardMeta_one: '{{time}} · {{count}} Ausführung',
    cardMeta_other: '{{time}} · {{count}} Ausführungen',
    analyseWithEva: 'Mit EVA analysieren',
    analyseDirectly: 'Direkt analysieren',
    empty: 'Noch keine Analysen',
    emptyHint:
      'Starte eine Analyse, um eine schreibgeschützte Ansicht deiner Ausführungsdaten zu speichern.',
    emptyFiltered: 'Keine Analysen entsprechen diesen Filtern',
    emptyFilteredHint: 'Passe die Filter oben an oder setze sie zurück.',
    creator: 'Ersteller',
    runsInAnalysis: 'Ausführungen in dieser Analyse',
    executionsInSession: 'Ausführungen in dieser Sitzung',
    created: 'Erstellt',
    detailsLabel: 'Analysedetails',
    notebookLabel: 'Notebook',
    selectNotebook: 'Notebook auswählen',
    blankNotebook: 'Leeres Notebook',
    saveTitle: 'Notebook als Analyse speichern',
    saveDescription:
      'Erstellt eine dauerhafte, schreibgeschützte Kopie dieses Notebooks und seiner Ergebnisse.',
    saveAction: 'Als Analyse speichern',
    saveSuccess: 'Analyse gespeichert',
    saveError: 'Analyse konnte nicht gespeichert werden',
    nameLabel: 'Analysename',
    notFound: 'Analyse nicht gefunden',
    readOnlyNotice: 'Diese Analyse ist schreibgeschützt.',
    executionsHeading: 'Ausführungen',
    activeSessions: 'Aktiv [{{count}}]',
    saved: 'Gespeichert [{{count}}]',
    started: 'Gestartet {{time}}',
    sessionMeta_one: 'Gestartet {{time}} · {{count}} Ausführung',
    sessionMeta_other: 'Gestartet {{time}} · {{count}} Ausführungen',
    fork: 'Duplizieren',
    forkTitle: 'Diese Analyse in eine neue bearbeitbare Sitzung duplizieren',
    forking: 'Wird dupliziert…',
    forkError: 'Analyse konnte nicht dupliziert werden',
    openInEva: 'In Eva öffnen',
  },
} as const satisfies Ns1Translations
